import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import config from '../config.js';
import openPdf from '../utils/openPdf';
import resolveShippingAddressId from '../utils/resolveShippingAddressId';
import CustomerSubmissionDrawingsCard from '../components/CustomerSubmissionDrawingsCard.jsx';
import LeadTimeSelector from '../components/LeadTimeSelector.jsx';
import { apiErrorMessage, detailToString } from '../utils/apiError';
import { emailSenderHeaders } from '../utils/emailSender';
import { fmtMoney, fmtDelta, fmtPct, num } from '../utils/priceMath';
import {
    deriveLinePrice, inferPriceSource, priceDrift, effectivePriceSource, basisLabel, headerFractions,
    zeroSellRates, summarizeAssemblyGroup, computeSubtotal, financialBuckets, recalcPreviewRows,
    normalizePriceSource, round2, lineUnitCost, materialUnresolved,
} from '../utils/quotePricing';
import QuoteCostAnalysisTable from '../components/quote/QuoteCostAnalysisTable.jsx';
import QuoteAssemblyPricingCard from '../components/quote/QuoteAssemblyPricingCard.jsx';
import QuoteRecalcPreviewModal from '../components/quote/QuoteRecalcPreviewModal.jsx';
import QuoteFinancialSummaryCard from '../components/quote/QuoteFinancialSummaryCard.jsx';
import QuoteTrueCostRatesPanel from '../components/quote/QuoteTrueCostRatesPanel.jsx';
import QuoteTermsCard from '../components/quote/QuoteTermsCard.jsx';
import { newQuoteRateSetup, describeQuoteRates } from '../utils/quoteRateDefaults';
// Time & Materials (slice 1d): the logic lives in these files; this page only wires them in.
import { useFeatures, tmCreationAllowed } from '../utils/features';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { isTm, money2 } from '../utils/tmPricing';
import {
    TM_NOT_IN_V1, TM_ACCEPT_CONFIRM, TM_RESTORE_CONFIRM, TM_POLICY_NOTE, TM_YIELD_POLICY_ID,
    tmQuoteLineRefusals, toTmLines, toFixedLines, restoreFixedLines, tmQuoteSaveFields, tmLinesEstimate,
} from '../utils/tmQuote';
import {
    TmBadge, TmQuotePricingToggle, TmQuoteLinePrice, TmQuoteLineSubtotal, TmQuoteTotals, TmQuoteOrderedBanner,
} from '../components/tm/TmQuoteControls.jsx';

// --- Reusable UI Components --

const Notification = ({ message, type, onDismiss }) => {
    if (!message) return null;
    return (
        <div 
            className={`alert alert-${type} alert-dismissible fade show`} 
            role="alert" 
            style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1050, minWidth: '300px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
        >
            {message}
            <button type="button" className="btn-close" onClick={onDismiss} aria-label="Close"></button>
        </div>
    );
};

const SnapshotHistoryModal = ({ show, quoteId, onClose }) => {
    const [snapshots, setSnapshots] = useState([]);
    const [auditLog, setAuditLog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeHistoryTab, setActiveHistoryTab] = useState('activity');

    useEffect(() => {
        if (show && quoteId) {
            setLoading(true);
            Promise.all([
                fetch(`${config.API_BASE_URL}/quotes/${quoteId}/snapshots`).then(r => r.json()).catch(() => []),
                fetch(`${config.API_BASE_URL}/quotes/${quoteId}/audit-log`).then(r => r.json()).catch(() => [])
            ]).then(([snaps, audit]) => {
                setSnapshots(snaps);
                setAuditLog(audit);
            }).finally(() => setLoading(false));
        }
    }, [show, quoteId]);

    if (!show) return null;

    const formatCentralTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const actionBadgeColor = (action) => {
        switch (action) {
            case 'created': return 'success';
            case 'approved': return 'primary';
            case 'updated': return 'warning';
            case 'emailed': return 'info';
            case 'exported_pdf': return 'secondary';
            case 'converted_to_so': return 'primary';
            case 'converted_to_invoice': return 'dark';
            default: return 'secondary';
        }
    };

    const actionLabel = (action) => {
        switch (action) {
            case 'created': return 'Created';
            case 'approved': return 'Approved';
            case 'updated': return 'Updated';
            case 'emailed': return 'Emailed';
            case 'exported_pdf': return 'PDF Export';
            case 'converted_to_so': return 'Sales Order';
            case 'converted_to_invoice': return 'Invoice';
            default: return action;
        }
    };

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Quote History</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <button className={`nav-link ${activeHistoryTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveHistoryTab('activity')}>
                                    Activity Log {auditLog.length > 0 && <span className="badge bg-secondary ms-1">{auditLog.length}</span>}
                                </button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link ${activeHistoryTab === 'versions' ? 'active' : ''}`} onClick={() => setActiveHistoryTab('versions')}>
                                    Sent Versions {snapshots.length > 0 && <span className="badge bg-secondary ms-1">{snapshots.length}</span>}
                                </button>
                            </li>
                        </ul>

                        {loading ? (
                            <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                        ) : activeHistoryTab === 'activity' ? (
                            auditLog.length === 0 ? (
                                <p className="text-muted text-center">No activity recorded yet.</p>
                            ) : (
                                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-hover align-middle table-sm">
                                        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                            <tr>
                                                <th style={{ width: '160px' }}>Date (CST)</th>
                                                <th style={{ width: '110px' }}>Action</th>
                                                <th>Details</th>
                                                <th style={{ width: '100px' }}>User</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {auditLog.map(entry => (
                                                <tr key={entry.audit_id}>
                                                    <td className="small">{formatCentralTime(entry.created_at)}</td>
                                                    <td>
                                                        <span className={`badge bg-${actionBadgeColor(entry.action)}`}>
                                                            {actionLabel(entry.action)}
                                                        </span>
                                                    </td>
                                                    <td className="small">
                                                        {entry.summary || '\u2014'}
                                                        {entry.changes && Object.keys(entry.changes).length > 0 && (
                                                            <div className="mt-1">
                                                                {Object.entries(entry.changes).map(([field, vals]) => (
                                                                    <div key={field} className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                        <strong>{field.replace(/_/g, ' ')}:</strong> {typeof vals.old === 'number' ? `$${vals.old.toFixed(2)}` : vals.old} &rarr; {typeof vals.new === 'number' ? `$${vals.new.toFixed(2)}` : vals.new}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="small text-muted">{entry.changed_by || 'System'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )
                        ) : (
                            snapshots.length === 0 ? (
                                <p className="text-muted text-center">No sent versions found. This quote hasn't been exported or emailed yet.</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Date (CST)</th>
                                                <th>Rev</th>
                                                <th>Action</th>
                                                <th className="text-end">View</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {snapshots.map(snap => (
                                                <tr key={snap.snapshot_id}>
                                                    <td>{formatCentralTime(snap.created_at)}</td>
                                                    <td>
                                                        <span className="badge bg-dark">
                                                            {snap.revision_letter || '\u2014'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge bg-${snap.action_type.includes('Email') ? 'info' : 'secondary'}`}>
                                                            {snap.action_type}
                                                        </span>
                                                    </td>
                                                    <td className="text-end">
                                                        <a
                                                            href={snap.s3_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-primary"
                                                        >
                                                            View PDF
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ConfirmationModal = ({ show, message, onConfirm, onCancel }) => {
    if (!show) return null;
    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Confirm Action</h5>
                        <button type="button" className="btn-close" onClick={onCancel} aria-label="Cancel"></button>
                    </div>
                    <div className="modal-body">
                        <p>{message}</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onConfirm}>Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const EmailModal = ({ show, title, recipientName, onClose, onSend, isLoading }) => {
    const [body, setBody] = useState("");

    // Reset body when modal opens
    useEffect(() => {
        if (show) {
            setBody(`Hello,\n\nPlease find the attached quote for your review.\n\nLet us know if you have any questions or would like to proceed.\n\nThank you,\nKanCraft MFG`);
        }
    }, [show]);

    if (!show) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <p className="mb-2"><strong>To:</strong> {recipientName || "Customer"}</p>
                        <div className="mb-3">
                            <label className="form-label">Message Body</label>
                            <textarea 
                                className="form-control" 
                                rows="6" 
                                value={body} 
                                onChange={(e) => setBody(e.target.value)}
                            ></textarea>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isLoading}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={() => onSend(body)} disabled={isLoading}>
                            {isLoading ? 'Sending...' : 'Send Email'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const RateCalculatorModal = ({ show, type, onClose, onApply }) => {
    const [annualHours, setAnnualHours] = useState(2000);
    const [machineCost, setMachineCost] = useState(0);
    const [usefulLife, setUsefulLife] = useState(7);
    const [salvageValue, setSalvageValue] = useState(0);
    const [annualBudget, setAnnualBudget] = useState(0);
    const [sqFt, setSqFt] = useState(0);
    const [costPerSqFt, setCostPerSqFt] = useState(0);

    if (!show) return null;

    const calculateResult = () => {
        const hours = parseFloat(annualHours) || 1;
        switch (type) {
            case 'depreciation':
                const cost = parseFloat(machineCost) || 0;
                const salvage = parseFloat(salvageValue) || 0;
                const life = parseFloat(usefulLife) || 1;
                return ((cost - salvage) / (life * hours));
            case 'maintenance':
                const budget = parseFloat(annualBudget) || 0;
                return (budget / hours);
            case 'rent':
                const area = parseFloat(sqFt) || 0;
                const rate = parseFloat(costPerSqFt) || 0;
                return ((area * rate) / hours);
            default:
                return 0;
        }
    };

     const result = calculateResult();

    const handleApply = () => {
        onApply(result.toFixed(2));
        onClose();
    };

    const renderInputs = () => {
        switch (type) {
            case 'depreciation':
                return (
                    <>
                        <div className="mb-2">
                            <label className="form-label small">Machine Purchase Cost ($)</label>
                            <input type="number" className="form-control" value={machineCost} onChange={e => setMachineCost(e.target.value)} />
                        </div>
                        <div className="mb-2">
                            <label className="form-label small">Useful Life (Years)</label>
                            <input type="number" className="form-control" value={usefulLife} onChange={e => setUsefulLife(e.target.value)} />
                        </div>
                        <div className="mb-2">
                            <label className="form-label small">Salvage Value ($)</label>
                            <input type="number" className="form-control" value={salvageValue} onChange={e => setSalvageValue(e.target.value)} />
                        </div>
                    </>
                );
            case 'maintenance':
                return (
                    <div className="mb-2">
                        <label className="form-label small">Annual Maintenance Budget ($)</label>
                        <input type="number" className="form-control" value={annualBudget} onChange={e => setAnnualBudget(e.target.value)} placeholder="Parts + Service / Year" />
                    </div>
                );
            case 'rent':
                return (
                    <>
                        <div className="mb-2">
                            <label className="form-label small">Occupied Area (Sq. Ft.)</label>
                            <input type="number" className="form-control" value={sqFt} onChange={e => setSqFt(e.target.value)} placeholder="Include working area buffer" />
                        </div>
                        <div className="mb-2">
                            <label className="form-label small">Facility Cost ($ / Sq. Ft. / Year)</label>
                            <input type="number" className="form-control" value={costPerSqFt} onChange={e => setCostPerSqFt(e.target.value)} placeholder="Rent + Utilities per sq ft" />
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    const getTitle = () => {
        if(type === 'depreciation') return "Calculate Depreciation";
        if(type === 'maintenance') return "Calculate Maintenance Budget";
        if(type === 'rent') return "Calculate Floor Cost";
        return "Calculator";
    };

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-sm">
                <div className="modal-content">
                    <div className="modal-header bg-light py-2">
                        <h6 className="modal-title">{getTitle()}</h6>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {renderInputs()}
                        <div className="mb-3">
                            <label className="form-label small text-muted">Annual Operating Hours</label>
                            <input type="number" className="form-control form-control-sm" value={annualHours} onChange={e => setAnnualHours(e.target.value)} />
                            <div className="form-text" style={{fontSize: '0.7rem'}}>Standard single shift ≈ 2000 hrs</div>
                        </div>
                        <div className="alert alert-info py-2 mb-0 text-center">
                            <strong>Calculated: ${result.toFixed(2)} / hr</strong>
                        </div>
                    </div>
                    <div className="modal-footer py-1">
                        <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="button" className="btn btn-sm btn-primary" onClick={handleApply}>Apply Value</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ShopRateManager = () => {
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [globalConfig, setGlobalConfig] = useState({ electricity_rate: 0.12, overhead_rate_per_hour: 20.00 });
    const [workstations, setWorkstations] = useState([]);
    const [activeCalc, setActiveCalc] = useState(null); 
    const API_BASE = config.API_BASE_URL;

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/shop_rates/config`);
            if (!res.ok) throw new Error("Failed to load shop rate configuration");
            const data = await res.json();
            setGlobalConfig(data.global_config);
            setWorkstations(data.workstations);
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    const handleGlobalChange = (e) => {
        const { name, value } = e.target;
        setGlobalConfig(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    };

    const handleStationChange = (index, field, value) => {
        const updated = [...workstations];
        updated[index][field] = parseFloat(value) || 0;
        setWorkstations(updated);
    };

    const handleApplyCalculation = (val) => {
        if (!activeCalc) return;
        const fieldMap = {
            'depreciation': 'depreciation_cost_per_hour',
            'maintenance': 'maintenance_cost_per_hour',
            'rent': 'rent_cost_per_hour'
        };
        handleStationChange(activeCalc.index, fieldMap[activeCalc.type], val);
        setActiveCalc(null);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const payload = {
                global_config: globalConfig,
                workstations: workstations
            };
            const res = await fetch(`${API_BASE}/shop_rates/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (!res.ok) throw new Error("Failed to save configuration");
            setNotification({ message: "Shop rates updated successfully!", type: 'success' });
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    const calculatePreviewRate = (station) => {
        const energyCost = station.energy_consumption_kw * globalConfig.electricity_rate;
        const totalOverhead = energyCost + station.depreciation_cost_per_hour + 
                              station.maintenance_cost_per_hour + station.rent_cost_per_hour + 
                              globalConfig.overhead_rate_per_hour;
        const eff = station.efficiency_percent > 0 ? station.efficiency_percent : 1;
        return totalOverhead / eff;
    };

    const InfoIcon = ({ title }) => (
        <span className="ms-1 text-primary" style={{ cursor: 'help', fontSize: '0.85em' }} title={title}>ℹ️</span>
    );

    return (
        <div className="p-4 h-100 overflow-auto">
            <Notification message={notification.message} type={notification.type} onDismiss={() => setNotification({ message: '', type: '' })} />
            <RateCalculatorModal show={!!activeCalc} type={activeCalc?.type} onClose={() => setActiveCalc(null)} onApply={handleApplyCalculation} />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>Shop Rate Configuration <span className="badge bg-secondary align-middle" style={{ fontSize: '0.5em' }}>placeholder</span></h3>
                <button className="btn btn-success" onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save Configuration'}</button>
            </div>
            {/* Decision 1 (2026-09-02): machine burden was never built out — these inputs are
                placeholders and the cost basis stays direct wages. Inputs are kept; only labelled. */}
            <div className="alert alert-secondary py-2 small mb-3" role="note">
                <strong>Placeholder inputs</strong> — not used by costing or quoting today; true cost is direct wages + gas/consumables + material + vendor charges. Nothing reads the Machine Rate column: quotes price labour with the per-quote sell rates in Quote Setup (defaults: {describeQuoteRates()}).
            </div>

            <div className="card mb-4 shadow-sm">
                <div className="card-header bg-secondary text-white">Global Variables (Applied to All Stations) <span className="badge bg-light text-dark ms-2" title="Not used by costing or quoting today.">placeholder — not used by costing</span></div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Electricity Cost ($/kWh)</label>
                            <div className="input-group">
                                <span className="input-group-text">$</span>
                                <input type="number" step="0.0001" className="form-control" name="electricity_rate" value={globalConfig.electricity_rate} onChange={handleGlobalChange} />
                            </div>
                            <div className="form-text">Your utility rate per kilowatt-hour.</div>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">General Overhead Adder ($/hr)</label>
                            <div className="input-group">
                                <span className="input-group-text">$</span>
                                <input type="number" step="0.01" className="form-control" name="overhead_rate_per_hour" value={globalConfig.overhead_rate_per_hour} onChange={handleGlobalChange} />
                            </div>
                            <div className="form-text">Admin salaries, insurance, software, rent (common areas).</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm">
                <div className="card-header bg-light fw-bold">Workstation Specifics <span className="badge bg-secondary ms-2" title="Not used by costing or quoting today.">placeholder — not used by costing</span></div>
                <div className="table-responsive">
                    <table className="table table-hover table-bordered mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th style={{width: '12%'}}>Station</th>
                                <th style={{width: '10%'}}>Power (kW)<InfoIcon title="Average energy usage." /></th>
                                <th style={{width: '18%'}}>Depreciation ($/hr)<InfoIcon title="Capital recovery cost." /></th>
                                <th style={{width: '18%'}}>Maintenance ($/hr)<InfoIcon title="Annual budget / hours." /></th>
                                <th style={{width: '18%'}}>Floor Space ($/hr)<InfoIcon title="Cost of space occupied." /></th>
                                <th style={{width: '10%'}}>Efficiency<InfoIcon title="Utilization factor (0.0 to 1.0)." /></th>
                                <th className="bg-success text-white">Machine Rate ($/hr)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {workstations.map((ws, idx) => {
                                const previewRate = calculatePreviewRate(ws);
                                return (
                                    <tr key={ws.workstation}>
                                        <td className="fw-bold">{ws.workstation}</td>
                                        <td><input type="number" step="0.1" className="form-control form-control-sm" value={ws.energy_consumption_kw} onChange={(e) => handleStationChange(idx, 'energy_consumption_kw', e.target.value)} /></td>
                                        <td>
                                            <div className="input-group input-group-sm">
                                                <input type="number" step="0.01" className="form-control" value={ws.depreciation_cost_per_hour} onChange={(e) => handleStationChange(idx, 'depreciation_cost_per_hour', e.target.value)} />
                                                <button className="btn btn-outline-secondary" type="button" onClick={() => setActiveCalc({type: 'depreciation', index: idx})}>Calc</button>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="input-group input-group-sm">
                                                <input type="number" step="0.01" className="form-control" value={ws.maintenance_cost_per_hour} onChange={(e) => handleStationChange(idx, 'maintenance_cost_per_hour', e.target.value)} />
                                                <button className="btn btn-outline-secondary" type="button" onClick={() => setActiveCalc({type: 'maintenance', index: idx})}>Calc</button>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="input-group input-group-sm">
                                                <input type="number" step="0.01" className="form-control" value={ws.rent_cost_per_hour} onChange={(e) => handleStationChange(idx, 'rent_cost_per_hour', e.target.value)} />
                                                <button className="btn btn-outline-secondary" type="button" onClick={() => setActiveCalc({type: 'rent', index: idx})}>Calc</button>
                                            </div>
                                        </td>
                                        <td><input type="number" step="0.01" max="1.0" min="0.1" className="form-control form-control-sm" value={ws.efficiency_percent} onChange={(e) => handleStationChange(idx, 'efficiency_percent', e.target.value)} /></td>
                                        <td className="fw-bold text-end bg-light">${previewRate.toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="card-footer text-muted small">* <strong>Machine Rate</strong> = (Energy + Depr + Maint + Rent + Global Overhead) / Efficiency. Operator Labor added separately — see the true-cost wage table below. Machine Rate is a <strong>placeholder</strong> — nothing in costing or quoting reads it; it is <strong>not</strong> part of quote cost.</div>
            </div>
            <div className="mt-4">
                <QuoteTrueCostRatesPanel />
            </div>
        </div>
    );
};

const QuoteSetup = ({ setup, onChange, onRecalculate, isCalculating, forceExpanded, readOnly }) => {
    const [expanded, setExpanded] = useState(false);
    const isExpanded = forceExpanded || expanded;
    return (
      <div className="border rounded mb-3" style={{ fontSize: '0.875rem' }}>
        <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light" style={{ cursor: forceExpanded ? 'default' : 'pointer' }} onClick={() => !forceExpanded && setExpanded(!expanded)}>
            <div className="d-flex align-items-center gap-2">
                {!forceExpanded && <span style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>&#9654;</span>}
                <strong>Quote Setup</strong>
                {!isExpanded && (
                    <span className="text-muted ms-2">
                        Margin {setup.target_margin}% &middot; Markup {setup.material_markup}%{setup.coating_markup !== '' && setup.coating_markup != null && <> &middot; Outsourced markup {setup.coating_markup}%</>} &middot; Laser ${setup.laser_rate}/hr
                    </span>
                )}
            </div>
            {!readOnly && (
                <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); onRecalculate(); }} disabled={isCalculating}>
                    {isCalculating ? 'Calculating...' : 'Recalculate All Prices'}
                </button>
            )}
        </div>
        {isExpanded && (
          <div className="px-3 py-2">
            <div className="row g-2">
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small text-primary fw-bold mb-1" title="Target margin for lines on the 'Target margin' basis. Saved with the quote (quotes.target_margin) and re-applied by Calc / Recalculate.">Margin % <span className="fw-normal text-muted">(saved)</span></label>
                <input type="number" className="form-control form-control-sm border-primary" name="target_margin" value={setup.target_margin} onChange={onChange} readOnly={readOnly} />
              </div>
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small mb-1">Markup %</label>
                <input type="number" className="form-control form-control-sm" name="material_markup" value={setup.material_markup} onChange={onChange} readOnly={readOnly} />
              </div>
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small mb-1" title="Applies to every outsourced step — Coating today (F43). Leave blank to follow Markup %.">Outsourced markup %</label>
                <input type="number" className="form-control form-control-sm" name="coating_markup"
                    value={setup.coating_markup == null ? '' : setup.coating_markup} onChange={onChange} readOnly={readOnly}
                    placeholder={`= ${setup.material_markup || 0}`}
                    title="Applies to every outsourced step — Coating today. Leave blank to follow Markup %. Saved as coating_markup; each outsourced row's Cost Analysis names which markup applied." />
              </div>
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small mb-1">Laser $/hr</label>
                <input type="number" className="form-control form-control-sm" name="laser_rate" value={setup.laser_rate} onChange={onChange} readOnly={readOnly} />
              </div>
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small mb-1">Tap $/hr</label>
                <input type="number" className="form-control form-control-sm" name="tap_rate" value={setup.tap_rate} onChange={onChange} readOnly={readOnly} />
              </div>
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small mb-1">Brake $/hr</label>
                <input type="number" className="form-control form-control-sm" name="brake_rate" value={setup.brake_rate} onChange={onChange} readOnly={readOnly} />
              </div>
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small mb-1">Fab $/hr</label>
                <input type="number" className="form-control form-control-sm" name="fabrication_rate" value={setup.fabrication_rate} onChange={onChange} readOnly={readOnly} />
              </div>
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small mb-1">Blast $/hr</label>
                <input type="number" className="form-control form-control-sm" name="blast_rate" value={setup.blast_rate} onChange={onChange} readOnly={readOnly} />
              </div>
              <div className="col-6 col-md-3 col-lg">
                <label className="form-label small mb-1">Finish $/hr</label>
                <input type="number" className="form-control form-control-sm" name="finish_rate" value={setup.finish_rate} onChange={onChange} readOnly={readOnly} />
              </div>
            </div>
            {!readOnly && <div className="form-text mt-1">Adjust rates, then click "Recalculate All Prices": every line gets a fresh cost breakdown and its price is re-derived from its <em>own</em> basis (calculated / target margin / cost + markup — manual and book prices are kept). A preview shows old → new before anything changes.</div>}
          </div>
        )}
      </div>
    );
};

const MaterialSelect = ({ materials, filteredMaterials, selectedId, onChange, disabled }) => {
    const displayOptions = useMemo(() => {
        if (selectedId) {
            const isVisible = filteredMaterials.some(m => m.material_id === selectedId);
            if (!isVisible) {
                const missingMaterial = materials.find(m => m.material_id === selectedId);
                if (missingMaterial) return [missingMaterial, ...filteredMaterials];
            }
        }
        return filteredMaterials;
    }, [materials, filteredMaterials, selectedId]);

    return (
        <select className="form-select" value={selectedId || ''} onChange={onChange} disabled={disabled}>
            <option value="">-- Select Material --</option>
            {displayOptions.map(m => (
                <option key={m.material_id} value={m.material_id}>{m.description}</option>
            ))}
        </select>
    );
};

const SearchablePartDropdown = ({ parts, onPartSelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredParts = useMemo(() => {
        const matches = searchTerm.length < 1
            ? parts
            : parts.filter(part => (part.product_id?.toLowerCase().includes(searchTerm.toLowerCase())) || (part.description?.toLowerCase().includes(searchTerm.toLowerCase())));

        const uniqueMap = new Map();
        matches.forEach(part => {
            if (!uniqueMap.has(part.product_id) || part.is_current_revision) {
                uniqueMap.set(part.product_id, part);
            }
        });
        return Array.from(uniqueMap.values());
    }, [parts, searchTerm]);

    const handleSelect = (partId) => {
        onPartSelect(partId);
        setSearchTerm('');
        setIsOpen(false);
    };
    
    return (
        <div className="dropdown" style={{ position: 'relative' }}>
            <input type="text" className="form-control" placeholder="Search for an existing part..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onFocus={() => setIsOpen(true)} onBlur={() => setTimeout(() => setIsOpen(false), 200)} />
            {isOpen && (
                <ul className="dropdown-menu show" style={{ maxHeight: '250px', overflowY: 'auto', width: '100%' }}>
                    {filteredParts.length > 0 ? (
                        filteredParts.map(part => (
                            <li key={part.part_id}>
                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSelect(part.part_id); }}><strong>{part.product_id}</strong>: {part.description}</a>
                            </li>
                        ))
                    ) : (<li><span className="dropdown-item-text text-muted">No parts found</span></li>)}
                </ul>
            )}
        </div>
    );
};

const SearchableAssemblyDropdown = ({ assemblies, onAssemblySelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filtered = useMemo(() => {
        if (!searchTerm) return assemblies;
        return assemblies.filter(a => 
            a.assembly_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [assemblies, searchTerm]);

    const handleSelect = (asm) => {
        onAssemblySelect(asm);
        setSearchTerm('');
        setIsOpen(false);
    };

    return (
        <div className="dropdown w-100" style={{ position: 'relative' }}>
            <input 
                type="text" 
                className="form-control border-info" 
                placeholder="Search for an assembly..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                onFocus={() => setIsOpen(true)} 
                onBlur={() => setTimeout(() => setIsOpen(false), 200)} 
            />
            {isOpen && (
                <ul className="dropdown-menu show" style={{ maxHeight: '250px', overflowY: 'auto', width: '100%', zIndex: 1050 }}>
                    {filtered.length > 0 ? (
                        filtered.map(asm => (
                            <li key={asm.assembly_id}>
                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSelect(asm); }}>
                                    <strong>{asm.assembly_name}</strong>
                                </a>
                            </li>
                        ))
                    ) : (<li><span className="dropdown-item-text text-muted">No assemblies found</span></li>)}
                </ul>
            )}
        </div>
    );
};

// Picker for a PURCHASED COMPONENT sold on its own line — something bought
// finished from a vendor and resold, with no manufacturing behind it (JobQ
// cannot buy a `parts` row: purchase_order_lines carries consumable_id and
// material_id, never part_id). Lists `consumables` of type 'component'.
const SearchableComponentDropdown = ({ components, onComponentSelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filtered = useMemo(() => {
        if (!searchTerm) return components;
        const q = searchTerm.toLowerCase();
        return components.filter(c =>
            (c.product_id || '').toLowerCase().includes(q) ||
            (c.description || '').toLowerCase().includes(q)
        );
    }, [components, searchTerm]);

    const handleSelect = (c) => {
        onComponentSelect(c);
        setSearchTerm('');
        setIsOpen(false);
    };

    return (
        <div className="dropdown w-100" style={{ position: 'relative' }}>
            <input
                type="text"
                className="form-control border-warning"
                placeholder="Search for a purchased component..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            />
            {isOpen && (
                <ul className="dropdown-menu show" style={{ maxHeight: '250px', overflowY: 'auto', width: '100%', zIndex: 1050 }}>
                    {filtered.length > 0 ? (
                        filtered.map(c => (
                            <li key={c.consumable_id}>
                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSelect(c); }}>
                                    <strong>{c.product_id}</strong>: {c.description}
                                </a>
                            </li>
                        ))
                    ) : (<li><span className="dropdown-item-text text-muted">No purchased components found</span></li>)}
                </ul>
            )}
        </div>
    );
};

const RevisionSelector = ({ productId, currentPartId, onRevisionSelect }) => {
    const [revisions, setRevisions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!productId) return;
        const fetchRevisions = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${config.API_BASE_URL}/parts/${productId}/revisions`);
                if (res.ok) {
                    const data = await res.json();
                    setRevisions(data);
                }
            } catch (err) { console.error("Failed to load revisions", err); } finally { setLoading(false); }
        };
        fetchRevisions();
    }, [productId]);

    if (!productId || revisions.length <= 1) return null;

    return (
        <div className="mt-1">
            <label className="form-label small text-muted mb-0" style={{ fontSize: '0.75rem' }}>Revision</label>
            <select className="form-select form-select-sm" value={currentPartId || ''} onChange={(e) => onRevisionSelect(e.target.value)} disabled={loading} style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>
                {revisions.map((rev) => (
                    <option key={rev.part_id} value={rev.part_id}>Rev {rev.revision_code} {rev.is_current_revision ? '(Current)' : ''}</option>
                ))}
            </select>
        </div>
    );
};

// --- HELPER: Historical Data Priority ---
// Ensures we always prefer Real World Averages (if available) over Initial Engineering Estimates
const applyHistoricalData = (partData) => {
    const rates = {};
    const stations = [
        'laser', 'tap', 'brake', 'blast', 'finish', 
        'cutting', 'drilling', 'milling', 'lathe', 'welding', 'assembly', 'tapping'
    ];

    stations.forEach(station => {
        // Map station name to the correct initial_est_XXX_minutes field name
        let estField = `initial_est_${station}_minutes`;
        if (station === 'tapping') estField = 'initial_est_fab_tap_minutes'; 
        if (station === 'welding') estField = 'initial_est_weld_minutes';

        // Check if we have a historical average (in hours) and convert to minutes
        if (partData.avg_time_per_station && partData.avg_time_per_station[station] > 0) {
            rates[estField] = (partData.avg_time_per_station[station] * 60).toFixed(2);
        } else {
            // Fallback to initial estimate
            rates[estField] = partData[estField] || '';
        }
    });
    return rates;
};

// Maps a process/station name to its estimate-minutes field on a quote line.
const PROCESS_MINUTE_FIELD = {
    Laser: 'initial_est_laser_minutes', Tap: 'initial_est_tap_minutes',
    Brake: 'initial_est_brake_minutes', Blast: 'initial_est_blast_minutes',
    Finish: 'initial_est_finish_minutes', Cutting: 'initial_est_cutting_minutes',
    Drilling: 'initial_est_drilling_minutes', Milling: 'initial_est_milling_minutes',
    Lathe: 'initial_est_lathe_minutes', Welding: 'initial_est_weld_minutes',
    Assembly: 'initial_est_assembly_minutes', Tapping: 'initial_est_fab_tap_minutes',
};

// For a Part-locked line (one backed by an existing part_id), jobq-api prices labor
// straight from the part record -- never from the line's own process/time fields,
// which are a frozen quote_parts snapshot. That snapshot can drift out of sync with
// the part (e.g. Welding added on the Part page shows in the Cost Analysis while the
// Welding checkbox stays unchecked). Derive the displayed processes from the saved
// cost breakdown instead, so what's shown always matches what's billed. Falls back
// to the snapshot list when no breakdown has been computed yet.
const deriveProcessesFromBreakdown = (line) => {
    const bd = line.cost_breakdown && line.cost_breakdown.breakdown;
    if (!bd) return line.process_steps || [];
    const active = [];
    const laser = bd.laser;
    const laserActive =
        (laser && (((laser.quoted_price || 0) > 0) || (((laser.details && laser.details.cutting_hours) || 0) > 0)))
        || (parseFloat(line.initial_est_laser_minutes) || 0) > 0;
    if (laserActive) active.push('Laser');
    (bd.operations || []).forEach(op => {
        if (op && op.station && op.station !== 'Coating' && active.indexOf(op.station) === -1) {
            active.push(op.station);
        }
    });
    return active;
};

// Purchased-component ("Component") line. Cost comes from the server-side
// consumable calculator when this API has it (line.cost_breakdown, F36), else
// from the assembly's consumable-components vendor cost (`_cost`); when neither
// resolved, the row says "cost unresolved" instead of pretending $0. The price
// basis (book / cost+markup / manual) is written to the line as price_source /
// price_basis so it survives reload and Recalculate (F01).
const ConsumableLineRow = ({ line, index, onPatch, onRemove, materialMarkup, badges, readOnly }) => {
    const cost = lineUnitCost(line);                       // NaN when unresolved
    const costKnown = Number.isFinite(cost);
    const bookPrice = parseFloat(line._book_price) || 0;
    const markupPct = parseFloat(materialMarkup) || 0;
    const markupFrac = markupPct / 100;
    const markupPrice = costKnown ? round2(cost * (1 + markupFrac)) : NaN;
    const source = effectivePriceSource(line);
    const belowCost = line._below_cost === true || (bookPrice > 0 && costKnown && bookPrice < cost - 0.005);
    const current = parseFloat(line.price) || 0;
    const margin = costKnown && current > 0 ? (current - cost) / current : null;
    const matSrc = line.cost_breakdown?.breakdown?.material?.source || null;
    const costSourceText = matSrc?.description
        || (line._cost_source ? `${line._cost_source} (assembly components)` : null)
        || (costKnown ? 'vendor cost from the assembly\'s components (preferred vendor, else highest)' : null);
    const calcWarnings = Array.isArray(line.cost_breakdown?.warnings) ? line.cost_breakdown.warnings : [];

    const choose = (mode) => {
        if (mode === 'book') onPatch(index, { price: bookPrice.toFixed(2), pricing_mode: 'book', price_source: 'book', price_basis: null, _price_source_inferred: false });
        if (mode === 'markup' && costKnown) onPatch(index, { price: markupPrice.toFixed(2), pricing_mode: 'markup', price_source: 'markup', price_basis: markupFrac, _price_source_inferred: false });
    };

    return (
        <div className="p-3 border rounded mb-3" style={{ backgroundColor: 'var(--jq-alert-warning-bg, #fff3cd)', borderLeft: '5px solid #ffc107' }}>
            <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                <h6 className="text-warning-emphasis mb-0">
                    <span className="badge bg-warning text-dark me-2">Component</span>
                    {line.product_id}
                </h6>
                <button className="btn btn-sm btn-danger" onClick={() => onRemove(line.id)} disabled={readOnly}>Remove</button>
            </div>
            {badges}

            <div className="row g-3 align-items-start">
                <div className="col-md-5">
                    <label className="form-label small text-muted">Description</label>
                    <input type="text" className="form-control" value={line.description} onChange={(e) => onPatch(index, { description: e.target.value })} />
                    <div className="small text-muted mt-1" style={{ lineHeight: 1.3 }}>
                        {costKnown ? (
                            <span title={costSourceText || ''}>
                                <strong>Cost {fmtMoney(cost)}</strong>/ea{costSourceText ? ` — ${costSourceText}` : ''}
                            </span>
                        ) : (
                            <span className="badge bg-warning text-dark" title="The component cost could not be fetched (assembly components request failed or the component is no longer on the assembly). It is excluded from cost totals — reload or Recalculate All.">cost unresolved</span>
                        )}
                        {belowCost && <span className="badge bg-danger ms-1" title={`Catalog book price ${fmtMoney(bookPrice)} is below the vendor cost ${fmtMoney(cost)}`}>book below cost</span>}
                        {calcWarnings.map((w, i) => <span key={i} className="badge bg-warning text-dark ms-1">{w}</span>)}
                    </div>
                </div>

                <div className="col-md-2">
                    <label className="form-label small text-muted">Quantity</label>
                    <input type="number" className="form-control" value={line.quantity} onChange={(e) => onPatch(index, { quantity: e.target.value })} />
                </div>

                <div className="col-md-5">
                    <label className="form-label small text-muted">
                        Price / ea — basis: <strong>{basisLabel(line)}</strong>
                        {margin != null && <span className={`ms-2 badge ${margin < 0 ? 'bg-danger' : margin < 0.2 ? 'bg-warning text-dark' : 'bg-success'}`} title={`(price ${fmtMoney(current)} − cost ${fmtMoney(cost)}) ÷ price`}>margin {(margin * 100).toFixed(1)}%</span>}
                    </label>
                    <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                            type="number"
                            className="form-control fw-bold"
                            value={line.price}
                            onChange={(e) => onPatch(index, { price: e.target.value, pricing_mode: 'manual', price_source: 'manual', price_basis: null, _price_source_inferred: false })}
                        />
                    </div>
                    <div className="mt-1 d-flex gap-2 flex-wrap">
                        <button type="button"
                            className={`btn btn-sm ${source === 'book' ? 'btn-primary' : 'btn-outline-secondary'}${belowCost ? ' border-danger' : ''}`}
                            onClick={() => choose('book')} disabled={!(bookPrice > 0)}
                            title={bookPrice > 0 ? `Saved catalog price (consumables.price)${belowCost ? ' — BELOW COST' : ''}` : 'No book price on file'}>
                            Book: {bookPrice > 0 ? fmtMoney(bookPrice) : '—'}
                        </button>
                        <button type="button"
                            className={`btn btn-sm ${source === 'markup' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => choose('markup')} disabled={!costKnown}
                            title={costKnown ? `cost ${fmtMoney(cost)} × (1 + ${markupPct}%) = ${fmtMoney(markupPrice)}` : 'Cost unknown'}>
                            Cost + {markupPct}%: {costKnown ? fmtMoney(markupPrice) : '—'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Per-line provenance strip (F01 / F05 / F09 / F10 / F26): the basis the price
// came from, the drift between that basis and the price (with a one-click
// Apply), the server's staleness verdict on the stored cost snapshot, whether
// an assembly override is billing the line instead of its own price, and the
// "Refresh cost (keep price)" action. Renders nothing it cannot back with a
// number — missing provenance says so instead of showing $0 / NaN.
const LineBadges = ({ line, header, inOverride, onApply, onRefresh, canEdit }) => {
    if (!line) return null;
    const drift = priceDrift(line, header);
    const stale = line.stale && typeof line.stale === 'object' ? line.stale : null;
    const staleReasons = stale && Array.isArray(stale.reasons) ? stale.reasons.filter(Boolean) : [];
    const cost = lineUnitCost(line);
    const current = num(line.price, NaN);
    const belowCost = Number.isFinite(cost) && Number.isFinite(current) && current > 0 && current < cost - 0.005;
    const hasBreakdown = !!line.cost_breakdown;
    // The engine could not work out the material (row gone, no vendor price…): never a cost of $0.
    const unresolved = [...materialUnresolved(line),
        ...(stale && Array.isArray(stale.material_unresolved) ? stale.material_unresolved : [])];
    const unresolvedText = [...new Set(unresolved.map(u => u && u.message).filter(Boolean))];
    const materialMoved = !unresolvedText.length && stale && stale.material_live != null && stale.material_stored != null
        && Math.abs(num(stale.material_live) - num(stale.material_stored)) > 0.005;
    return (
        <div className="d-flex flex-wrap align-items-center gap-1 mb-1" style={{ fontSize: '0.72rem' }}>
            {unresolvedText.length > 0 && (
                <span className="badge bg-danger" data-testid="material-unresolved-badge"
                      title={`Material cost unresolved — not $0: ${unresolvedText.join(' · ')}`}>
                    cost unresolved: {unresolvedText[0]}
                </span>
            )}
            <span className="badge bg-light text-dark border"
                  title={line._price_source_inferred
                      ? 'Basis inferred from the stored price — this line was saved before the basis was recorded. Save to record it.'
                      : 'Price basis saved with the line (quote_parts.price_source / price_basis); Calc and Recalculate re-derive the price from it.'}>
                basis: {basisLabel(line)}{line._price_source_inferred ? ' ?' : ''}
            </span>
            {inOverride && (
                <span className="badge bg-warning text-dark" title="This assembly has a per-set price override: the Subtotal bills override × sets and this line's own price is not billed.">priced by assembly override</span>
            )}
            {drift && (
                <>
                    <span className={`badge ${drift.kind === 'manual_vs_calc' ? 'bg-info text-dark' : 'bg-warning text-dark'}`}
                          title={drift.kind === 'manual_vs_calc'
                              ? 'The stored breakdown says a different calculated price. The price is kept because the basis is manual / book; Apply adopts the calculated price (basis becomes Calculated).'
                              : 'The breakdown changed and this line\'s basis now derives a different price. Apply re-derives the price from the basis.'}>
                        {drift.kind === 'manual_vs_calc' ? 'Calculated' : 'Basis says'} {fmtMoney(drift.calculated)} vs price {fmtMoney(drift.price)} ({fmtDelta(drift.delta)})
                    </span>
                    {canEdit && onApply && (
                        <button type="button" className="btn btn-outline-primary btn-sm py-0 px-1" style={{ fontSize: '0.7rem' }}
                                onClick={() => onApply(drift.apply)}>
                            {drift.kind === 'manual_vs_calc' ? 'Apply calculated' : 'Apply basis'}
                        </button>
                    )}
                </>
            )}
            {belowCost && <span className="badge bg-danger" title={`price ${fmtMoney(current)} is below cost ${fmtMoney(cost)}`}>below cost</span>}
            {stale && stale.is_stale && (
                <span className="badge bg-warning text-dark" title={staleReasons.join(' · ') || 'stored cost snapshot is stale'}>
                    stale{materialMoved ? ` — material now ${fmtMoney(stale.material_live)} (stored ${fmtMoney(stale.material_stored)})` : ''}
                </span>
            )}
            {stale && !stale.is_stale && staleReasons.length > 0 && (
                <span className="badge bg-light text-dark border" title={staleReasons.join(' · ')}>{staleReasons[0]}</span>
            )}
            {!stale && hasBreakdown && !line.cost_breakdown.inputs && (
                <span className="badge bg-light text-dark border" title="This breakdown was stored before the engine recorded its inputs; rates and material source are n/a until it is refreshed.">n/a — priced before provenance was recorded</span>
            )}
            {canEdit && onRefresh && (
                <button type="button" className="btn btn-outline-secondary btn-sm py-0 px-1" style={{ fontSize: '0.7rem' }}
                        title="Re-run the calculator and replace the stored cost breakdown WITHOUT changing the price (the drift badge then shows the gap)."
                        onClick={onRefresh}>Refresh cost (keep price)</button>
            )}
        </div>
    );
};

const AssemblySetCountInput = ({ currentSetCount, disabled, onCommit }) => {
    const [local, setLocal] = useState(String(currentSetCount));
    useEffect(() => { setLocal(String(currentSetCount)); }, [currentSetCount]);
    const commit = () => {
        const parsed = parseInt(local, 10);
        if (!Number.isFinite(parsed) || parsed < 1) {
            setLocal(String(currentSetCount));
            return;
        }
        if (parsed === currentSetCount) return;
        onCommit(parsed);
    };
    return (
        <input
            type="number"
            min="1"
            className="form-control form-control-sm"
            style={{ width: '80px' }}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            disabled={disabled}
            title="Change qty of assemblies — press Enter or click away to cascade qty to all component lines"
        />
    );
};

export default function QuoteManagementPage() {
    const [activeTab, setActiveTab] = useState('quotes');
    const [quotes, setQuotes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCustomerId, setFilterCustomerId] = useState('');
    const [filterDays, setFilterDays] = useState(30);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [quoteMode, setQuoteMode] = useState('Quick');
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [notes, setNotes] = useState('');
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    const [newCustomerName, setNewCustomerName] = useState("");
    const [customers, setCustomers] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [yieldPolicies, setYieldPolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [customerParts, setCustomerParts] = useState([]);
    const fileInputRef = useRef(null);
    const [assemblyQty, setAssemblyQty] = useState(1);
    const [assemblyPriceOverrides, setAssemblyPriceOverrides] = useState({});
    // Per-assembly collapse state in the Advanced quote editor. Key: assembly_id.
    // Default-collapsed so each assembly is reviewed in isolation; first interaction
    // expands. Standalone lines (no _source_assembly_id) are unaffected.
    const [expandedAssemblies, setExpandedAssemblies] = useState({});
    const toggleAssemblyExpanded = (asmId) => {
        setExpandedAssemblies(prev => ({ ...prev, [asmId]: !prev[asmId] }));
    };

    // --- Contact State ---
    const [contacts, setContacts] = useState([]);
    const [selectedContactId, setSelectedContactId] = useState("");
    const [showContactModal, setShowContactModal] = useState(false);
    
    // --- Contact Form State ---
    const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", role: "General" });

    const [materialFilters, setMaterialFilters] = useState({ owner: '', type: '', thickness: '', finish: '' });
    const [filterOptions, setFilterOptions] = useState({ types: [], thicknesses: [], finishes: [] });

    const [shipping, setShipping] = useState(0);
    const [additionalFees, setAdditionalFees] = useState(0);
    const [tax, setTax] = useState(0);
    const [discount, setDiscount] = useState(0);

    // --- Lead Time / Promised Date ---
    const [completionMode, setCompletionMode] = useState('lead_time'); // 'lead_time' or 'date'
    const [leadTime, setLeadTime] = useState('Standard (10-14 Days)');
    const [promisedDate, setPromisedDate] = useState('');
    // Required at quote create + edit. Pre-fills from customer's
    // default_fulfillment_type when a customer is picked, but the operator
    // must consciously confirm/override before save (validation blocks
    // empty). Inherits to the SO at convert-to-sales-order.
    const [fulfillmentType, setFulfillmentType] = useState('');
    const [addresses, setAddresses] = useState([]);
    const [shippingAddressId, setShippingAddressId] = useState('');

    // --- Schedule Estimation ---
    const [scheduleEstimate, setScheduleEstimate] = useState(null);
    const [estimateLoading, setEstimateLoading] = useState(false);

    // New-quote defaults come from the ONE shared constant (decision 2, wave 2)
    // that the nester's Create Quote modal also seeds from: percent units, $/hr.
    const [quoteSetup, setQuoteSetup] = useState(() => newQuoteRateSetup());

    // Time & Materials (slice 1d): the quote's pricing mode and its optional internal budget
    // notes. `tmDirty` marks a typed note: only typed notes are sent (tmQuoteSaveFields).
    const [quotePricingMode, setQuotePricingMode] = useState('fixed');
    const [tmEstimate, setTmEstimate] = useState('');
    const [tmNte, setTmNte] = useState('');
    const [tmDirty, setTmDirty] = useState({});
    const features = useFeatures();
    const isTmQuote = quotePricingMode === 'tm';

    const [notification, setNotification] = useState({ message: '', type: '' });
    const [confirmation, setConfirmation] = useState({ show: false, message: '', onConfirm: () => {} });

    const [emailModal, setEmailModal] = useState({ show: false, quoteId: null });

    const isQuoteLoading = useRef(false);
    // T&M toggle undo: a Fixed→T&M switch on a quote stored as FIXED keeps the fixed lines, mode,
    // tax, subtotal and customer here, so switching straight back (no save in between) gives each
    // line NOT changed while in T&M its fixed price and basis back instead of re-pricing it at its
    // calculated price (restoreFixedLines). Cleared on load, New and save.
    const tmToggleSnapshot = useRef(null);
    // Set when that restore puts the typed tax back: the auto-tax effect must not overwrite it.
    const skipAutoTaxOnce = useRef(false);

    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // Deep-link: /quotes?id=N auto-selects the quote once it has loaded.
    // Tracks the last param value the effect handled so a stale param won't
    // re-clobber the operator's manual selection on subsequent renders.
    const [searchParams] = useSearchParams();
    const linkedQuoteId = searchParams.get('id');
    const autoSelectedRef = useRef(null);
    // Monotonic selection counter: a detail fetch only lands if no later
    // selection (click, deep link, save) has started since it was issued.
    const selectSeqRef = useRef(0);
    useEffect(() => {
        if (!linkedQuoteId) return;
        if (autoSelectedRef.current === linkedQuoteId) return;
        if (!quotes.length) return;
        const target = quotes.find(q => String(q.quote_id) === String(linkedQuoteId));
        if (target) {
            autoSelectedRef.current = linkedQuoteId;
            setIsCreating(false);
            // The list row has no per-line `stale` block (the API computes it only
            // on GET /quotes/{id} and on PATCH): hydrate from the detail, fall
            // back to the row if the fetch fails.
            const seq = ++selectSeqRef.current;
            fetch(`${config.API_BASE_URL}/quotes/${target.quote_id}`)
                .then(res => (res.ok ? res.json() : null))
                .catch(() => null)
                .then(detail => {
                    if (seq !== selectSeqRef.current) return;
                    setSelectedQuote(detail && detail.quote_id ? detail : target);
                });
        }
    }, [linkedQuoteId, quotes]);

    const handleEmailQuoteClick = () => {
        if (!selectedQuote) return;
        setEmailModal({ show: true, quoteId: selectedQuote.quote_id });
    };

    const handleSendEmail = async (emailBody) => {
        if (!emailModal.quoteId) return;

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/quotes/${emailModal.quoteId}/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...emailSenderHeaders() },
                body: JSON.stringify({ email_body: emailBody })
            });

            if (!res.ok) {
                const errText = await res.text();
                let errorMsg = 'Failed to send email.';
                try { errorMsg = detailToString(JSON.parse(errText).detail) || errorMsg; } catch (e) {}
                throw new Error(errorMsg);
            }

            const result = await res.json();
            setNotification({ message: result.message, type: 'success' });
            setEmailModal({ show: false, quoteId: null });

        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };
    
    const mainProcessOptions = ["Laser", "Tap", "Brake", "Blast", "Finish"];
    const fabricationSubProcesses = ["Cutting", "Drilling", "Milling", "Lathe", "Welding", "Assembly", "Tapping"];
    
    const getInitialQuickLine = () => ({ id: `temp_${Date.now()}_${Math.random()}`, product_id: '', description: '', material: '', quantity: 1, price: 0 });

    const getInitialAdvancedLine = () => ({
        id: `temp_${Date.now()}_${Math.random()}`,
        product_id: '', description: '', size_length: '', size_width: '', material_id: '', is_customer_supplied_material: false, manual_material_price: '', price: '', quantity: 1, current_db_price: 0,
        process_steps: [], 
        initial_est_laser_minutes: '', initial_est_tap_minutes: '', initial_est_brake_minutes: '', initial_est_blast_minutes: '', initial_est_finish_minutes: '',
        initial_est_cutting_minutes: '', initial_est_drilling_minutes: '', initial_est_milling_minutes: '', initial_est_lathe_minutes: '', initial_est_weld_minutes: '', initial_est_assembly_minutes: '', initial_est_fab_tap_minutes: '',
        beam_on_minutes: '', cost_breakdown: null, show_breakdown: false,
    });
    
    const [quickQuoteLines, setQuickQuoteLines] = useState([getInitialQuickLine()]);
    const [advancedQuoteLines, setAdvancedQuoteLines] = useState([getInitialAdvancedLine()]);

    const [assemblies, setAssemblies] = useState([]);
    // Purchased components (consumables of type 'component') sellable on their own line.
    const [purchasedComponents, setPurchasedComponents] = useState([]);

    // Recalculate-All preview (F01): proposed lines are parked here until Apply.
    const [recalcPreview, setRecalcPreview] = useState(null);
    // Whether the Financial Summary's cost side is the STORED breakdowns or an
    // in-memory recalculation (load-time hydration or an applied Recalculate) (F41).
    const [hydrationInfo, setHydrationInfo] = useState({ ran: false, at: null });

    // Header margin / markup as fractions — what the engine and the API use.
    const headerFrac = useMemo(() => headerFractions(quoteSetup, { quotePricingMode }), [quoteSetup, quotePricingMode]);
    // Sell rates that are $0/hr on the header (F42) — red banner + approval block.
    const zeroRates = useMemo(() => zeroSellRates(quoteSetup), [quoteSetup]);

    // Functional patch so several fields on one line change in ONE state update
    // (price + price_source + price_basis must never be split across renders).
    const patchAdvancedLine = (index, patch) => {
        setAdvancedQuoteLines(prev => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    };

    // Per-assembly, per-SET summary (F20): Manufactured / Purchased / Ops
    // buckets, quoted vs calculated per set, set count (primary ÷ ppq, else the
    // ops line qty, else 1 — and which), the System Price with its source, and
    // the override state. Keyed by assembly id as a string.
    const assemblySummaries = useMemo(() => {
        if (quoteMode !== 'Advanced') return {};
        const ids = [];
        advancedQuoteLines.forEach(l => {
            if (l._source_assembly_id != null && !ids.includes(String(l._source_assembly_id))) ids.push(String(l._source_assembly_id));
        });
        const out = {};
        ids.forEach(id => {
            const asmDef = assemblies.find(a => String(a.assembly_id) === id);
            out[id] = summarizeAssemblyGroup(advancedQuoteLines, id, asmDef, { override: assemblyPriceOverrides[id], header: headerFrac });
        });
        return out;
    }, [advancedQuoteLines, quoteMode, assemblies, assemblyPriceOverrides, headerFrac]);

    // Σ qty × price; assemblies with a per-set override bill override × sets instead.
    const subtotal = useMemo(() => {
        if (quoteMode === 'Quick') return quickQuoteLines.reduce((acc, line) => acc + (Number(line.quantity) * Number(line.price)), 0);
        return computeSubtotal(advancedQuoteLines, assemblyPriceOverrides, assemblies);
    }, [quoteMode, quickQuoteLines, advancedQuoteLines, assemblyPriceOverrides, assemblies]);

    const grandTotal = useMemo(() => {
        return Number(subtotal) + Number(shipping) + Number(additionalFees) + Number(tax) - Number(discount);
    }, [subtotal, shipping, additionalFees, tax, discount]);

    // Clear schedule estimate when line items change
    useEffect(() => {
        setScheduleEstimate(null);
    }, [quickQuoteLines, advancedQuoteLines]);

    // --- NEW: Production Summary Calculation ---
    const productionSummary = useMemo(() => {
        if (quoteMode !== 'Advanced') return [];

        const summary = {
            'Laser': 0, 'Tap': 0, 'Brake': 0, 'Blast': 0, 'Finish': 0,
            'Cutting': 0, 'Drilling': 0, 'Milling': 0, 'Lathe': 0, 'Welding': 0, 'Assembly': 0, 'Tapping': 0
        };

        advancedQuoteLines.forEach(line => {
            const qty = parseInt(line.quantity) || 0;
            if (qty <= 0) return;

            // Helper to add time from initial estimates (in minutes)
            const addTime = (field, station) => {
                const mins = parseFloat(line[field]) || 0;
                if (mins > 0) summary[station] += (mins * qty);
            };

            // For Laser: prefer cost_breakdown hours (includes shuttle and uses
            // historical data when available) over raw initial estimates
            const laserDetails = line.cost_breakdown?.breakdown?.laser?.details;
            if (laserDetails) {
                const cuttingHrs = laserDetails.cutting_hours || 0;
                const shuttleHrs = laserDetails.shuttle_hours || 0;
                const totalLaserMins = (cuttingHrs + shuttleHrs) * 60;
                if (totalLaserMins > 0) summary['Laser'] += (totalLaserMins * qty);
            } else {
                addTime('initial_est_laser_minutes', 'Laser');
            }

            addTime('initial_est_tap_minutes', 'Tap');
            addTime('initial_est_brake_minutes', 'Brake');
            addTime('initial_est_blast_minutes', 'Blast');
            addTime('initial_est_finish_minutes', 'Finish');

            // Fabrication sub-processes
            addTime('initial_est_cutting_minutes', 'Cutting');
            addTime('initial_est_drilling_minutes', 'Drilling');
            addTime('initial_est_milling_minutes', 'Milling');
            addTime('initial_est_lathe_minutes', 'Lathe');
            addTime('initial_est_weld_minutes', 'Welding');
            addTime('initial_est_assembly_minutes', 'Assembly');
            addTime('initial_est_fab_tap_minutes', 'Tapping');
        });

        // Filter out zero entries and convert to hours
        return Object.entries(summary)
            .filter(([_, mins]) => mins > 0)
            .map(([station, mins]) => ({ station, hours: mins / 60 }));

    }, [advancedQuoteLines, quoteMode]);

    // Financial Performance Summary (F38/F41): revenue (overrides honoured) and
    // cost split into Material / Purchased / Outsourced / Internal labour /
    // Gas & consumables from the per-line breakdown keys, plus the lines whose
    // cost is unresolved (excluded, never counted as $0).
    const financialSummary = useMemo(() => {
        if (quoteMode !== 'Advanced') return null;
        return financialBuckets(advancedQuoteLines, assemblyPriceOverrides, assemblies);
    }, [advancedQuoteLines, quoteMode, assemblyPriceOverrides, assemblies]);

    // ------------------------------------------

    const API_BASE = config.API_BASE_URL;

    useEffect(() => { loadInitialData(); }, []);

    // --- Auto-Calculate Tax when Customer or Subtotal Changes ---
    useEffect(() => {
        // Prevent auto-calc when loading an existing quote to preserve manual overrides or historical rates
        if (isQuoteLoading.current) return; 
        // A T&M quote carries no tax: it is worked out when the order is priced.
        if (quotePricingMode === 'tm') return;
        // The T&M → Fixed undo just restored the tax as it was: keep it.
        if (skipAutoTaxOnce.current) { skipAutoTaxOnce.current = false; return; }

        // 1. Find the current customer object
        const currentCustomer = customers.find(c => String(c.customer_id) === String(selectedCustomerId));
        
        // 2. Check if customer exists and has a tax rate
        if (currentCustomer && currentCustomer.tax_rate) {
            let rate = parseFloat(currentCustomer.tax_rate);
            
            // Smart Rate Logic: Handle '8.0' (percentage) vs '0.08' (multiplier)
            // If rate > 1.0, assume it's a percentage and divide by 100
            if (rate > 1.0) {
                rate = rate / 100.0;
            }
            
            // 3. Calculate tax based on the current subtotal
            // Assuming tax_rate is stored as a decimal (e.g., 0.08 for 8%)
            const calculatedTax = subtotal * rate;
            
            // 4. Update the tax state
            setTax(calculatedTax.toFixed(2));
        } else if (currentCustomer && !currentCustomer.tax_rate) {
             // Reset tax to 0 if customer has no rate
             setTax(0);
        }
    }, [selectedCustomerId, subtotal, customers, quotePricingMode]);

    useEffect(() => {
        if (notification.message) {
            const timer = setTimeout(() => setNotification({ message: '', type: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    useEffect(() => {
        if (selectedQuote) {
            isQuoteLoading.current = true; // Set Loading Flag
            
            if (selectedQuote.customer_id) {
                setSelectedCustomerId(selectedQuote.customer_id);
            }
            setSelectedContactId(selectedQuote.contact_id || "");
            setNotes(selectedQuote.notes || '');
            setShipping(selectedQuote.shipping_amount || 0);
            setAdditionalFees(selectedQuote.additional_fees || 0);
            setTax(selectedQuote.tax_amount || 0);
            // F12: discount_amount is now returned by the API; hydrate it so a
            // reload no longer shows $0 and the next save no longer wipes it.
            setDiscount(selectedQuote.discount_amount != null ? (Number(selectedQuote.discount_amount) || 0) : 0);
            setRecalcPreview(null);
            setHydrationInfo({ ran: false, at: null });

            // Restore assembly price overrides from saved quote
            setAssemblyPriceOverrides(selectedQuote.assembly_price_overrides || {});

            if (selectedQuote.promised_date) {
                setCompletionMode('date');
                setPromisedDate(selectedQuote.promised_date);
                setLeadTime('Standard (10-14 Days)'); // Default fallback
            } else {
                setCompletionMode('lead_time');
                setLeadTime(selectedQuote.lead_time || 'Standard (10-14 Days)');
                setPromisedDate('');
            }

            setFulfillmentType(selectedQuote.fulfillment_type || '');
            setShippingAddressId(selectedQuote.shipping_address_id ? String(selectedQuote.shipping_address_id) : '');
            // T&M: mode + the stored budget notes (null pricing_mode = fixed).
            setQuotePricingMode(isTm(selectedQuote) ? 'tm' : 'fixed');
            setTmEstimate(selectedQuote.tm_estimate_total != null ? String(selectedQuote.tm_estimate_total) : '');
            setTmNte(selectedQuote.tm_not_to_exceed != null ? String(selectedQuote.tm_not_to_exceed) : '');
            setTmDirty({});
            tmToggleSnapshot.current = null;
            skipAutoTaxOnce.current = false;

            if (selectedQuote.lines) {
                // Mode detection. `is_quick_quote_item` is not a stored column — the API
                // derives it as `parts.part_id IS NULL`, so a purchased-COMPONENT line
                // (consumable_id, no part_id by design) reports true and used to drag the
                // whole quote into Quick mode, where a component cannot render at all.
                // Any line that is a component, an assembly member or an assembly-ops line
                // means the quote is Advanced regardless of what line 0 looks like.
                const hasAdvancedShape = selectedQuote.lines.some(
                    l => l.consumable_id || l.source_assembly_id || l.is_assembly_ops);
                const isAdvanced = selectedQuote.lines.length > 0
                    && (hasAdvancedShape || !selectedQuote.lines[0].is_quick_quote_item);
                setQuoteMode(isAdvanced ? 'Advanced' : 'Quick');

                // --- FIX: Define currentQuoteSetup locally ---
                const currentQuoteSetup = {
                    // quotes.target_margin is a FRACTION (0.40); the form holds percent.
                    // Quotes saved before the column existed hydrate the 40% default.
                    target_margin: (selectedQuote.target_margin != null && Number.isFinite(Number(selectedQuote.target_margin)))
                        ? +(Number(selectedQuote.target_margin) * 100).toFixed(4) : 40,
                    material_markup: selectedQuote.material_markup * 100,
                    // null = follows Markup % (renders as blank input)
                    coating_markup: selectedQuote.coating_markup == null ? '' : +(selectedQuote.coating_markup * 100).toFixed(4),
                    laser_rate: selectedQuote.laser_rate,
                    tap_rate: selectedQuote.tap_rate,
                    brake_rate: selectedQuote.brake_rate,
                    fabrication_rate: selectedQuote.fabrication_rate,
                    blast_rate: selectedQuote.blast_rate,
                    finish_rate: selectedQuote.finish_rate,
                };
                
                // Update state with local variable
                setQuoteSetup(currentQuoteSetup);
                
                if (isAdvanced) {
                    // 1. Parse Standard Lines first (Synchronous)
                    const baseLines = selectedQuote.lines.map(line => {
                        // --- Material Resolution Logic (Preserved) ---
                        let resolvedMaterialId = line.material_id;
                        let resolvedType = line.material_type;
                        let resolvedThicknessId = line.thickness_id;
                        let resolvedFinish = line.surface_finish;
                        let resolvedCategory = line.material_category || null;

                        if (resolvedMaterialId) {
                            const matMatch = materials.find(m => m.material_id === resolvedMaterialId);
                            if (matMatch) {
                                resolvedType = matMatch.material_type;
                                resolvedThicknessId = matMatch.thickness_id;
                                resolvedFinish = matMatch.surface_finish;
                                resolvedCategory = matMatch.material_category;
                            }
                        } else if (resolvedType && resolvedThicknessId) {
                            const match = materials.find(m => 
                                m.material_type === resolvedType &&
                                m.thickness_id === resolvedThicknessId &&
                                (resolvedFinish ? m.surface_finish === resolvedFinish : true)
                            );
                            if (match) {
                                resolvedMaterialId = match.material_id;
                                resolvedCategory = match.material_category;
                            }
                        }

                        let sourceAsmName = null;
                        if (line.source_assembly_id && assemblies.length > 0) {
                            const foundAsm = assemblies.find(a => a.assembly_id === line.source_assembly_id);
                            if (foundAsm) sourceAsmName = foundAsm.assembly_name;
                        }

                        return {
                            id: line.quote_part_id, 
                            part_id: line.part_id,
                            product_id: line.product_id || '', description: line.description || '',
                            size_length: line.size_length || '', size_width: line.size_width || '',
                            
                            material_id: resolvedMaterialId || '',
                            material_type: resolvedType || null,
                            thickness_id: resolvedThicknessId || null,
                            surface_finish: resolvedFinish || null,
                            material_category: resolvedCategory,

                            // Read-through from the linked part (jobq-api 4fe34ba). When
                            // line.part_id is set these come from p.material_price_method
                            // / p.manual_material_price / p.material_cost_component on
                            // the server side via the part_attributes overlay. NULL for
                            // ad-hoc lines.
                            material_price_method: line.material_price_method || null,
                            manual_material_price: line.manual_material_price != null ? parseFloat(line.manual_material_price) : null,
                            material_cost_component: line.material_cost_component != null ? parseFloat(line.material_cost_component) : null,

                            // F29: prefer the stored quote_parts column (the API falls
                            // back to the notes-derived flag for older rows).
                            is_customer_supplied_material: !!line.is_customer_supplied_material,
                            current_db_price: line.current_book_price != null ? parseFloat(line.current_book_price) : 0,
                            // F01: price basis persisted per line. null → inferred below.
                            // (a T&M line keeps 'tm_pending': PRICE_SOURCES deliberately omits it)
                            price_source: line.price_source === 'tm_pending' ? 'tm_pending' : normalizePriceSource(line.price_source, null),
                            price_basis: line.price_basis != null && Number.isFinite(Number(line.price_basis)) ? Number(line.price_basis) : null,
                            // F09/F26: server-computed staleness of the stored breakdown.
                            stale: line.stale || null,
                            beam_on_minutes: line.beam_on_minutes || '',
                            price: line.estimated_price != null ? String(line.estimated_price) : '',
                            quantity: line.quantity || 1,
                            process_steps: line.process_steps ? line.process_steps.split(',').map(s => s.trim()).filter(p => p) : [],
                            initial_est_laser_minutes: line.initial_est_laser_minutes || '',
                            initial_est_tap_minutes: line.initial_est_tap_minutes || '',
                            initial_est_brake_minutes: line.initial_est_brake_minutes || '',
                            initial_est_blast_minutes: line.initial_est_blast_minutes || '',
                            initial_est_finish_minutes: line.initial_est_finish_minutes || '',
                            initial_est_cutting_minutes: line.initial_est_cutting_minutes || '',
                            initial_est_drilling_minutes: line.initial_est_drilling_minutes || '',
                            initial_est_milling_minutes: line.initial_est_milling_minutes || '',
                            initial_est_lathe_minutes: line.initial_est_lathe_minutes || '',
                            initial_est_weld_minutes: line.initial_est_weld_minutes || '',
                            initial_est_assembly_minutes: line.initial_est_assembly_minutes || '',
                            initial_est_fab_tap_minutes: line.initial_est_fab_tap_minutes || '',
                            
                            // Essential for Consumable Hydration:
                            consumable_id: line.consumable_id || null, // Ensure ID is mapped
                            _source_assembly_id: line.source_assembly_id || null,
                            _source_assembly_name: sourceAsmName || 'Loaded Assembly',
                            is_assembly_ops: !!line.is_assembly_ops,

                            cost_breakdown: line.cost_breakdown || null, show_breakdown: !!line.cost_breakdown,
                        };
                    });

                    // F01: legacy lines (saved before price_source existed) get their
                    // basis inferred ONCE from the stored number; the badge marks it "?".
                    // Component lines are inferred after their vendor cost is hydrated.
                    const headerF = headerFractions(currentQuoteSetup, { quotePricingMode: selectedQuote.pricing_mode });
                    // A line is a purchased COMPONENT whenever it carries a consumable_id —
                    // STANDALONE (bought finished and resold, no assembly) or as a member of
                    // one. Assembly members with no part_id count too: lines authored before
                    // consumable_id was persisted carry neither id and are resolved by name.
                    const isComponentCandidate = (l) => !l.is_assembly_ops && (l.consumable_id || (l._source_assembly_id && !l.part_id));
                    baseLines.forEach(l => {
                        if (!l.price_source && !isComponentCandidate(l)) {
                            const inf = inferPriceSource(l, l.cost_breakdown, headerF);
                            l.price_source = inf.price_source;
                            l.price_basis = inf.price_basis;
                            l._price_source_inferred = true;
                        }
                    });

                    // 2. Identify Consumable Candidates. An ASSEMBLY-sourced component reads
                    // its vendor cost from the assembly's component list; a STANDALONE one has
                    // no such list, so it is hydrated from the consumable calculator below
                    // (hydrateStandaloneComponents) — the same server-side vendor rule.
                    const assemblyIdsToFetch = [...new Set(
                        baseLines
                            .filter(l => isComponentCandidate(l) && l._source_assembly_id)
                            .map(l => l._source_assembly_id)
                    )];
                    const hasStandaloneComponents = baseLines.some(l => isComponentCandidate(l) && !l._source_assembly_id);

                    if (assemblyIdsToFetch.length > 0 || hasStandaloneComponents) {
                        // 3. Async Hydration
                        Promise.all(assemblyIdsToFetch.map(asmId => 
                            // F38: a failed fetch is recorded as null (NOT an empty list) so
                            // the component's cost renders as "unresolved" rather than $0.
                            fetch(`${config.API_BASE_URL}/assemblies/${asmId}/consumable-components`)
                                .then(res => res.ok ? res.json() : null)
                                .then(data => ({ asmId, components: Array.isArray(data) ? data : null }))
                                .catch(() => ({ asmId, components: null }))
                        )).then(results => {
                            // Map results: AssemblyID -> List of Components
                            const componentMap = {};
                            results.forEach(({ asmId, components }) => {
                                componentMap[asmId] = components;
                            });

                            // Enrich lines with restored metadata
                            const enrichedLines = baseLines.map(line => {
                                // A line is a purchased component when it carries `consumable_id`,
                                // regardless of whether the backend also stamped a `part_id` on it.
                                // Older quotes authored through the "Add Part" path persist both
                                // (consumable_id + a temp `parts` row); newer "Quick Quote" lines
                                // persist only consumable_id. Both must render as Components.
                                if (isComponentCandidate(line)) {
                                    const components = componentMap[line._source_assembly_id] || [];
                                    
                                    // Match by ID first, fallback to name
                                    let match = null;
                                    if (line.consumable_id) {
                                        match = components.find(c => c.consumable_id === line.consumable_id);
                                    }
                                    if (!match) {
                                        match = components.find(c => c.product_id === line.product_id);
                                    }

                                    const baseCost = match ? num(match.cost, 0) : 0;
                                    const basePrice = match ? num(match.price, 0) : 0;
                                    const resolvedConsumableId = match ? match.consumable_id : line.consumable_id;

                                    const enriched = {
                                        ...line,
                                        is_consumable_view: true,
                                        consumable_id: resolvedConsumableId,
                                        _cost: baseCost,
                                        _book_price: basePrice,
                                        // Cost is only "known" when the component row was actually found.
                                        _cost_resolved: !!match,
                                        _cost_source: match ? (match.cost_source || null) : null,
                                        _below_cost: !!match && (match.below_cost === true || (basePrice > 0 && baseCost > basePrice + 0.005)),
                                    };
                                    // Basis: stored price_source when present, else inferred once.
                                    if (!enriched.price_source) {
                                        const inf = inferPriceSource(enriched, enriched.cost_breakdown, headerF);
                                        enriched.price_source = inf.price_source;
                                        enriched.price_basis = inf.price_basis;
                                        enriched._price_source_inferred = true;
                                    }
                                    enriched.pricing_mode = enriched.price_source === 'book' ? 'book'
                                        : enriched.price_source === 'markup' ? 'markup' : 'manual';
                                    return enriched;
                                }
                                return line;
                            });
                            
                            setAdvancedQuoteLines(enrichedLines);
                            // A STANDALONE component found no assembly list to cost from, so it
                            // is sitting at "cost unresolved". Resolve it from the consumable
                            // calculator, then publish again — a failed call leaves the line
                            // unresolved rather than pretending $0.
                            hydrateStandaloneComponents(enrichedLines, currentQuoteSetup).then(finalLines => {
                                if (finalLines !== enrichedLines) setAdvancedQuoteLines(finalLines);
                            });
                            // Only recalculate costs if lines don't already have saved breakdowns
                            const needsHydration = enrichedLines.some(l => !l.is_consumable_view && (l.part_id || l.product_id) && !l.cost_breakdown);
                            if (needsHydration) {
                                hydrateCostsForDashboard(enrichedLines, currentQuoteSetup, selectedQuote.status !== 'Draft');
                            }
                        });
                    } else {
                        // No consumables to hydrate
                        setAdvancedQuoteLines(baseLines);
                        // Only recalculate costs if lines don't already have saved breakdowns
                        const needsHydration = baseLines.some(l => !l.is_consumable_view && (l.part_id || l.product_id) && !l.cost_breakdown);
                        if (needsHydration) {
                            hydrateCostsForDashboard(baseLines, currentQuoteSetup, selectedQuote.status !== 'Draft');
                        }
                    }

                } else {
                    // Quick Quote Logic (Unchanged)
                    const loadedQuickLines = selectedQuote.lines.map(line => ({
                        id: line.quote_part_id, part_id: line.part_id, product_id: line.product_id || '',
                        description: line.description.includes('(Material:') ? line.description.split('(Material:')[0].trim() : line.description,
                        material: line.description.includes('(Material:') ? line.description.split('(Material:')[1].replace(')','').trim() : '',
                        quantity: line.quantity || 1, price: line.estimated_price != null ? String(line.estimated_price) : 0,
                    }));
                    setQuickQuoteLines(loadedQuickLines.length > 0 ? loadedQuickLines : [getInitialQuickLine()]);
                    setAdvancedQuoteLines([getInitialAdvancedLine()]);
                }
            }

            // Clear loading flag after a brief delay to allow state to settle
            setTimeout(() => {
                isQuoteLoading.current = false;
            }, 200);
        }
    }, [selectedQuote, materials, assemblies]);

    useEffect(() => {
        const fetchCustomerParts = async () => {
            if (selectedCustomerId) {
                try {
                    const res = await fetch(`${API_BASE}/customers/${selectedCustomerId}/parts`);
                    if (!res.ok) throw new Error("Could not fetch customer's parts.");
                    const parts = await res.json();
                    setCustomerParts(parts);
                } catch (err) {
                    setNotification({ message: err.message, type: 'danger' });
                    setCustomerParts([]);
                }
            } else {
                setCustomerParts([]);
            }
        };
        fetchCustomerParts();
    }, [selectedCustomerId]);

    // --- Load Contacts when Customer Changes ---
    useEffect(() => {
        if (selectedCustomerId) {
            const fetchContacts = async () => {
                try {
                    const res = await fetch(`${API_BASE}/customers/${selectedCustomerId}/contacts`);
                    if (res.ok) {
                        const data = await res.json();
                        setContacts(data);
                    } else {
                        setContacts([]);
                    }
                } catch (err) {
                    console.error("Failed to load contacts", err);
                }
            };
            fetchContacts();
        } else {
            setContacts([]);
            setSelectedContactId("");
        }
    }, [selectedCustomerId]);

    // Auto-fill fulfillmentType from customer's default_fulfillment_type when
    // a customer is picked AND we're not loading an existing quote (loading
    // an existing quote uses its stored fulfillment_type, not the customer
    // default — quote is a frozen historical record). Only fires for new
    // quotes (selectedQuote == null) or when fulfillmentType is currently
    // empty so we don't clobber an operator's explicit pick.
    useEffect(() => {
        if (!selectedCustomerId) return;
        if (selectedQuote && selectedQuote.fulfillment_type) return;  // edit mode preserves stored value
        if (fulfillmentType) return;  // operator already picked
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/customers/${selectedCustomerId}`);
                if (!res.ok) return;
                const cust = await res.json();
                if (cust.default_fulfillment_type) {
                    setFulfillmentType(cust.default_fulfillment_type);
                }
            } catch (err) {
                console.error("Failed to fetch customer default fulfillment", err);
            }
        })();
    }, [selectedCustomerId, selectedQuote, fulfillmentType]);

    // Load ship-to addresses for the selected customer; pre-select the default
    // for NEW quotes (an existing quote keeps its stored shipping_address_id).
    useEffect(() => {
        if (!selectedCustomerId) { setAddresses([]); setShippingAddressId(''); return; }
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/customers/${selectedCustomerId}/addresses`);
                if (!res.ok) return;
                const list = await res.json();
                setAddresses(list);
                // Never keep an address the selected customer doesn't own — switching
                // customers leaves the previous one's id in state and the <select>
                // hides that by falling back to its first option. See
                // resolveShippingAddressId for the full failure it prevents.
                setShippingAddressId(resolveShippingAddressId(list, {
                    storedQuoteAddressId: selectedQuote ? selectedQuote.shipping_address_id : null,
                    currentPick: shippingAddressId,
                }));
            } catch (e) { /* non-fatal */ }
        })();
    }, [selectedCustomerId, selectedQuote, shippingAddressId]);

    async function loadInitialData() {
        setLoading(true);
        setNotification({ message: '', type: '' });
        try {
            // Added assembliesRes to the destructuring array below
            const [quotesRes, customersRes, materialsRes, typesRes, finishesRes, thicknessesRes, assembliesRes, yieldPoliciesRes, consumablesRes] = await Promise.all([
                fetch(`${API_BASE}/quotes`),
                fetch(`${API_BASE}/customers`),
                fetch(`${API_BASE}/materials`),
                fetch(`${API_BASE}/materials/types`),
                fetch(`${API_BASE}/materials/finishes`),
                fetch(`${API_BASE}/material_thicknesses`),
                // F30: include retired assemblies so a quote that references one still
                // resolves its ppq, name and system price (the picker filters them out).
                fetch(`${API_BASE}/assemblies?include_inactive=true`),
                fetch(`${API_BASE}/yield_policies`),
                // Only `component` rows are sellable — the rest of `consumables` is shop
                // supply (nozzles, gas, shot) that is never quoted to a customer.
                fetch(`${API_BASE}/consumables?consumable_type=component`)
            ]);

            if (!quotesRes.ok) throw new Error('Failed to load quotes');

            setQuotes(await quotesRes.json());
            setCustomers((await customersRes.json()).sort((a, b) => a.name.localeCompare(b.name)));
            setMaterials(await materialsRes.json());
            if (yieldPoliciesRes.ok) setYieldPolicies(await yieldPoliciesRes.json());

            const typesData = await typesRes.json();
            const finishesData = await finishesRes.json();
            const thicknessesData = await thicknessesRes.json();

            setFilterOptions({
                types: typesData.map(t => t.type_name).sort(),
                finishes: finishesData.map(f => f.finish_name).sort(),
                thicknesses: thicknessesData.sort((a,b) => a.thickness_in - b.thickness_in)
            });
    
            // Now assembliesRes is defined and this will work
            setAssemblies(await assembliesRes.json()); 

            // A failed fetch leaves the picker empty rather than failing the page.
            // The type filter is applied again here so a server that ignores the
            // query param cannot leak nozzles and gas into a customer quote.
            if (consumablesRes.ok) {
                const componentRows = await consumablesRes.json();
                setPurchasedComponents(
                    (Array.isArray(componentRows) ? componentRows : [])
                        .filter(c => c.consumable_type === 'component')
                        .sort((a, b) => (a.product_id || '').localeCompare(b.product_id || ''))
                );
            }

        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    }
    
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setMaterialFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            if (name === 'owner' || name === 'type') { newFilters.thickness = ''; newFilters.finish = ''; }
            if (name === 'thickness') { newFilters.finish = ''; }
            return newFilters;
        });
    };

    const handleCreateNew = () => {
        setIsCreating(true);
        setSelectedQuote(null);
        setSelectedCustomerId('');
        setNotes('');
        setQuoteMode('Quick');
        setQuickQuoteLines([getInitialQuickLine()]);
        setAdvancedQuoteLines([getInitialAdvancedLine()]);
        setShowNewCustomerForm(false);
        setNewCustomerName("");
        setShipping(0);
        setAdditionalFees(0);
        setTax(0);
        setDiscount(0);
        setRecalcPreview(null);
        setHydrationInfo({ ran: false, at: null });
        setCompletionMode('lead_time');
        setLeadTime('Standard (10-14 Days)');
        setPromisedDate('');
        setAssemblyPriceOverrides({});
        setFulfillmentType('');
        setShippingAddressId('');
        // A new quote starts from the shared defaults, not from whatever quote was open last.
        setQuoteSetup(newQuoteRateSetup());
        setQuotePricingMode('fixed');
        setTmEstimate('');
        setTmNte('');
        setTmDirty({});
        tmToggleSnapshot.current = null;
        skipAutoTaxOnce.current = false;
    };

    // Pricing toggle (T&M slice 1d). Fixed → T&M (confirm): Advanced lines only, every line $0 on
    // the 'tm_pending' basis (the server forces the same). T&M → fixed (confirm): straight back
    // after a Fixed → T&M on a quote stored as fixed, every line not changed while in T&M gets its
    // fixed price and basis back (all unchanged: the lines and mode come back exactly as they
    // were), and a line changed or added in T&M takes its calculated price. The typed tax comes
    // back when neither the subtotal nor the customer changed, as a Fixed edit would keep it;
    // otherwise the auto-tax works it out again. On a quote stored as T&M every line takes its
    // calculated price. A line with no calculated price makes the switch wait ("Calc first").
    const handleQuotePricingModeChange = (next) => {
        if (next === quotePricingMode) return;
        if (next === 'tm') {
            const refusals = tmQuoteLineRefusals(quoteMode === 'Quick' ? [] : advancedQuoteLines,
                { quickMode: quoteMode === 'Quick', quickLines: quickQuoteLines });
            if (refusals.length > 0) {
                setNotification({ message: `Can't switch to Time & Materials: ${refusals.join(' · ')}`, type: 'warning' });
                return;
            }
            if (Object.keys(assemblyPriceOverrides).length > 0) {
                setNotification({ message: `Can't switch to Time & Materials: assembly price overrides are ${TM_NOT_IN_V1.toLowerCase()}.`, type: 'warning' });
                return;
            }
            if (!window.confirm('Switch this quote to Time & Materials? Every line goes to $0.00 and loses its price basis, and the tax goes to $0.00 (it is worked out when the order is priced). Switching back before saving restores them on every line you leave unchanged; a line you change or add takes its calculated price.')) return;
            tmToggleSnapshot.current = isTm(selectedQuote) ? null : {
                advancedQuoteLines, quickQuoteLines, quoteMode, tax, subtotal, customerId: selectedCustomerId,
            };
            setQuoteMode('Advanced');
            setAdvancedQuoteLines(prev => toTmLines(prev));
            setTax(0);
            setQuotePricingMode('tm');
            return;
        }
        const calcFirst = (missing) => setNotification({ message: `Calc first: ${missing.join(', ')} ha${missing.length === 1 ? 's' : 've'} no calculated price yet. Press Calc on ${missing.length === 1 ? 'it' : 'them'}, then switch.`, type: 'warning' });
        const snap = tmToggleSnapshot.current;
        if (snap) {
            const back = restoreFixedLines(snap.advancedQuoteLines, advancedQuoteLines);
            // The typed tax stands when nothing it follows changed (a Fixed edit keeps it the same way).
            const sameCustomer = String(selectedCustomerId) === String(snap.customerId);
            const sameSubtotal = back.untouched
                || Math.abs(computeSubtotal(back.lines, assemblyPriceOverrides, assemblies) - snap.subtotal) < 0.005;
            const keepTax = sameCustomer && sameSubtotal;
            const taxNote = keepTax ? 'The tax from before the switch comes back.'
                : `The tax is worked out again for the new ${sameCustomer ? 'subtotal' : 'customer'}.`;
            let question;
            if (back.untouched && keepTax) question = 'Switch this quote back to fixed pricing? The prices, price bases and tax from before the switch come back.';
            else if (back.untouched) question = `Switch this quote back to fixed pricing? The prices and price bases from before the switch come back. ${taxNote}`;
            else question = `Switch this quote back to fixed pricing? Lines not changed since the switch get their prices and price bases from before it back; lines changed or added while in Time & Materials get their calculated price (Calculated basis). ${taxNote}`;
            if (!window.confirm(question)) return;
            if (back.missing.length > 0) { calcFirst(back.missing); return; }
            tmToggleSnapshot.current = null;
            skipAutoTaxOnce.current = keepTax;
            setQuoteMode(back.untouched ? snap.quoteMode : 'Advanced');
            setAdvancedQuoteLines(back.lines);
            if (back.untouched) setQuickQuoteLines(snap.quickQuoteLines);
            setTax(snap.tax);
            setQuotePricingMode('fixed');
            return;
        }
        if (!window.confirm('Switch this quote back to fixed pricing? Each line gets its calculated price (Calculated basis).')) return;
        const { lines, missing } = toFixedLines(advancedQuoteLines);
        if (missing.length > 0) { calcFirst(missing); return; }
        setAdvancedQuoteLines(lines);
        setQuotePricingMode('fixed');
    };

    // GET /quotes/{id}: the only quote-returning reads that carry the per-line
    // `stale` block are the detail GET and the PATCH response — never the list.
    // Returns null when the detail is unavailable so callers keep the list row.
    const fetchQuoteDetail = async (quoteId) => {
        try {
            const res = await fetch(`${API_BASE}/quotes/${quoteId}`);
            if (!res.ok) return null;
            const data = await res.json();
            return data && data.quote_id ? data : null;
        } catch (e) {
            console.warn('Quote detail fetch failed; using the list row', e);
            return null;
        }
    };

    const handleSelectQuote = async (quote) => {
        const seq = ++selectSeqRef.current;
        setIsCreating(false);
        const detail = await fetchQuoteDetail(quote.quote_id);
        if (seq !== selectSeqRef.current) return; // a later selection won
        setSelectedQuote(detail || quote);
    };
    
    const handleCreateCustomer = async () => {
        if (!newCustomerName.trim()) {
            setNotification({ message: 'Customer name is required.', type: 'warning' });
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/customers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newCustomerName, is_temporary: true })
            });
            if (!res.ok) throw new Error('Failed to create customer');
            const newCustomer = await res.json();
            setCustomers(prev => [...prev, newCustomer].sort((a, b) => a.name.localeCompare(b.name)));
            setSelectedCustomerId(newCustomer.customer_id);
            setShowNewCustomerForm(false);
            setNewCustomerName("");
            setNotification({ message: `Temporary customer '${newCustomer.name}' created.`, type: 'success' });
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        }
    };

    const addExistingPartToQuote = async (partId) => {
        try {
            const res = await fetch(`${API_BASE}/parts/${partId}`);
            if (!res.ok) throw new Error('Could not fetch part details.');
            const part = await res.json();
            
            let resolvedMaterialId = part.material_id || '';
            let resolvedType = part.material_type;
            let resolvedThickness = part.thickness_id;
            let resolvedFinish = part.surface_finish;
            let resolvedCategory = part.stock_type; // Priority 1: Use Part's definition

            // If we have an ID, look up the authoritative specs from the materials list
            if (resolvedMaterialId) {
                const matMatch = materials.find(m => m.material_id === resolvedMaterialId);
                if (matMatch) {
                    resolvedType = matMatch.material_type;
                    resolvedThickness = matMatch.thickness_id;
                    resolvedFinish = matMatch.surface_finish;
                    // If part didn't specify stock type, trust the material definition
                    if (!resolvedCategory) resolvedCategory = matMatch.material_category;
                }
            } 
            // Fallback lookup
            else if (part.material_type && part.thickness_id) {
                const match = materials.find(m => 
                    m.material_type === part.material_type &&
                    m.thickness_id === part.thickness_id &&
                    (part.surface_finish ? m.surface_finish === part.surface_finish : true)
                );
                if (match) {
                    resolvedMaterialId = match.material_id;
                    if (!resolvedCategory) resolvedCategory = match.material_category;
                }
            }

            // --- UPDATED: Apply Historical Data Priority Logic ---
            const timeEstimates = applyHistoricalData(part);

            const isManualZeroCost = (part.material_price_method === 'manual' || part.material_price_method === 'manual_price') 
                && parseFloat(part.manual_material_price || 0) === 0;

            const newAdvancedLine = {
                id: `temp_${Date.now()}_${Math.random()}`,
                part_id: part.part_id,
                product_id: part.product_id || '',
                description: part.description || '',
                current_db_price: part.price != null ? parseFloat(part.price) : 0,
                price: part.price != null && parseFloat(part.price) > 0 ? String(part.price) : '',
                size_length: part.size_length || '',
                size_width: part.size_width || '',

                material_id: resolvedMaterialId,
                material_type: part.material_type,
                thickness_id: part.thickness_id,
                surface_finish: part.surface_finish,
                material_category: resolvedCategory,
                material_price_method: part.material_price_method || null,
                material_cost_component: part.material_cost_component != null ? parseFloat(part.material_cost_component) : null,

                manual_material_price: part.manual_material_price || part.material_cost_component || '',
                is_customer_supplied_material:
                    (part.notes || '').includes("Customer Supplied Material") ||
                    (part.material_type || '').toLowerCase().includes("customer") ||
                    isManualZeroCost,
                quantity: 1,
                process_steps: part.process_steps ? part.process_steps.split(',').map(s => s.trim()).filter(p => p) : [],

                ...timeEstimates,

                beam_on_minutes: part.beam_on_minutes || '',
                cost_breakdown: null, show_breakdown: false,
                // F01: a seeded book price is on the 'book' basis; an unpriced line
                // adopts the calculated price on its first Calc.
                price_source: part.price != null && parseFloat(part.price) > 0 ? 'book' : 'calculated',
                price_basis: null,
            };
            setAdvancedQuoteLines(prev => [...prev, newAdvancedLine]);
            setQuoteMode('Advanced');
            setNotification({ message: `Added part '${part.product_id}' to quote.`, type: 'success' });
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        }
    };

    // Add a PURCHASED COMPONENT as its own quote line — bought finished from a
    // vendor and resold, with no assembly behind it. The line carries only
    // consumable_id (never a part_id): the API deliberately skips temp-part
    // creation for a component line, and the SO side already recognises the shape
    // ("loose purchased component (no assembly BOM row)"). Price is resolved by
    // the same consumable calculator the Calc button uses; a component with no
    // vendor cost on file prices at $0 and says so rather than silently guessing.
    const addComponentToQuote = async (component) => {
        if (!component || !component.consumable_id) return;
        const newLine = {
            ...getInitialAdvancedLine(),
            id: `temp_${Date.now()}_${Math.random()}`,
            part_id: null,
            consumable_id: component.consumable_id,
            _source_assembly_id: null,
            is_consumable_view: true,
            is_assembly_ops: false,
            product_id: component.product_id || '',
            description: component.description || '',
            quantity: 1,
            price: '',
            _cost: 0,
            _cost_resolved: false,
            _book_price: component.price != null ? parseFloat(component.price) || 0 : 0,
            cost_breakdown: null,
            price_source: null,
            price_basis: null,
        };
        setAdvancedQuoteLines(prev => [...prev, newLine]);
        setQuoteMode('Advanced');
        setNotification({ message: `Added component '${component.product_id}' to quote.`, type: 'success' });

        try {
            const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildConsumablePayload(newLine, quoteSetup)),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const result = await res.json();
            setAdvancedQuoteLines(prev => prev.map(l => {
                if (l.id !== newLine.id) return l;
                const priced = {
                    ...l,
                    ...consumableExtrasFromCalc(l, result),
                    cost_breakdown: result,
                    // Cost + markup is the default basis for a bought-and-resold item;
                    // the operator can switch to Book or type a manual price.
                    price: result.calculated_price != null ? String(round2(result.calculated_price)) : l.price,
                    price_source: 'markup',
                    price_basis: (parseFloat(quoteSetup.material_markup) || 0) / 100,
                    pricing_mode: 'markup',
                    stale: null,
                };
                return priced;
            }));
        } catch (e) {
            // The line stays on the quote at "cost unresolved" — Calc retries it.
            console.warn('addComponentToQuote: consumable calculator unavailable', component.product_id, e);
        }
    };

    const handleBulkImportClick = () => {
        if (!selectedCustomerId) {
            setNotification({ message: "Please select a customer first.", type: "warning" });
            return;
        }
        fileInputRef.current.click();
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => processCSV(e.target.result);
        reader.readAsText(file);
        event.target.value = null;
    };

    const processCSV = (csvText) => {
        const lines = csvText.split(/\r?\n/);
        const headers = lines[0].split(',').map(h => h.trim());
        const partNameIndex = headers.indexOf('Part Name');
        const numberIndex = headers.indexOf('Number');

        if (partNameIndex === -1 || numberIndex === -1) {
            setNotification({ message: "CSV Missing columns: 'Part Name' and 'Number'", type: "danger" });
            return;
        }

        const newLines = [];
        for (let i = 1; i < lines.length; i++) {
            const row = lines[i].split(',').map(c => c.trim());
            if (row.length < 2 || !row[partNameIndex]) continue;
            const productId = row[partNameIndex];
            const quantity = parseInt(row[numberIndex], 10) || 1;
            const existingPart = customerParts.find(p => p.product_id.trim() === productId);

            if (existingPart) {
                let resolvedMaterialId = existingPart.material_id || '';
                if (!resolvedMaterialId && existingPart.material_type && existingPart.thickness_id) {
                    const match = materials.find(m => 
                        m.material_type === existingPart.material_type &&
                        m.thickness_id === existingPart.thickness_id &&
                        (existingPart.surface_finish ? m.surface_finish === existingPart.surface_finish : true)
                    );
                    if (match) resolvedMaterialId = match.material_id;
                }

                const timeEstimates = applyHistoricalData(existingPart);

                newLines.push({
                    id: `temp_${Date.now()}_${Math.random()}`,
                    part_id: existingPart.part_id,
                    product_id: existingPart.product_id || '',
                    description: existingPart.description || '',
                    current_db_price: existingPart.price != null ? parseFloat(existingPart.price) : 0,
                    price: existingPart.price != null ? String(existingPart.price) : '',
                    size_length: existingPart.size_length || '',
                    size_width: existingPart.size_width || '',
                    material_id: resolvedMaterialId,
                    material_price_method: existingPart.material_price_method || null,
                    material_cost_component: existingPart.material_cost_component != null ? parseFloat(existingPart.material_cost_component) : null,
                    manual_material_price: existingPart.manual_material_price || existingPart.material_cost_component || '',
                    is_customer_supplied_material: (existingPart.notes || '').includes("Customer Supplied Material"),
                    quantity: quantity,
                    process_steps: existingPart.process_steps ? existingPart.process_steps.split(',').map(s => s.trim()).filter(p => p) : [],
                    ...timeEstimates,
                    beam_on_minutes: existingPart.beam_on_minutes || '',
                    cost_breakdown: null, show_breakdown: false,
                    price_source: existingPart.price != null && parseFloat(existingPart.price) > 0 ? 'book' : 'calculated',
                    price_basis: null,
                });
            } else {
                newLines.push({
                    ...getInitialAdvancedLine(),
                    product_id: productId,
                    quantity: quantity,
                    description: 'Imported from CSV'
                });
            }
        }

        if (newLines.length > 0) {
            setAdvancedQuoteLines(prev => (prev.length === 1 && !prev[0].product_id) ? newLines : [...prev, ...newLines]);
            setQuoteMode('Advanced');
            setNotification({ message: `Imported ${newLines.length} parts.`, type: "success" });
        } else {
            setNotification({ message: "No valid rows found.", type: "warning" });
        }
    };

    const handleLineChange = (lines, setLines, index, field, value) => {
        const updatedLines = [...lines];
        const line = updatedLines[index];
        line[field] = value;
        setLines(updatedLines);
    };

    // Typing in a Price box makes the line MANUAL (F01): the basis is recorded
    // with the number so Calc / Recalculate know to keep it (drift badge only).
    const setLinePriceManual = (index, value) => {
        patchAdvancedLine(index, { price: value, price_source: 'manual', price_basis: null, _price_source_inferred: false });
    };

    // Adopt a price together with the basis that produced it (radios, Apply).
    const applyLinePrice = (index, sel) => {
        // A T&M line keeps no price (0 / tm_pending): nothing may apply a book / calculated price to it.
        if (!sel || isTmQuote) return;
        const price = num(sel.price, NaN);
        patchAdvancedLine(index, {
            price: Number.isFinite(price) ? price.toFixed(2) : '',
            price_source: normalizePriceSource(sel.price_source),
            price_basis: sel.price_basis != null && Number.isFinite(Number(sel.price_basis)) ? Number(sel.price_basis) : null,
            _price_source_inferred: false,
        });
    };

    const handleToggleProcessForLine = (index, processName) => {
        const updatedLines = [...advancedQuoteLines];
        const line = updatedLines[index];
        const currentProcesses = line.process_steps || [];
        line.process_steps = currentProcesses.includes(processName) 
            ? currentProcesses.filter(p => p !== processName) 
            : [...currentProcesses, processName];
        setAdvancedQuoteLines(updatedLines);
    };

    const handleQuoteChange = (field, value) => {
        setSelectedQuote(prev => ({ 
            ...(prev || {}), 
            [field]: value,
            // FIX: Ensure we don't lose the customer ID if we are starting from scratch and typing PO first
            customer_id: prev?.customer_id || selectedCustomerId 
        }));
    };

    const handleRevisionChange = async (index, newPartId) => {
        try {
            const res = await fetch(`${API_BASE}/parts/${newPartId}`);
            if (!res.ok) throw new Error('Could not fetch revision details.');
            const part = await res.json();
            
            let resolvedMaterialId = part.material_id || '';
            
            let resolvedCategory = part.stock_type; 

            if (!resolvedMaterialId && part.material_type && part.thickness_id) {
                const match = materials.find(m => 
                    m.material_type === part.material_type &&
                    m.thickness_id === part.thickness_id &&
                    (part.surface_finish ? m.surface_finish === part.surface_finish : true)
                );
                if (match) {
                    resolvedMaterialId = match.material_id;
                    // Fallback to material definition if part didn't have stock_type
                    if (!resolvedCategory) resolvedCategory = match.material_category;
                }
            } else if (resolvedMaterialId) {
                 const matMatch = materials.find(m => m.material_id === resolvedMaterialId);
                 // Fallback to material definition if part didn't have stock_type
                 if (matMatch && !resolvedCategory) resolvedCategory = matMatch.material_category;
            }

            const timeEstimates = applyHistoricalData(part);

            const updatedLines = [...advancedQuoteLines];
            updatedLines[index] = {
                ...updatedLines[index],
                part_id: part.part_id,
                product_id: part.product_id,
                description: part.description || '',
                current_db_price: part.price != null ? parseFloat(part.price) : 0,
                price: part.price != null ? String(part.price) : '',
                size_length: part.size_length || '',
                size_width: part.size_width || '',

                material_id: resolvedMaterialId,
                material_type: part.material_type,
                thickness_id: part.thickness_id,
                surface_finish: part.surface_finish,
                material_category: resolvedCategory,
                material_price_method: part.material_price_method || null,
                material_cost_component: part.material_cost_component != null ? parseFloat(part.material_cost_component) : null,

                manual_material_price: part.manual_material_price || part.material_cost_component || '',
                is_customer_supplied_material: (part.notes || '').includes("Customer Supplied Material"),
                
                // Spread new time estimates
                ...timeEstimates,

                beam_on_minutes: part.beam_on_minutes || '',
                process_steps: part.process_steps ? part.process_steps.split(',').map(s => s.trim()).filter(p => p) : [],
                cost_breakdown: null, show_breakdown: false, stale: null,
                price_source: part.price != null && parseFloat(part.price) > 0 ? 'book' : 'calculated',
                price_basis: null,
            };
            setAdvancedQuoteLines(updatedLines);
            setNotification({ message: `Updated to Revision ${part.revision_code || '?'}.`, type: 'success' });
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        }
    };

    const addLine = (setLines, initialLineFactory) => { setLines(prev => [...prev, initialLineFactory()]); };
    
    const removeLine = (lines, setLines, lineId) => {
        if (lines.length <= 1) {
            setNotification({ message: "Quote must have at least one line.", type: 'warning' });
            return;
        }
        // An ASSEMBLY OPS line is the ONLY carrier of the assembly's at/after-join
        // work — its join welding, its outsourced coating, its finish. The primary
        // part line deliberately excludes those stations, so removing this line
        // deletes that cost and price outright and no reprice can recover it (the
        // primary has no weld/coating data left to price from). The conversion to a
        // sales order then refuses outright unless a price override exists.
        const target = lines.find(line => line.id === lineId);
        if (target?.is_assembly_ops) {
            const label = target.description || 'this assembly';
            const ok = window.confirm(
                `Remove the assembly operations line for ${label}?\n\n` +
                `This line carries ALL of the assembly's at/after-join work — join welding, ` +
                `outsourced coating and finish. The primary part line does not price those ` +
                `stations, so removing it drops that cost and price from the quote with ` +
                `nothing carrying them, and "Recalculate All" cannot bring them back.\n\n` +
                `Remove it anyway?`
            );
            if (!ok) return;
        }
        // F39: the PRIMARY line is where the set count comes from (qty ÷ ppq).
        // Confirm before dropping it, and say what the card falls back to.
        if (target && !target.is_assembly_ops && !target.is_consumable_view && target._source_assembly_id) {
            const asmDef = assemblies.find(a => a.assembly_id === target._source_assembly_id);
            if (asmDef && asmDef.primary_part_id != null && asmDef.primary_part_id === target.part_id) {
                const hasOps = lines.some(l => l.is_assembly_ops && l._source_assembly_id === target._source_assembly_id);
                const ok = window.confirm(
                    `Remove the PRIMARY part line (${target.product_id || 'primary'}) of assembly ` +
                    `${asmDef.assembly_name || target._source_assembly_name || target._source_assembly_id}?\n\n` +
                    `The set count is derived from this line (qty ÷ ppq ${asmDef.primary_part_quantity || 1}). Without it the ` +
                    `Assembly Pricing card ${hasOps ? 'takes the set count from the ASSEMBLY OPS line qty' : 'falls back to 1 set and the Qty cascade is disabled'}, ` +
                    `and the quote PDF prints the assembly qty the same way. A replacement part added later cannot rejoin this group.\n\n` +
                    `Remove it anyway?`
                );
                if (!ok) return;
            }
        }
        setLines(lines.filter(line => line.id !== lineId));
    };
    
    const buildLinePayload = (line, currentSetup, opsAsmIds = null) => {
        const totalFabMinutes = fabricationSubProcesses.reduce((total, proc) => {
            const field = (proc === 'Tapping') ? 'initial_est_fab_tap_minutes'
                : (proc === 'Welding') ? 'initial_est_weld_minutes'
                : `initial_est_${proc.toLowerCase()}_minutes`;
            return total + safeParseFloatMinutes(line[field]);
        }, 0);

        return {
            part_id: line.part_id || null,
            consumable_id: line.consumable_id || null,
            size_length: parseFloat(line.size_length) || 0,
            size_width: parseFloat(line.size_width) || 0,
            material_id: line.material_id ? parseInt(line.material_id, 10) : null,
            is_customer_supplied_material: line.is_customer_supplied_material,
            manual_material_price: safeParseFloatMinutes(line.manual_material_price),
            
            // --- UPDATED: Pass Material Characteristics Explicitly ---
            // This ensures Gas/Consumable calc works even if material_id is missing/custom
            material_type: line.material_type || null,
            thickness_id: line.thickness_id ? parseInt(line.thickness_id, 10) : null,
            surface_finish: line.surface_finish || null,

            process_steps: Array.isArray(line.process_steps) ? line.process_steps.join(',') : (line.process_steps || ''),
            
            initial_est_laser_minutes: safeParseFloatMinutes(line.initial_est_laser_minutes),
            initial_est_tap_minutes: safeParseFloatMinutes(line.initial_est_tap_minutes),
            initial_est_brake_minutes: safeParseFloatMinutes(line.initial_est_brake_minutes),
            initial_est_blast_minutes: safeParseFloatMinutes(line.initial_est_blast_minutes),
            initial_est_finish_minutes: safeParseFloatMinutes(line.initial_est_finish_minutes),
            initial_est_fabrication_minutes: totalFabMinutes,
            initial_est_fab_tap_minutes: safeParseFloatMinutes(line.initial_est_fab_tap_minutes),
            initial_est_weld_minutes: safeParseFloatMinutes(line.initial_est_weld_minutes),
            initial_est_assembly_minutes: safeParseFloatMinutes(line.initial_est_assembly_minutes),
            
            // --- NEW: Pass Beam On Minutes ---
            // This is critical for prioritizing Real Historical Data over Initial Estimates
            beam_on_minutes: safeParseFloatMinutes(line.beam_on_minutes),

            material_markup: parseFloat(currentSetup.material_markup) / 100,
            coating_markup: (currentSetup.coating_markup === '' || currentSetup.coating_markup == null || isNaN(parseFloat(currentSetup.coating_markup)))
                ? null : parseFloat(currentSetup.coating_markup) / 100,
            laser_rate: parseFloat(currentSetup.laser_rate),
            tap_rate: parseFloat(currentSetup.tap_rate),
            brake_rate: parseFloat(currentSetup.brake_rate),
            fabrication_rate: parseFloat(currentSetup.fabrication_rate),
            blast_rate: parseFloat(currentSetup.blast_rate),
            finish_rate: parseFloat(currentSetup.finish_rate),

            material_category: line.material_category,

            // Assembly-ops exclusion: when this line is an assembly's PRIMARY,
            // the server skips the at/after-join stations covered by
            // assembly_processes — they price on the per-set ops line instead.
            // Only when the quote actually carries an ops line for this assembly
            // (opsAsmIds) — a LEGACY quote's assembly group has no ops line, and
            // excluding its primary's weld/coating there would silently drop
            // that value from the quote with nothing carrying it.
            source_assembly_id: (opsAsmIds && line._source_assembly_id && opsAsmIds.has(line._source_assembly_id))
                ? line._source_assembly_id : null,
        };
    };

    // Purchased-component line → the consumable calculator (F36): the same
    // endpoint with `consumable_id` instead of `part_id`; the server prices it
    // from the preferred-vendor cost (else the highest vendor cost) × (1 +
    // material markup) and returns the PartCostResponse shape (+ book_price,
    // warnings). size_length/size_width are required by the request model.
    const buildConsumablePayload = (line, currentSetup) => ({
        part_id: null,
        consumable_id: line.consumable_id,
        size_length: 0,
        size_width: 0,
        is_customer_supplied_material: false,
        process_steps: '',
        material_markup: (parseFloat(currentSetup.material_markup) || 0) / 100,
        coating_markup: (currentSetup.coating_markup === '' || currentSetup.coating_markup == null || isNaN(parseFloat(currentSetup.coating_markup)))
            ? null : parseFloat(currentSetup.coating_markup) / 100,
        laser_rate: parseFloat(currentSetup.laser_rate) || 0,
        tap_rate: parseFloat(currentSetup.tap_rate) || 0,
        brake_rate: parseFloat(currentSetup.brake_rate) || 0,
        fabrication_rate: parseFloat(currentSetup.fabrication_rate) || 0,
        blast_rate: parseFloat(currentSetup.blast_rate) || 0,
        finish_rate: parseFloat(currentSetup.finish_rate) || 0,
    });

    // What a consumable calculator result changes on the line besides the
    // breakdown: the resolved vendor cost, the catalog book price it returned,
    // and the below-cost flag (F23). Falls back to what the line already had.
    const consumableExtrasFromCalc = (line, result) => {
        const cost = num(result && result.total_cost, NaN);
        const book = result && result.book_price != null ? num(result.book_price, NaN) : num(line._book_price, NaN);
        const bookOk = Number.isFinite(book) && book > 0;
        return {
            _cost: Number.isFinite(cost) ? cost : num(line._cost, 0),
            _cost_resolved: Number.isFinite(cost) || line._cost_resolved === true,
            _book_price: bookOk ? book : 0,
            _below_cost: bookOk && Number.isFinite(cost) && book < cost - 0.005,
        };
    };

    // A STANDALONE purchased component (consumable_id, no source_assembly_id) has
    // no assembly component list to read a vendor cost from. Price it through the
    // same server-side consumable calculator that Calc and Recalculate use, so the
    // cost, book price and below-cost flag all come from one definition. Lines that
    // already carry a stored breakdown are left untouched; a calculator failure
    // leaves the line "cost unresolved" rather than reporting a false $0.
    const hydrateStandaloneComponents = async (lines, currentSetup) => {
        const targets = (lines || []).filter(l =>
            l.is_consumable_view && !l._source_assembly_id && l.consumable_id && !l.cost_breakdown);
        if (targets.length === 0) return lines;
        const results = new Map();
        await Promise.all(targets.map(async (l) => {
            try {
                const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(buildConsumablePayload(l, currentSetup)),
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                results.set(l.id, await res.json());
            } catch (e) {
                console.warn('standalone component: consumable calculator unavailable', l.product_id, e);
            }
        }));
        if (results.size === 0) return lines;
        const headerF = headerFractions(currentSetup);
        return lines.map(l => {
            const result = results.get(l.id);
            if (!result) return l;
            const next = { ...l, ...consumableExtrasFromCalc(l, result), cost_breakdown: result, stale: null };
            if (!next.price_source) {
                const inf = inferPriceSource(next, next.cost_breakdown, headerF);
                next.price_source = inf.price_source;
                next.price_basis = inf.price_basis;
                next._price_source_inferred = true;
            }
            next.pricing_mode = next.price_source === 'book' ? 'book'
                : next.price_source === 'markup' ? 'markup' : 'manual';
            return next;
        });
    };

    // Assemblies that have an ops line among the given lines — gates the
    // primary-line exclusion (see buildLinePayload).
    const opsAsmIdsOf = (lines) => new Set(
        (lines || []).filter(l => l.is_assembly_ops && l._source_assembly_id)
                     .map(l => l._source_assembly_id)
    );

    // Est fields the assembly's ops line carries (used to keep the primary's
    // time-forecast fields blanked so minutes aren't double-counted).
    const coveredEstFieldsForAsm = (lines, asmId) => {
        const ops = (lines || []).find(l => l.is_assembly_ops && l._source_assembly_id === asmId);
        if (!ops) return [];
        return Object.keys(ops).filter(k => k.startsWith('initial_est_') && parseFloat(ops[k]) > 0);
    };

    // Every est-minutes field an ASSEMBLY OPS line can carry. Rebuilt in full on
    // every reprice so a station REMOVED from assembly_processes clears instead of
    // lingering.
    const OPS_EST_FIELDS = [
        'initial_est_laser_minutes', 'initial_est_tap_minutes', 'initial_est_brake_minutes',
        'initial_est_blast_minutes', 'initial_est_finish_minutes', 'initial_est_cutting_minutes',
        'initial_est_drilling_minutes', 'initial_est_milling_minutes', 'initial_est_lathe_minutes',
        'initial_est_weld_minutes', 'initial_est_assembly_minutes', 'initial_est_fab_tap_minutes',
        'initial_est_fabrication_minutes',
    ];

    const opsEstFieldFor = (step) =>
        (step === 'Tapping') ? 'initial_est_fab_tap_minutes'
        : (step === 'Welding') ? 'initial_est_weld_minutes'
        : `initial_est_${String(step).toLowerCase()}_minutes`;

    // The ops line's `initial_est_*` minutes are what the Production Time Forecast
    // reads, and they were only ever built ONCE at group creation. A reprice merged
    // `price` + `cost_breakdown` and left them stale, so after editing an assembly's
    // welding from 180 to 240 the same screen showed "240 min" on the ops row while
    // the forecast card still reported 3.00 hrs. The fresh minutes are already in
    // the response — breakdown.operations[].minutes — so no extra request is needed.
    const opsEstFromBreakdown = (ops) => {
        const fresh = {};
        OPS_EST_FIELDS.forEach(f => { fresh[f] = 0; });
        (ops?.breakdown?.operations || []).forEach(op => {
            const mins = parseFloat(op?.minutes) || 0;
            if (!mins || !op?.station) return;
            const field = opsEstFieldFor(op.station);
            fresh[field] = (parseFloat(fresh[field]) || 0) + mins;
        });
        return fresh;
    };

    // Price the per-set "Assembly operations" line from assembly_processes.
    const calcAssemblyOps = async (assemblyId, currentSetup) => {
        const res = await fetch(`${API_BASE}/quotes/calculate-assembly-ops`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                assembly_id: assemblyId,
                material_markup: parseFloat(currentSetup.material_markup) / 100,
                coating_markup: (currentSetup.coating_markup === '' || currentSetup.coating_markup == null || isNaN(parseFloat(currentSetup.coating_markup)))
                    ? null : parseFloat(currentSetup.coating_markup) / 100,
                laser_rate: parseFloat(currentSetup.laser_rate),
                tap_rate: parseFloat(currentSetup.tap_rate),
                brake_rate: parseFloat(currentSetup.brake_rate),
                fabrication_rate: parseFloat(currentSetup.fabrication_rate),
                blast_rate: parseFloat(currentSetup.blast_rate),
                finish_rate: parseFloat(currentSetup.finish_rate),
            }),
        });
        if (!res.ok) throw new Error(`Assembly ops calculation failed: ${await res.text()}`);
        return res.json();
    };

    // Per-line Calc (F05): replaces the breakdown AND re-derives the price from
    // the line's own basis — a 'calculated' line follows the new calculated
    // price, a target-margin / cost+markup line keeps its margin on the new
    // cost, a manual / book line keeps its price (the drift badge shows the gap
    // and offers Apply). The ops line's ↺ follows the same rule (ops lines
    // default to 'calculated'). `keepPrice` is the "Refresh cost (keep price)"
    // action (F09): breakdown only, whatever the basis.
    const handleCalculateCost = async (index, { keepPrice = false } = {}) => {
        const line = advancedQuoteLines[index];
        if (!line) return;
        const lineId = line.id;
        const label = line.product_id || line.description || `line ${index + 1}`;

        // Merge the fresh breakdown (and any extra fields) into the line by id —
        // the operator may have edited other lines while the request ran.
        const commit = (fresh, extra = {}) => {
            const merged = { ...line, ...extra, cost_breakdown: fresh };
            const patch = { ...extra, cost_breakdown: fresh, show_breakdown: true, stale: null };
            let note;
            if (keepPrice) {
                note = `${label}: cost breakdown refreshed; price ${fmtMoney(num(line.price, 0))} kept (${basisLabel(line)}).`;
            } else {
                const d = deriveLinePrice(merged, fresh, headerFrac);
                if (d.changed && d.price != null) {
                    patch.price = d.price.toFixed(2);
                    if (d.basis != null && line.price_basis == null) patch.price_basis = d.basis;
                    note = `${label}: price ${fmtMoney(num(line.price, 0))} → ${fmtMoney(d.price)} — ${basisLabel(line)} (${d.reason}).`;
                } else {
                    note = `${label}: cost breakdown refreshed — ${d.reason}.`;
                }
            }
            setAdvancedQuoteLines(prev => prev.map(l => (l.id === lineId ? { ...l, ...patch } : l)));
            setNotification({ message: note, type: 'info' });
        };

        // Assembly-ops lines price from assembly_processes, not part data.
        if (line.is_assembly_ops) {
            try {
                const ops = await calcAssemblyOps(line._source_assembly_id, quoteSetup);
                commit(ops, opsEstFromBreakdown(ops));
            } catch (err) {
                setNotification({ message: err.message, type: 'danger' });
            }
            return;
        }

        // Purchased-component lines price from the consumable's vendor cost (F36).
        if (line.is_consumable_view) {
            if (!line.consumable_id) {
                setNotification({ message: `${label}: this component line has no consumable_id, so it cannot be costed server-side.`, type: 'warning' });
                return;
            }
            try {
                const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(buildConsumablePayload(line, quoteSetup)),
                });
                if (!res.ok) throw new Error(`Component cost calculation failed: ${await res.text()}`);
                const result = await res.json();
                commit(result, consumableExtrasFromCalc(line, result));
            } catch (err) {
                setNotification({ message: err.message, type: 'danger' });
            }
            return;
        }

        // --- VALIDATION UPDATE: Allow missing width for Shape Stock ---
        const isShapeStock = line.material_category === 'Shape Stock';

        if (!line.size_length || (!isShapeStock && !line.size_width) || (!line.is_customer_supplied_material && !line.material_id && !line.manual_material_price)) {
            setNotification({ message: `Provide Length${isShapeStock ? '' : ', Width'}, and Material/Price to calculate.`, type: 'warning' });
            return;
        }

        try {
            const payload = buildLinePayload(line, quoteSetup, opsAsmIdsOf(advancedQuoteLines));
            const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error(`Cost calculation failed: ${await res.text()}`);

            const result = await res.json();
            commit(result);
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        }
    };

    // Recalculate All Prices (F01 #3): gather a fresh breakdown for EVERY line
    // (ops rows first — order is load-bearing, see below — then parts and
    // purchased components in parallel), re-derive each price from its OWN
    // basis, and park the result in a preview. Nothing on the quote changes
    // until the operator clicks Apply in the preview modal.
    const handleRecalculateAll = async () => {
        setLoading(true);
        const failures = [];
        try {
            const updatedLines = [...advancedQuoteLines];

            // ORDER IS LOAD-BEARING: reprice the ASSEMBLY OPS lines FIRST, in
            // place, before the part pass reads `coveredEstFieldsForAsm`. The
            // part pass runs its lines through Promise.all against this same
            // array, so if the ops lines were repriced inside that pass every
            // part line would read the PRE-reprice covered set. A station added
            // to assembly_processes after the quote was built would then be
            // missing from that set, the primary's minutes would be refilled by
            // applyHistoricalData, and the forecast would double-count it while
            // the server still excluded that station from the primary's PRICE.
            for (let i = 0; i < updatedLines.length; i++) {
                const l = updatedLines[i];
                if (!l.is_assembly_ops || !l._source_assembly_id) continue;
                try {
                    const ops = await calcAssemblyOps(l._source_assembly_id, quoteSetup);
                    updatedLines[i] = { ...l, ...opsEstFromBreakdown(ops), cost_breakdown: ops, stale: null };
                } catch (e) {
                    console.warn('Assembly ops recalc failed', e);
                    failures.push(`${l.product_id || 'ASSEMBLY OPS'} (${l._source_assembly_name || l._source_assembly_id}): ${e.message}`);
                }
            }

            const promises = updatedLines.map(async (line) => {

                // Purchased components (F36): costed server-side from the
                // consumable's vendor cost. When the calculator is unavailable the
                // line keeps the vendor cost it already carries from the assembly.
                if (line.is_consumable_view) {
                    if (!line.consumable_id) return { ...line, _recalc_note: 'no consumable_id — vendor cost kept' };
                    try {
                        const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(buildConsumablePayload(line, quoteSetup)),
                        });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const result = await res.json();
                        return { ...line, ...consumableExtrasFromCalc(line, result), cost_breakdown: result, stale: null };
                    } catch (e) {
                        failures.push(`${line.product_id || 'component'}: consumable calculator ${e.message}`);
                        return { ...line, _recalc_note: 'consumable calculator unavailable — vendor cost from the assembly kept' };
                    }
                }

                // Per-set Assembly operations line: already repriced above, in
                // order, so the part lines below see the FRESH covered set.
                if (line.is_assembly_ops) return line;

                let enrichedLine = { ...line };

                if (line.part_id) {
                    try {
                        // 1. RE-FETCH FRESH PART DATA (Single Source of Truth)
                        const partRes = await fetch(`${API_BASE}/parts/${line.part_id}`);
                        if (partRes.ok) {
                            const partData = await partRes.json();
                            
                            // --- UPDATED: Apply Historical Data Priority Logic ---
                            // This ensures that if the shop has logged actual times since the quote was made,
                            // the recalculation uses those new actuals for ALL stations.
                            const freshTimeEstimates = applyHistoricalData(partData);
                            enrichedLine = { ...enrichedLine, ...freshTimeEstimates };

                            // B. Update BEAM ON TIME (for Gas/Consumables only)
                            if (partData.beam_on_minutes !== undefined && partData.beam_on_minutes !== null) {
                                enrichedLine.beam_on_minutes = partData.beam_on_minutes;
                            }

                            // C. Update Material Characteristics (Critical for Tech Specs)
                            if (partData.material_type) enrichedLine.material_type = partData.material_type;
                            if (partData.thickness_id) enrichedLine.thickness_id = partData.thickness_id;
                            if (partData.surface_finish) enrichedLine.surface_finish = partData.surface_finish;

                            // D. Fill Material ID if missing
                            if (!enrichedLine.material_id && partData.material_id) {
                                enrichedLine.material_id = partData.material_id;
                            }
                        }
                    } catch (e) {
                        console.warn("Could not refresh part data", e);
                    }
                }

                // Keep the primary's ops-covered est fields BLANK after the
                // fresh-part spread — the ops line carries those minutes, and
                // refilled fields would double-count the Production Time
                // Forecast and re-enter part_attributes on save.
                const _coveredNow = line._source_assembly_id
                    ? coveredEstFieldsForAsm(updatedLines, line._source_assembly_id) : [];
                if (_coveredNow.length > 0 && line.part_id) {
                    _coveredNow.forEach(f => { enrichedLine[f] = ''; });
                }

                // 2. Build Payload using the ENRICHED line data
                const payload = buildLinePayload(enrichedLine, quoteSetup, opsAsmIdsOf(updatedLines));

                try {
                    const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });

                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const result = await res.json();

                    // 3. Return the merged object
                    // This keeps 'current_db_price' from 'line' (via 'enrichedLine')
                    // and updates 'cost_breakdown' and the refreshed time/beam data.
                    // The PRICE is re-derived below, from the line's own basis.
                    return {
                        ...enrichedLine,
                        cost_breakdown: result,
                        show_breakdown: true,
                        stale: null,
                    };
                } catch (err) {
                    failures.push(`${line.product_id || line.description || 'line'}: ${err.message}`);
                    return { ...line, _recalc_note: 'calculation failed — breakdown and price kept' };
                }
            });

            const refreshed = await Promise.all(promises);

            // Re-derive every price from ITS OWN basis (F01): calculated lines
            // follow the new calculated price, target-margin lines keep their
            // margin on the new cost, cost+markup components keep their markup,
            // manual / book lines keep their price (breakdown only).
            const newLines = refreshed.map(l => {
                const d = deriveLinePrice(l, l.cost_breakdown, headerFrac);
                const out = { ...l };
                if (d.changed && d.price != null) {
                    out.price = d.price.toFixed(2);
                    if (d.basis != null && out.price_basis == null) out.price_basis = d.basis;
                }
                out._recalc_note = [l._recalc_note, d.reason].filter(Boolean).join(' · ');
                return out;
            });

            const overridden = Object.keys(assemblyPriceOverrides || {})
                .filter(k => assemblyPriceOverrides[k] !== '' && assemblyPriceOverrides[k] != null);
            const overrideNote = overridden.length > 0
                ? `Assemblies with a per-set override (${overridden.map(k => (assemblySummaries[String(k)] && assemblySummaries[String(k)].name) || `#${k}`).join(', ')}) bill override × sets; their line prices are informational until the override is cleared.`
                : null;
            setRecalcPreview({
                rows: recalcPreviewRows(advancedQuoteLines, newLines),
                oldTotal: computeSubtotal(advancedQuoteLines, assemblyPriceOverrides, assemblies),
                newTotal: computeSubtotal(newLines, assemblyPriceOverrides, assemblies),
                failures,
                overrideNote,
                proposedLines: newLines,
            });
        } catch (err) {
            setNotification({ message: `Batch recalculation encountered errors: ${err.message}`, type: "warning" });
        } finally {
            setLoading(false);
        }
    };

    // Apply the Recalculate-All preview: the proposed lines become the quote's
    // lines (still unsaved), and the Financial Summary is flagged as a live
    // recalculation until the next Save + reload.
    const applyRecalcPreview = () => {
        if (!recalcPreview) return;
        const changed = recalcPreview.rows.filter(r => r.changed).length;
        setAdvancedQuoteLines(recalcPreview.proposedLines.map(l => {
            const out = { ...l };
            delete out._recalc_note;
            return out;
        }));
        setHydrationInfo({ ran: true, at: new Date(), reason: 'recalculate' });
        setRecalcPreview(null);
        setNotification({
            message: `Recalculated ${recalcPreview.rows.length} line(s): ${changed} price(s) re-derived from their basis, every breakdown refreshed. Not saved until Save Changes.`,
            type: 'success',
        });
    };

    // Silent cost calculator for dashboard initialization (F41): runs when a
    // loaded quote has part lines WITHOUT a stored breakdown. It writes
    // breakdowns into React state only — never the price — and flags the
    // Financial Summary as "recalculated live (not saved)".
    const hydrateCostsForDashboard = async (lines, setup, expandBreakdowns = false) => {
        const setupFrac = headerFractions(setup);
        // Only the lines WITHOUT a stored breakdown are costed live (skip
        // consumables, ops lines and empty lines). Lines that carry a stored
        // snapshot keep it — together with the server's `stale` verdict — so
        // "as stored" means as stored; Calc / Refresh cost re-run them on demand.
        const calcQueue = lines.map((line, index) => ({ line, index }))
            .filter(({ line }) => !line.is_consumable_view && !line.is_assembly_ops && (line.part_id || line.product_id) && !line.cost_breakdown);

        if (calcQueue.length === 0) return;

        // Run calculations in parallel
        const results = await Promise.all(calcQueue.map(async ({ line, index }) => {
            const payload = buildLinePayload(line, setup, opsAsmIdsOf(lines));
            try {
                const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                if (res.ok) {
                    const costData = await res.json();
                    // Return index and the specific cost data
                    return { index, cost_breakdown: costData };
                }
            } catch (e) {
                console.warn("Silent cost calc failed for line", index);
            }
            return null;
        }));

        // Valid results only
        const updates = results.filter(r => r !== null);
        
        if (updates.length > 0) {
            setAdvancedQuoteLines(prevLines => {
                const newLines = [...prevLines];
                updates.forEach(({ index, cost_breakdown }) => {
                    // Only update if the line still exists and hasn't changed drastically
                    if (newLines[index]) {
                        const prev = newLines[index];
                        const next = { ...prev, cost_breakdown, show_breakdown: expandBreakdowns, stale: null };
                        // A basis inferred without a breakdown can now be re-checked
                        // against calculated_price / total_cost (F01).
                        if (prev._price_source_inferred || !prev.price_source) {
                            const inf = inferPriceSource(next, cost_breakdown, setupFrac);
                            next.price_source = inf.price_source;
                            next.price_basis = inf.price_basis;
                            next._price_source_inferred = true;
                        }
                        newLines[index] = next;
                    }
                });
                return newLines;
            });
            setHydrationInfo({ ran: true, at: new Date() });
        }
    };

    const safeParseFloatMinutes = (val) => {
        if (!val || String(val).trim() === "") return 0;
        const parsed = parseFloat(val);
        return (isNaN(parsed) || parsed < 0) ? 0 : parsed;
    };

    const handleEstimateCompletion = async () => {
        const lines = quoteMode === 'Quick' ? quickQuoteLines : advancedQuoteLines;
        const estimationLines = lines.map(line => ({
            part_id: line.part_id || null,
            quantity: parseInt(line.quantity, 10) || 1,
            process_steps: Array.isArray(line.process_steps)
                ? line.process_steps.join(',')
                : (line.process_steps || ''),
            initial_est_laser_minutes: safeParseFloatMinutes(line.initial_est_laser_minutes),
            initial_est_tap_minutes: safeParseFloatMinutes(line.initial_est_tap_minutes),
            initial_est_brake_minutes: safeParseFloatMinutes(line.initial_est_brake_minutes),
            initial_est_fabrication_minutes: safeParseFloatMinutes(line.initial_est_fabrication_minutes),
            initial_est_blast_minutes: safeParseFloatMinutes(line.initial_est_blast_minutes),
            initial_est_finish_minutes: safeParseFloatMinutes(line.initial_est_finish_minutes),
            initial_est_cutting_minutes: safeParseFloatMinutes(line.initial_est_cutting_minutes),
            initial_est_drilling_minutes: safeParseFloatMinutes(line.initial_est_drilling_minutes),
            initial_est_milling_minutes: safeParseFloatMinutes(line.initial_est_milling_minutes),
            initial_est_lathe_minutes: safeParseFloatMinutes(line.initial_est_lathe_minutes),
            initial_est_weld_minutes: safeParseFloatMinutes(line.initial_est_weld_minutes),
            initial_est_assembly_minutes: safeParseFloatMinutes(line.initial_est_assembly_minutes),
            initial_est_fab_tap_minutes: safeParseFloatMinutes(line.initial_est_fab_tap_minutes),
        })).filter(l =>
            l.part_id ||
            l.process_steps ||
            [
                l.initial_est_laser_minutes, l.initial_est_tap_minutes,
                l.initial_est_brake_minutes, l.initial_est_fabrication_minutes,
                l.initial_est_blast_minutes, l.initial_est_finish_minutes,
                l.initial_est_cutting_minutes, l.initial_est_drilling_minutes,
                l.initial_est_milling_minutes, l.initial_est_lathe_minutes,
                l.initial_est_weld_minutes, l.initial_est_assembly_minutes,
                l.initial_est_fab_tap_minutes,
            ].some(v => v > 0)
        );

        if (estimationLines.length === 0) {
            setNotification({ message: 'No line items with time estimates to schedule.', type: 'warning' });
            return;
        }

        setEstimateLoading(true);
        setScheduleEstimate(null);
        try {
            const res = await fetch(`${API_BASE}/quotes/estimate-completion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lines: estimationLines, priority_level: 2 }),
            });
            if (!res.ok) {
                throw new Error(await apiErrorMessage(res));
            }
            const data = await res.json();
            setScheduleEstimate(data);
        } catch (err) {
            setNotification({ message: `Schedule estimation failed: ${err.message}`, type: 'danger' });
        } finally {
            setEstimateLoading(false);
        }
    };

    const handleSaveQuote = async () => {
        if (!selectedCustomerId) {
            setNotification({ message: "Select a customer.", type: 'warning' });
            return;
        }
        const isUpdating = selectedQuote && selectedQuote.quote_id;
        
        let payloadLines;
        if (quoteMode === 'Quick') {
            payloadLines = quickQuoteLines.map(line => {
                const payload = {
                    product_id: line.product_id || null,
                    description: line.material ? `${line.description} (Material: ${line.material})` : line.description,
                    quantity: parseInt(line.quantity, 10) || 1, 
                    price: parseFloat(line.price) || 0,
                    is_quick_quote_item: true, 
                    part_id: line.part_id || null,
                };
                // Only include the ID if we are updating an existing quote.
                // The backend model 'QuoteLineCreate' (used for new quotes) does not accept an ID field.
                if (isUpdating) {
                    payload.id = line.id;
                }
                return payload;
            });
        } else {
            payloadLines = advancedQuoteLines.map(line => {
                const payload = {
                    part_id: line.part_id || null,
                    consumable_id: line.consumable_id || null,
                    source_assembly_id: line._source_assembly_id || null,
                    is_assembly_ops: line.is_assembly_ops === true,
                    product_id: line.product_id || null,
                    description: line.description || '', 
                    quantity: parseInt(line.quantity, 10) || 1,
                    price: parseFloat(line.price) || 0, 
                    is_quick_quote_item: line.is_quick_quote_item === true,
                    material_id: line.material_id ? parseInt(line.material_id, 10) : null,
                    material_type: line.material_type || null,
                    thickness_id: line.thickness_id || null,
                    surface_finish: line.surface_finish || null,
                    // F29: persisted to quote_parts.is_customer_supplied_material
                    is_customer_supplied_material: !!line.is_customer_supplied_material,
                    // F01: price basis travels with the price
                    price_source: normalizePriceSource(effectivePriceSource(line)),
                    price_basis: line.price_basis != null && Number.isFinite(Number(line.price_basis)) ? Number(line.price_basis) : null,
                    size_length: line.size_length ? parseFloat(line.size_length) : 0.0,
                    size_width: line.size_width ? parseFloat(line.size_width) : 0.0,
                    process_steps: (line.process_steps || []).join(','),
                    initial_est_laser_minutes: safeParseFloatMinutes(line.initial_est_laser_minutes),
                    initial_est_tap_minutes: safeParseFloatMinutes(line.initial_est_tap_minutes),
                    initial_est_brake_minutes: safeParseFloatMinutes(line.initial_est_brake_minutes),
                    initial_est_blast_minutes: safeParseFloatMinutes(line.initial_est_blast_minutes),
                    initial_est_finish_minutes: safeParseFloatMinutes(line.initial_est_finish_minutes),
                    initial_est_cutting_minutes: safeParseFloatMinutes(line.initial_est_cutting_minutes),
                    initial_est_drilling_minutes: safeParseFloatMinutes(line.initial_est_drilling_minutes),
                    initial_est_milling_minutes: safeParseFloatMinutes(line.initial_est_milling_minutes),
                    initial_est_lathe_minutes: safeParseFloatMinutes(line.initial_est_lathe_minutes),
                    initial_est_weld_minutes: safeParseFloatMinutes(line.initial_est_weld_minutes),
                    initial_est_assembly_minutes: safeParseFloatMinutes(line.initial_est_assembly_minutes),
                    initial_est_fab_tap_minutes: safeParseFloatMinutes(line.initial_est_fab_tap_minutes),
                    cost_breakdown: line.cost_breakdown || null,
                };
                if (isUpdating) {
                    payload.id = line.id;
                }
                return payload;
            });
        }

        // Required: Fulfillment must be picked before saving a quote so the
        // choice locks in and inherits to the SO at conversion. DB has a
        // NOT NULL DEFAULT 'shipping' as backstop, but the UI requires an
        // explicit selection so operators don't silently default for pickup
        // customers.
        if (!fulfillmentType) {
            setNotification({
                message: 'Fulfillment is required — pick Carrier Shipment, Customer Pickup, or We Deliver before saving.',
                type: 'warning',
            });
            return;
        }

        const payload = {
            customer_id: parseInt(selectedCustomerId, 10),
            contact_id: selectedContactId ? parseInt(selectedContactId, 10) : null,
            customer_po: selectedQuote ? selectedQuote.customer_po : null, // Handle null safe access if new quote
            customer_so: selectedQuote ? selectedQuote.customer_so : null,
            notes: notes,
            lines: payloadLines,
            status: isUpdating ? selectedQuote.status : 'Draft',
            // F01: quotes.target_margin (fraction) — the header basis for target-margin lines
            target_margin: Number.isFinite(parseFloat(quoteSetup.target_margin)) ? parseFloat(quoteSetup.target_margin) / 100 : null,
            material_markup: parseFloat(quoteSetup.material_markup) / 100,
            coating_markup: (quoteSetup.coating_markup === '' || quoteSetup.coating_markup == null || isNaN(parseFloat(quoteSetup.coating_markup)))
                ? null : parseFloat(quoteSetup.coating_markup) / 100,
            laser_rate: parseFloat(quoteSetup.laser_rate),
            tap_rate: parseFloat(quoteSetup.tap_rate),
            brake_rate: parseFloat(quoteSetup.brake_rate),
            fabrication_rate: parseFloat(quoteSetup.fabrication_rate),
            blast_rate: parseFloat(quoteSetup.blast_rate),
            finish_rate: parseFloat(quoteSetup.finish_rate),
            shipping_amount: Number(shipping) || 0,
            additional_fees: Number(additionalFees) || 0,
            tax_amount: Number(tax) || 0,
            discount_amount: Number(discount) || 0,
            lead_time: completionMode === 'lead_time' ? leadTime : null,
            promised_date: completionMode === 'date' ? promisedDate : null,
            assembly_price_overrides: Object.keys(assemblyPriceOverrides).length > 0 ? assemblyPriceOverrides : null,
            fulfillment_type: fulfillmentType,
            shipping_address_id: shippingAddressId ? parseInt(shippingAddressId, 10) : null
        };
        // T&M keys: none at all for a fixed quote that stays fixed (the payload is unchanged).
        const tmSave = tmQuoteSaveFields({
            mode: quotePricingMode, storedMode: selectedQuote ? selectedQuote.pricing_mode : null,
            estimate: tmEstimate, nte: tmNte, dirty: tmDirty,
        });
        if (tmSave.errors.length > 0) {
            setNotification({ message: tmSave.errors.join(' · '), type: 'warning' });
            return;
        }
        Object.assign(payload, tmSave.fields);
    
        const url = isUpdating ? `${API_BASE}/quotes/${selectedQuote.quote_id}` : `${API_BASE}/quotes`;
        const method = isUpdating ? 'PATCH' : 'POST';
    
        setLoading(true);
        try {
            const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to save quote'));
            const savedQuote = await res.json();
            // Server-side drift warnings (price_source says 'calculated' but the
            // stored price differs from the breakdown, etc.) — non-blocking.
            const warnings = Array.isArray(savedQuote.warnings) ? savedQuote.warnings.filter(Boolean) : [];
            if (warnings.length > 0) {
                setNotification({ message: `Quote #${savedQuote.quote_id} saved with ${warnings.length} warning(s): ${warnings.join(' · ')}`, type: 'warning' });
            } else {
                setNotification({ message: `Quote #${savedQuote.quote_id} saved!`, type: 'success' });
            }
            setIsCreating(false);
            // Hydrate from the save response itself — it is the full quote with
            // per-line `stale` + `warnings`, which the list endpoint never carries.
            // If the response is not a full quote, fall back to GET /quotes/{id}.
            const full = Array.isArray(savedQuote.lines)
                ? savedQuote
                : ((await fetchQuoteDetail(savedQuote.quote_id)) || savedQuote);
            selectSeqRef.current += 1; // supersede any in-flight selection fetch
            tmToggleSnapshot.current = null; // saved: the stored mode is now what was on screen
            setSelectedQuote(full);
            // Sidebar list refresh in the background (totals / status / updated_at).
            fetch(`${API_BASE}/quotes`)
                .then(r => (r.ok ? r.json() : null))
                .then(list => { if (Array.isArray(list)) setQuotes(list); })
                .catch(e => console.warn('Quote list refresh after save failed', e));
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };
    
    const handleApproveQuote = (quoteId) => {
        // The pricing toggle changed but was not saved: approving would act on the STORED mode.
        if (selectedQuote && quotePricingMode !== (isTm(selectedQuote) ? 'tm' : 'fixed')) {
            setNotification({ message: 'Save the quote first: its pricing (Fixed / Time & Materials) changed.', type: 'warning' });
            return;
        }
        // F42: the server refuses approval when every sell rate is 0; say so
        // here, before the confirm, for any zero rate.
        const zeros = quoteMode === 'Advanced' ? zeroSellRates(quoteSetup) : [];
        if (zeros.length > 0) {
            setNotification({ message: `Cannot approve: sell rate is $0/hr for ${zeros.join(', ')}. Set the rates in Quote Setup, Recalculate, Save, then approve.`, type: 'danger' });
            return;
        }
        setConfirmation({
            show: true,
            // Decision 6 (wave 2): approval writes PRICES only — no part attribute is pushed.
            // A T&M quote is ACCEPTED: no price is written at all.
            message: isTm(selectedQuote) ? TM_ACCEPT_CONFIRM : "Approve this quote? Approving writes each line's price to its part (book price) and the per-set price to each assembly's System Price. Part attributes (routing, minutes, material, sizes, description) are not changed — edit those on the Part page. Temporary parts/customers are finalized.",
            onConfirm: () => proceedWithApproval(quoteId)
        });
    };

    const [poModal, setPoModal] = useState({ show: false, quoteId: null, poNumber: '', soNumber: '', file: null, pin: '', yieldPolicyId: '' });

    const closePoModal = () => setPoModal({ show: false, quoteId: null, poNumber: '', soNumber: '', file: null, pin: '', yieldPolicyId: '' });

    const handleConvertToSalesOrderClick = (quoteId) => {
        const existingPO = selectedQuote.customer_po || '';
        const existingSO = selectedQuote.customer_so || '';
        setPoModal({
            show: true,
            quoteId: quoteId,
            poNumber: existingPO,
            soNumber: existingSO,
            file: null,
            pin: '',
            // A T&M order bills what ships: policy 4 (Ship Any Qty, Bill Shipped) is required.
            yieldPolicyId: isTm(selectedQuote) ? TM_YIELD_POLICY_ID : ''
        });
    };

    const submitSalesOrderConversion = async () => {
        if (!poModal.quoteId) return;
        if (!poModal.yieldPolicyId) {
            setNotification({ message: 'Yield/Billing Policy is required.', type: 'danger' });
            return;
        }
        if (poModal.file && !poModal.pin.trim()) {
            setNotification({ message: 'PIN is required to attach a PDF.', type: 'danger' });
            return;
        }

        const quoteIdForReload = poModal.quoteId;
        const poForUpload = poModal.poNumber;
        const soForUpload = poModal.soNumber;
        const fileForUpload = poModal.file;
        const pinForUpload = poModal.pin;
        const yieldPolicyIdForUpload = parseInt(poModal.yieldPolicyId, 10);

        setLoading(true);
        try {
            const payload = { customer_po: poForUpload, customer_so: soForUpload, yield_policy_id: yieldPolicyIdForUpload };

            const res = await fetch(`${API_BASE}/quotes/${quoteIdForReload}/convert-to-sales-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errText = await res.text();
                let errorMsg = 'Conversion failed.';
                try { errorMsg = detailToString(JSON.parse(errText).detail) || errorMsg; } catch (e) {}
                throw new Error(errorMsg);
            }

            const result = await res.json();
            const newSalesOrderId = result.sales_order_id;

            let uploadWarning = null;
            if (fileForUpload && newSalesOrderId) {
                const displayName = poForUpload
                    ? `Customer PO ${poForUpload}.pdf`
                    : fileForUpload.name;
                const fd = new FormData();
                fd.append('file', fileForUpload);
                fd.append('pin', pinForUpload);
                fd.append('display_name', displayName);
                fd.append('doc_type', 'Customer PO');
                try {
                    const upRes = await fetch(`${API_BASE}/sales_orders/${newSalesOrderId}/documents`, {
                        method: 'POST',
                        body: fd
                    });
                    if (!upRes.ok) {
                        const upErrTxt = await upRes.text();
                        try { uploadWarning = detailToString(JSON.parse(upErrTxt).detail) || 'Upload failed.'; }
                        catch (e) { uploadWarning = 'Upload failed.'; }
                    }
                } catch (e) {
                    uploadWarning = 'Network error while uploading PDF.';
                }
            }

            if (uploadWarning) {
                setNotification({
                    message: `Sales Order ${result.order_number} created, but PDF upload failed: ${uploadWarning}. Upload it from the Sales Order page.`,
                    type: 'warning'
                });
            } else {
                setNotification({ message: `Success! Created Sales Order ${result.order_number}.`, type: 'success' });
            }

            closePoModal();
            await loadInitialData();

            const reloadedRes = await fetch(`${API_BASE}/quotes/${quoteIdForReload}`);
            if (reloadedRes.ok) {
                const reloadedQuote = await reloadedRes.json();
                setSelectedQuote(reloadedQuote);
            }
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    const handleUnapproveQuote = (quoteId) => {
        setConfirmation({
            show: true, message: isTm(selectedQuote) ? TM_RESTORE_CONFIRM : "Restore this quote to Draft? This will allow editing but won't reverse any part price changes from the original approval.",
            onConfirm: () => proceedWithUnapproval(quoteId)
        });
    };

    const proceedWithUnapproval = async (quoteId) => {
        setConfirmation({ show: false, message: '', onConfirm: () => {} });
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/quotes/${quoteId}/unapprove`, { method: 'POST' });
            if (!res.ok) {
                const errText = await res.text();
                let errorMsg = 'Failed to restore to draft.';
                try { errorMsg = detailToString(JSON.parse(errText).detail) || errorMsg; } catch (e) {}
                throw new Error(errorMsg);
            }
            const updatedQuote = await res.json();
            setNotification({ message: `Quote #${quoteId} restored to Draft.`, type: 'success' });
            await loadInitialData();
            setSelectedQuote(updatedQuote);
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    const proceedWithApproval = async (quoteId) => {
        setConfirmation({ show: false, message: '', onConfirm: () => {} });
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/quotes/${quoteId}/approve`, { method: 'POST' });
            if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to approve'));
            setNotification({ message: `Quote #${quoteId} approved!`, type: 'success' });
            await loadInitialData();
            setSelectedQuote(null);
        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };
    
    // Kept but made secondary in the UI if Sales Order exists
    const handleConvertToInvoice = async (quoteId) => {
        setConfirmation({
            show: true, message: "Convert directly to Invoice? (Normally done via Sales Order)",
            onConfirm: async () => {
                setConfirmation({ show: false, message: '', onConfirm: () => {} });
                setLoading(true);
                try {
                    const res = await fetch(`${API_BASE}/quotes/${quoteId}/convert-to-invoice`, { method: 'POST' });
                    if (!res.ok) {
                        throw new Error(await apiErrorMessage(res, 'Conversion failed'));
                    }
                    const result = await res.json();
                    setNotification({ message: `Converted to Invoice #${result.invoice_number}.`, type: 'success' });
                    await loadInitialData();
                    const reloaded = await (await fetch(`${API_BASE}/quotes`)).json();
                    setSelectedQuote(reloaded.find(q => q.quote_id === quoteId) || null);
                } catch (err) {
                    setNotification({ message: err.message, type: 'danger' });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleDeleteQuote = (quoteId) => {
        setConfirmation({
            show: true,
            message: `Are you sure you want to delete Quote #${quoteId}? This cannot be undone.`,
            onConfirm: async () => {
                setConfirmation({ show: false, message: '', onConfirm: () => {} });
                setLoading(true);
                try {
                    const res = await fetch(`${API_BASE}/quotes/${quoteId}`, { method: 'DELETE' });
                    if (!res.ok) {
                        throw new Error(await apiErrorMessage(res, 'Delete failed'));
                    }
                    setQuotes(prev => prev.filter(q => q.quote_id !== quoteId));
                    setSelectedQuote(null);
                    setIsCreating(false);
                    setNotification({ message: `Quote #${quoteId} deleted.`, type: 'success' });
                } catch (err) {
                    setNotification({ message: err.message, type: 'danger' });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleDuplicateQuote = (quoteId) => {
        setConfirmation({
            show: true,
            message: `Duplicate Quote #${quoteId} as a new Draft quote?`,
            onConfirm: async () => {
                setConfirmation({ show: false, message: '', onConfirm: () => {} });
                setLoading(true);
                try {
                    const res = await fetch(`${API_BASE}/quotes/${quoteId}/duplicate`, { method: 'POST' });
                    if (!res.ok) {
                        throw new Error(await apiErrorMessage(res, 'Duplicate failed'));
                    }
                    const newQuote = await res.json();
                    setQuotes(prev => [newQuote, ...prev]);
                    setSelectedQuote(newQuote);
                    setIsCreating(false);
                    setNotification({ message: `Created Quote #${newQuote.quote_id} (duplicated from #${quoteId}).`, type: 'success' });
                } catch (err) {
                    setNotification({ message: err.message, type: 'danger' });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleSaveContact = async () => {
        if (!contactForm.name.trim()) {
            setNotification({ message: "Contact Name is required", type: "warning" });
            return;
        }

        try {
            setLoading(true);
            const res = await fetch(`${API_BASE}/customers/${selectedCustomerId}/contacts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(contactForm)
            });

            if (!res.ok) throw new Error("Failed to create contact");

            const newContact = await res.json();
            
            // Add to list and auto-select
            setContacts(prev => [...prev, newContact]);
            setSelectedContactId(newContact.contact_id);
            
            // Cleanup
            setShowContactModal(false);
            setContactForm({ name: "", email: "", phone: "", role: "General" });
            setNotification({ message: "Contact added successfully", type: "success" });
        } catch (err) {
            setNotification({ message: err.message, type: "danger" });
        } finally {
            setLoading(false);
        }
    };

    // Cascades a new set count to every line carrying this _source_assembly_id (primary,
    // sub-components, and consumables). Prices are intentionally untouched — assembly
    // price overrides are per-set so revenue scales via setCount on its own.
    const handleChangeAssemblyQty = (asmId, newSetCount) => {
        const parsed = parseInt(newSetCount, 10);
        if (!Number.isFinite(parsed) || parsed < 1) return;
        const summary = assemblySummaries[String(asmId)];
        const currentSetCount = summary?.setCount;
        // F39: with neither a primary nor an ops line the current set count is
        // a guess (1), so scaling every line by it would be arbitrary — refuse.
        if (summary && summary.setCountSource === 'fallback') {
            setNotification({ message: `Cannot cascade the assembly qty for ${summary.name}: no primary or ops line on the quote, so the current set count is unknown. Edit the line quantities directly.`, type: 'warning' });
            return;
        }
        if (!currentSetCount || currentSetCount < 1 || parsed === currentSetCount) return;
        setAdvancedQuoteLines(prev => prev.map(line => {
            if (String(line._source_assembly_id) !== String(asmId)) return line;
            const oldQty = parseFloat(line.quantity) || 0;
            if (oldQty <= 0) return line;
            const newQty = Math.max(1, Math.round((oldQty / currentSetCount) * parsed));
            return { ...line, quantity: newQty };
        }));
        setNotification({ message: `Updated assembly qty: ${currentSetCount} → ${parsed} sets. Component qtys cascaded; prices unchanged — click Recalculate All Prices if you want costs refreshed.`, type: 'info' });
    };

    // Per-set assembly price override (F10): exists only when the operator
    // types one here; '' clears it and the quoted line sum bills again.
    const handleOverrideChange = (asmId, val) => {
        setAssemblyPriceOverrides(prev => {
            const next = { ...prev };
            if (val === '' || val == null) delete next[asmId]; else next[asmId] = val;
            return next;
        });
    };

    const handleAddAssemblyToQuote = async (assemblyId) => {
        try {
            setLoading(true);

            // 1. Parallel Fetch: Header, Components, AND Consumables
            const [headerRes, componentsRes, consumablesRes] = await Promise.all([
                fetch(`${API_BASE}/assemblies/${assemblyId}`),
                fetch(`${API_BASE}/assemblies/${assemblyId}/components`),
                fetch(`${API_BASE}/assemblies/${assemblyId}/consumable-components`)
            ]);

            if (!headerRes.ok) throw new Error("Failed to load assembly header.");
            if (!componentsRes.ok) throw new Error("Failed to load assembly components.");
            
            const header = await headerRes.json();
            const components = await componentsRes.json();
            // Consumables might be empty or endpoint optional, handled gracefully
            const consumables = consumablesRes.ok ? await consumablesRes.json() : [];

            // --- USE THE QUANTITY INPUT ---
            const buildQty = parseInt(assemblyQty) || 1;

            // Helper to process a part into a quote line (Same as before)
            const processPart = async (partId, qtyPerAsm) => {
                const partRes = await fetch(`${API_BASE}/parts/${partId}`);
                const part = await partRes.json();
                
                let resolvedMaterialId = part.material_id || '';
                let resolvedCategory = part.stock_type; 

                if (resolvedMaterialId) {
                    const matMatch = materials.find(m => m.material_id === resolvedMaterialId);
                    if (matMatch && !resolvedCategory) resolvedCategory = matMatch.material_category;
                } 
                else if (part.material_type && part.thickness_id) {
                    const match = materials.find(m => 
                        m.material_type === part.material_type &&
                        m.thickness_id === part.thickness_id &&
                        (part.surface_finish ? m.surface_finish === part.surface_finish : true)
                    );
                    if (match) {
                        resolvedMaterialId = match.material_id;
                        if (!resolvedCategory) resolvedCategory = match.material_category;
                    }
                }

                const timeEstimates = applyHistoricalData(part);

                // --- CALCULATE TOTAL QUANTITY ---
                const totalLineQty = qtyPerAsm * buildQty;

                return {
                    id: `temp_${Date.now()}_${Math.random()}`,
                    part_id: part.part_id,
                    product_id: part.product_id || '',
                    description: part.description || '',
                    current_db_price: part.price != null ? parseFloat(part.price) : 0,    
                    price: part.price != null && parseFloat(part.price) > 0 ? String(part.price) : '',
                    size_length: part.size_length || '',
                    size_width: part.size_width || '',
                    
                    material_id: resolvedMaterialId,
                    material_type: part.material_type,
                    thickness_id: part.thickness_id,
                    surface_finish: part.surface_finish,
                    material_category: resolvedCategory,

                    manual_material_price: part.manual_material_price || part.material_cost_component || '',
                    is_customer_supplied_material: (part.notes || '').includes("Customer Supplied Material"),
                    quantity: totalLineQty,
                    process_steps: part.process_steps ? part.process_steps.split(',').map(s => s.trim()).filter(p => p) : [],
                    ...timeEstimates,
                    beam_on_minutes: part.beam_on_minutes || '',
                    cost_breakdown: null, 
                    show_breakdown: false,
                    _source_assembly_id: header.assembly_id,
                    _source_assembly_name: header.assembly_name,
                    // F01: seeded book price → 'book' basis; unpriced → 'calculated'
                    price_source: part.price != null && parseFloat(part.price) > 0 ? 'book' : 'calculated',
                    price_basis: null,
                    is_quick_quote_item: false // Standard manufacturing part
                };
            };

            const promises = [];

            // 3. Queue Primary Part
            if (header.primary_part_id) {
                promises.push(processPart(header.primary_part_id, header.primary_part_quantity || 1));
            }

            // 4. Queue Sub-Components
            for (const comp of components) {
                promises.push(processPart(comp.part_id, comp.quantity_per_assembly));
            }

            // Execute part fetches
            const manufacturingLines = await Promise.all(promises);

            // 5. Process Consumables (Map directly to line items). The price is
            // seeded here from the assembly's vendor cost and re-derived below
            // (F11/F23): max(book, cost × (1 + markup)), never a below-cost book.
            const seedMarkupFrac = (parseFloat(quoteSetup.material_markup) || 0) / 100;
            const consumableLines = consumables.map(c => {
                const seedCost = num(c.cost, NaN);
                const seedBook = num(c.price, 0);
                const seedMarkupPrice = Number.isFinite(seedCost) ? round2(seedCost * (1 + seedMarkupFrac)) : NaN;
                const bookWins = seedBook > 0 && (!Number.isFinite(seedMarkupPrice) || seedBook >= seedMarkupPrice);
                return {
                    id: `temp_cons_${Date.now()}_${Math.random()}`,
                    part_id: null,
                    consumable_id: c.consumable_id,
                    product_id: c.product_id || 'Consumable',
                    description: c.description || 'Assembly Consumable',
                    quantity: (c.quantity_per_assembly || 1) * buildQty,

                    price: bookWins ? seedBook.toFixed(2) : (Number.isFinite(seedMarkupPrice) ? seedMarkupPrice.toFixed(2) : (seedBook > 0 ? seedBook.toFixed(2) : '')),
                    price_source: bookWins ? 'book' : (Number.isFinite(seedMarkupPrice) ? 'markup' : (seedBook > 0 ? 'book' : 'manual')),
                    price_basis: !bookWins && Number.isFinite(seedMarkupPrice) ? seedMarkupFrac : null,

                    // Store Metadata for the UI
                    _cost: Number.isFinite(seedCost) ? seedCost : 0,
                    _cost_resolved: Number.isFinite(seedCost),
                    _cost_source: c.cost_source || null,
                    _book_price: seedBook,
                    _below_cost: c.below_cost === true || (seedBook > 0 && Number.isFinite(seedCost) && seedBook < seedCost - 0.005),
                    is_consumable_view: true, // <--- New Flag to trigger specific UI
                    pricing_mode: bookWins ? 'book' : 'markup', // Default mode tracking

                    // Standard fields (kept for compatibility)
                    is_quick_quote_item: true,
                    size_length: '', size_width: '', material_id: '',
                    material_type: null, thickness_id: null, surface_finish: null,
                    is_customer_supplied_material: false,
                    process_steps: [],
                    cost_breakdown: null, show_breakdown: false, stale: null,
                    _source_assembly_id: header.assembly_id,
                    _source_assembly_name: header.assembly_name
                };
            });

            // 5b. Per-set "Assembly operations" line (design §3.4): prices the
            // at/after-join work (weld / finish / coating) from the assembly's
            // assembly_processes rows. Only row-bearing assemblies emit one —
            // older assemblies without rows quote exactly as before.
            let opsLines = [];
            let finalManufacturingLines = manufacturingLines;
            try {
                const procRes = await fetch(`${API_BASE}/assemblies/${assemblyId}/processes`);
                if (!procRes.ok) throw new Error(`processes fetch failed (${procRes.status})`);
                const procs = await procRes.json();
                if (Array.isArray(procs) && procs.length > 0) {
                    const ops = await calcAssemblyOps(assemblyId, quoteSetup);
                    // Carry per-assembly minutes in the standard est fields so the
                    // Production Time Forecast sums them (minutes × sets).
                    // Same shape the reprice paths rebuild, from the same helper.
                    const opsEst = opsEstFromBreakdown(ops);
                    opsLines = [{
                        id: `temp_ops_${Date.now()}`,
                        part_id: null, consumable_id: null,
                        is_assembly_ops: true,
                        product_id: 'ASSEMBLY OPS',
                        description: `Assembly operations — ${header.assembly_name}`,
                        quantity: buildQty,
                        price: ops.calculated_price.toString(),
                        price_source: 'calculated', price_basis: null,
                        cost_breakdown: ops, show_breakdown: false, stale: null,
                        is_quick_quote_item: false,
                        is_customer_supplied_material: false,
                        size_length: '', size_width: '', material_id: '',
                        material_type: null, thickness_id: null, surface_finish: null,
                        process_steps: [],
                        ...opsEst,
                        _source_assembly_id: header.assembly_id,
                        _source_assembly_name: header.assembly_name,
                    }];

                    // Zero the PRIMARY line's est fields for stations the ops
                    // line covers (Production Time Forecast must not count the
                    // same minutes twice), then REPRICE the primary with the
                    // ops exclusion — its book price still includes the
                    // at/after-join value until Phase 3 strips part fields, and
                    // seeding it unrepriced would double-charge next to the ops line.
                    const coveredFields = Object.keys(opsLines[0]).filter(k => k.startsWith('initial_est_') && parseFloat(opsLines[0][k]) > 0);
                    finalManufacturingLines = manufacturingLines.map(l =>
                        l.part_id === header.primary_part_id
                            ? { ...l, ...Object.fromEntries(coveredFields.map(f => [f, ''])) }
                            : l
                    );
                    const primIdx = finalManufacturingLines.findIndex(l => l.part_id === header.primary_part_id);
                    if (primIdx >= 0) {
                        const forcedSet = new Set([header.assembly_id]);
                        const repriceRes = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(buildLinePayload(finalManufacturingLines[primIdx], quoteSetup, forcedSet)),
                        });
                        if (!repriceRes.ok) throw new Error(`primary re-price failed (${repriceRes.status})`);
                        const primCalc = await repriceRes.json();
                        finalManufacturingLines[primIdx] = {
                            ...finalManufacturingLines[primIdx],
                            price: primCalc.calculated_price.toString(),
                            // The book price still includes the at/after-join value the
                            // ops line now carries, so the primary is CALCULATED here.
                            price_source: 'calculated', price_basis: null,
                            cost_breakdown: primCalc, stale: null,
                        };
                    }
                }
            } catch (e) {
                // Coherent fallback: WITHOUT the ops line the exclusion never
                // applies (opsAsmIds gate) and the primary keeps its inclusive
                // book price — a complete legacy-style quote, visibly flagged.
                console.warn('Assembly ops line skipped:', e);
                opsLines = [];
                finalManufacturingLines = manufacturingLines;
                setNotification({
                    message: `Assembly operations pricing unavailable (${e.message}) — quoted "${header.assembly_name}" the legacy way (no ops line). Retry the add if you want per-assembly ops pricing.`,
                    type: 'warning',
                });
            }

            // 6. Price EVERY line from the calculator (F11): part lines that
            // still lack a breakdown (sub-components, or the primary of a
            // rowless assembly) and every purchased component via the
            // consumable calculator (F36). Price follows the basis: an
            // unpriced part adopts the calculated price; a book-priced part
            // keeps its book price but gets the breakdown (drift is visible);
            // components default to max(book, cost × (1 + markup)) and are
            // flagged when the book price is below cost (F23).
            const opsSetForPricing = opsLines.length > 0 ? new Set([header.assembly_id]) : new Set();
            let pricedFromCalc = 0;
            const notPriced = [];
            const pricedParts = await Promise.all(finalManufacturingLines.map(async (l) => {
                if (l.cost_breakdown) { pricedFromCalc += 1; return l; }
                try {
                    const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(buildLinePayload(l, quoteSetup, opsSetForPricing)),
                    });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const result = await res.json();
                    const next = { ...l, cost_breakdown: result, stale: null };
                    const d = deriveLinePrice(next, result, headerFrac);
                    if (d.changed && d.price != null) next.price = d.price.toFixed(2);
                    pricedFromCalc += 1;
                    return next;
                } catch (e) {
                    console.warn('Add Assembly: part line not priced', l.product_id, e);
                    notPriced.push(l.product_id || 'part');
                    return l;
                }
            }));
            const belowCostComponents = [];
            const pricedConsumables = await Promise.all(consumableLines.map(async (l) => {
                let next = l;
                if (l.consumable_id) {
                    try {
                        const res = await fetch(`${API_BASE}/quotes/calculate-part-cost`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(buildConsumablePayload(l, quoteSetup)),
                        });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const result = await res.json();
                        next = { ...l, ...consumableExtrasFromCalc(l, result), cost_breakdown: result, stale: null };
                        pricedFromCalc += 1;
                    } catch (e) {
                        // Vendor cost from GET /assemblies/{id}/consumable-components stands in.
                        console.warn('Add Assembly: consumable calculator unavailable', l.product_id, e);
                        if (l._cost_resolved) pricedFromCalc += 1; else notPriced.push(l.product_id || 'component');
                    }
                }
                // Default price = max(book, cost × (1 + markup)); never a below-cost book.
                const cost = lineUnitCost(next);
                const book = num(next._book_price, 0);
                const markupPrice = Number.isFinite(cost) ? round2(cost * (1 + seedMarkupFrac)) : NaN;
                const bookWins = book > 0 && (!Number.isFinite(markupPrice) || book >= markupPrice);
                if (next._below_cost) belowCostComponents.push(`${next.product_id} (book ${fmtMoney(book)} < cost ${fmtMoney(cost)})`);
                if (bookWins) return { ...next, price: book.toFixed(2), price_source: 'book', price_basis: null, pricing_mode: 'book' };
                if (Number.isFinite(markupPrice)) return { ...next, price: markupPrice.toFixed(2), price_source: 'markup', price_basis: seedMarkupFrac, pricing_mode: 'markup' };
                return next;
            }));

            // Combine and update state
            const totalCount = pricedParts.length + pricedConsumables.length + opsLines.length;
            const priceable = pricedParts.length + pricedConsumables.length;
            setAdvancedQuoteLines(prev => [...prev, ...pricedParts, ...pricedConsumables, ...opsLines]);
            setQuoteMode('Advanced');

            // F10: the assembly's System Price is NOT seeded as an override any
            // more — it is shown on the Assembly Pricing card with its source
            // as a hint; an override exists only when the operator types one.
            const sysPrice = num(header.assembly_price, 0);
            const sysHint = sysPrice > 0
                ? ` System Price ${fmtMoney(sysPrice)}${header.assembly_price_source === 'quote_approval' ? ` (from Quote #${header.assembly_price_quote_id || '?'})` : header.assembly_price_source ? ` (${header.assembly_price_source})` : ''} is shown on the card as a hint, not applied as an override.`
                : '';
            const belowNote = belowCostComponents.length > 0 ? ` Book below cost: ${belowCostComponents.join('; ')} — priced at cost + markup instead.` : '';
            const notPricedNote = notPriced.length > 0 ? ` Not priced: ${notPriced.join(', ')} — click Calc on those lines.` : '';
            setNotification({
                message: `Added ${buildQty} set(s) of assembly '${header.assembly_name}' (${totalCount} total lines). ${pricedFromCalc} of ${priceable} lines priced from calculator.${belowNote}${notPricedNote}${sysHint}`,
                type: notPriced.length > 0 || belowCostComponents.length > 0 ? 'warning' : 'success',
            });

        } catch (err) {
            setNotification({ message: err.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    const availableThicknesses = useMemo(() => {
        const relMats = materials.filter(m => (materialFilters.owner ? m.owner_customer_id === parseInt(materialFilters.owner, 10) : true) && (materialFilters.type ? m.material_type === materialFilters.type : true));
        const ids = [...new Set(relMats.map(m => m.thickness_id))];
        return filterOptions.thicknesses.filter(t => ids.includes(t.thickness_id));
    }, [materialFilters.owner, materialFilters.type, materials, filterOptions.thicknesses]);

    const availableFinishes = useMemo(() => {
        const relMats = materials.filter(m => (materialFilters.owner ? m.owner_customer_id === parseInt(materialFilters.owner, 10) : true) && (materialFilters.type ? m.material_type === materialFilters.type : true) && (materialFilters.thickness ? m.thickness_id === parseInt(materialFilters.thickness, 10) : true));
        return [...new Set(relMats.map(m => m.surface_finish).filter(Boolean))].sort();
    }, [materialFilters, materials]);

    const filteredMaterials = useMemo(() => materials.filter(m => {
        return (materialFilters.owner ? m.owner_customer_id === parseInt(materialFilters.owner, 10) : true) &&
               (materialFilters.type ? m.material_type === materialFilters.type : true) &&
               (materialFilters.thickness ? m.thickness_id === parseInt(materialFilters.thickness, 10) : true) &&
               (materialFilters.finish ? m.surface_finish === materialFilters.finish : true);
    }), [materials, materialFilters]);

    const filteredAssemblies = useMemo(() => {
        if (!selectedCustomerId) return [];
        // Retired assemblies stay loaded (F30) for quotes that already reference
        // them, but are not offered for adding.
        return assemblies.filter(a => String(a.customer_id) === String(selectedCustomerId) && a.active_status !== false);
    }, [assemblies, selectedCustomerId]);

    const filteredQuotes = useMemo(() => {
        const now = new Date();
        const cutoff = filterDays ? new Date(now.getTime() - filterDays * 24 * 60 * 60 * 1000) : null;
        const term = searchTerm.trim().toLowerCase();
        return quotes.filter(q => {
            // Universal text search — quote header fields + line items
            if (term) {
                const customerName = (customers.find(c => c.customer_id === q.customer_id)?.name || '').toLowerCase();
                const headerFields = [
                    String(q.quote_id ?? ''),
                    customerName,
                    String(q.customer_po ?? '').toLowerCase(),
                    String(q.customer_so ?? '').toLowerCase(),
                    String(q.contact_name ?? '').toLowerCase(),
                    String(q.contact_email ?? '').toLowerCase(),
                    String(q.notes ?? '').toLowerCase(),
                    String(q.status ?? '').toLowerCase(),
                    String(q.generated_sales_order_number ?? '').toLowerCase(),
                    String(q.nester_project_id ?? '').toLowerCase(),
                ];
                const headerMatch = headerFields.some(v => v.includes(term));
                const lineMatch = !headerMatch && Array.isArray(q.lines) && q.lines.some(l =>
                    String(l.product_id ?? '').toLowerCase().includes(term) ||
                    String(l.description ?? '').toLowerCase().includes(term)
                );
                if (!headerMatch && !lineMatch) return false;
            }
            // Status filter
            if (filterStatus && q.status !== filterStatus) return false;
            // Customer filter
            if (filterCustomerId && q.customer_id !== parseInt(filterCustomerId, 10)) return false;
            // Date filter
            if (cutoff && q.created_at) {
                const createdAt = new Date(q.created_at);
                if (createdAt < cutoff) return false;
            }
            return true;
        });
    }, [quotes, searchTerm, filterStatus, filterCustomerId, filterDays, customers]);
    const getCustomerName = (id) => customers.find(c => c.customer_id === id)?.name || 'Unknown';
    const toggleBreakdown = (index) => {
        const lines = [...advancedQuoteLines];
        lines[index].show_breakdown = !lines[index].show_breakdown;
        setAdvancedQuoteLines(lines);
    };
    const isEditingAllowed = isCreating || (selectedQuote && selectedQuote.status === 'Draft');

    const renderQuoteForm = () => (
        <div>
            <h5 className="mt-4">1. Customer & Contact</h5>

            <div className="row g-2 mb-3">
                <div className="col-md-5">
                    <label htmlFor="customer" className="form-label small text-muted mb-1">Customer</label>
                    <div className="input-group input-group-sm">
                        <select id="customer" className="form-select" value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)}>
                            <option value="" disabled>-- Select Customer --</option>
                            {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                        </select>
                        <button className="btn btn-outline-secondary" type="button" onClick={() => setShowNewCustomerForm(!showNewCustomerForm)}>{showNewCustomerForm ? 'Cancel' : '+ New'}</button>
                    </div>
                </div>

                <div className="col-md-5">
                    <label className="form-label small text-muted mb-1">Contact Person</label>
                    <div className="input-group input-group-sm">
                        <select
                            className="form-select"
                            value={selectedContactId}
                            onChange={e => setSelectedContactId(e.target.value)}
                            disabled={!selectedCustomerId}
                        >
                            <option value="">-- Main / No Contact --</option>
                            {contacts.map(c => (
                                <option key={c.contact_id} value={c.contact_id}>
                                    {c.name} ({c.role})
                                </option>
                            ))}
                        </select>
                        <button
                            className="btn btn-outline-secondary"
                            type="button"
                            disabled={!selectedCustomerId}
                            onClick={() => setShowContactModal(true)}
                        >
                            + New
                        </button>
                    </div>
                </div>
                <div className="col-md-2">
                    <label className="form-label small text-muted mb-1">Customer PO #</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={selectedQuote?.customer_po || ''}
                        onChange={e => handleQuoteChange('customer_po', e.target.value)}
                        placeholder="PO-12345"
                    />
                </div>
                <div className="col-md-2">
                    <label className="form-label small text-muted mb-1">Cust SO</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={selectedQuote?.customer_so || ''}
                        onChange={e => handleQuoteChange('customer_so', e.target.value)}
                        placeholder="SO-6789"
                    />
                </div>
                <div className="col-md-2">
                    <label className="form-label small text-muted mb-1">
                        Fulfillment <span className="text-danger" title="Required — selection is locked at quote time and inherits to the Sales Order at conversion.">*</span>
                    </label>
                    <select
                        className={`form-select form-select-sm ${!fulfillmentType ? 'border-danger' : ''}`}
                        value={fulfillmentType}
                        onChange={e => setFulfillmentType(e.target.value)}
                        title="Pickup orders use Mark Ready for Pickup → Confirm Pickup workflow on the SO. Choice carries to the SO at convert-to-sales-order."
                    >
                        <option value="">— Select —</option>
                        <option value="shipping">Carrier Shipment</option>
                        <option value="pickup">Customer Pickup</option>
                        <option value="delivery">We Deliver</option>
                    </select>
                </div>
                <div className="col-md-2">
                    <label className="form-label small text-muted mb-1">Ship To</label>
                    <select
                        className="form-select form-select-sm"
                        value={shippingAddressId}
                        onChange={e => setShippingAddressId(e.target.value)}
                        title="Destination for this quote's order. Manage locations on the Customers page."
                    >
                        {addresses.length === 0 && <option value="">(customer default)</option>}
                        {addresses.map(a => (
                            <option key={a.address_id} value={a.address_id}>
                                {a.label}{a.city ? ` — ${a.city}, ${a.state || ''}` : ''}{a.is_default ? ' (default)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <ErrorBoundary label="The pricing toggle">
                <TmQuotePricingToggle
                    mode={quotePricingMode}
                    offer={tmCreationAllowed(features, selectedCustomerId)}
                    onChange={handleQuotePricingModeChange}
                />
            </ErrorBoundary>
            {showNewCustomerForm && (
                <div className="card card-body mb-3 py-2">
                    <h6 className="mb-2">Create Temporary Customer</h6>
                    <div className="input-group input-group-sm"><input type="text" className="form-control" placeholder="New Customer Name" value={newCustomerName} onChange={e => setNewCustomerName(e.target.value)} /><button className="btn btn-success" onClick={handleCreateCustomer}>Save</button></div>
                </div>
            )}

            <div className="d-flex align-items-center gap-3 mt-3 mb-2">
                <h6 className="mb-0">2. Quote Type</h6>
                <div className="btn-group btn-group-sm"><button type="button" className={`btn ${quoteMode === 'Quick' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setQuoteMode('Quick')} disabled={isTmQuote} title={isTmQuote ? TM_NOT_IN_V1 : undefined}>Quick</button><button type="button" className={`btn ${quoteMode === 'Advanced' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setQuoteMode('Advanced')}>Advanced</button></div>
            </div>

            <h6 className="mt-3 mb-2">3. Line Items</h6>
            {/* 1. Add Part / Add Assembly Row */}
            <div className="row g-2 mb-3">
                <div className="col-md-6">
                    <label className="form-label small text-muted">Add Individual Part</label>
                    {selectedCustomerId && customerParts.length > 0 && (
                        <SearchablePartDropdown 
                            parts={customerParts} 
                            onPartSelect={addExistingPartToQuote} 
                        />
                    )}
                </div>
                
                {/* --- MODIFIED ASSEMBLY SELECTION BLOCK --- */}
                <div className="col-md-6">
                    <div className="row g-2">
                         <div className="col-3">
                            <label className="form-label small text-muted">Qty of Assemblies</label>
                            <input 
                                type="number" 
                                className="form-control" 
                                value={assemblyQty} 
                                onChange={(e) => setAssemblyQty(parseInt(e.target.value) || 1)} 
                                min="1" 
                            />
                        </div>
                        <div className="col-9">
                            <label className="form-label small text-muted">Select Assembly</label>
                            {isTmQuote ? (
                                <input type="text" className="form-control" disabled value="" placeholder={TM_NOT_IN_V1} title={TM_NOT_IN_V1} />
                            ) : (
                            <SearchableAssemblyDropdown 
                                assemblies={filteredAssemblies}
                                onAssemblySelect={(a) => handleAddAssemblyToQuote(a.assembly_id)} 
                            />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 1b. Add Purchased Component — an item bought finished from a vendor and
                resold on its own line, with no assembly and nothing manufactured here. */}
            <div className="row g-2 mb-3">
                <div className="col-md-6">
                    <label className="form-label small text-muted">
                        Add Purchased Component
                        <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>
                            (bought finished from a vendor — priced from its vendor cost)
                        </span>
                    </label>
                    {isTmQuote ? (
                        <input type="text" className="form-control" disabled value="" placeholder={TM_NOT_IN_V1} title={TM_NOT_IN_V1} />
                    ) : (
                    <SearchableComponentDropdown
                        components={purchasedComponents}
                        onComponentSelect={addComponentToQuote}
                    />
                    )}
                </div>
            </div>

            {/* 2. CSV Import Button (Kept Separate) */}
            <div className="d-flex justify-content-end mb-3">
                <div>
                    <input 
                        type="file" 
                        accept=".csv" 
                        style={{ display: 'none' }} 
                        ref={fileInputRef} 
                        onChange={handleFileUpload} 
                    />
                    <button className="btn btn-outline-success btn-sm" onClick={handleBulkImportClick} disabled={isTmQuote} title={isTmQuote ? TM_NOT_IN_V1 : undefined}>
                        📥 Bulk Import CSV
                    </button>
                </div>
            </div>

            {quoteMode === 'Quick' ? (
                <div>
                    {quickQuoteLines.map((line, index) => (
                        <div key={line.id}
                             className="p-2 rounded mb-2"
                             style={{
                                 backgroundColor: index % 2 === 0 ? 'var(--jq-bg-card, #FFFFFF)' : 'var(--jq-bg-surface, #F0F2F5)',
                                 fontSize: '0.875rem',
                                 borderLeft: `4px solid ${index % 2 === 0 ? '#4A90D9' : '#7AB648'}`,
                                 borderTop: `2px solid ${index % 2 === 0 ? '#4A90D9' : '#7AB648'}`,
                                 borderRight: `2px solid ${index % 2 === 0 ? '#4A90D9' : '#7AB648'}`,
                                 borderBottom: `2px solid ${index % 2 === 0 ? '#4A90D9' : '#7AB648'}`,
                             }}
                        >
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="badge rounded-pill text-white" style={{ backgroundColor: index % 2 === 0 ? '#4A90D9' : '#7AB648', fontSize: '0.7rem' }}>Part {index + 1}</span>
                                <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={() => removeLine(quickQuoteLines, setQuickQuoteLines, line.id)}>Remove</button>
                            </div>
                            <div className="row g-1">
                                <div className="col-md-2">
                                    <label className="form-label small mb-0">Product ID</label>
                                    <input type="text" className="form-control form-control-sm" value={line.product_id} onChange={e => handleLineChange(quickQuoteLines, setQuickQuoteLines, index, 'product_id', e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small mb-0">Description</label>
                                    <input type="text" className="form-control form-control-sm" value={line.description} onChange={e => handleLineChange(quickQuoteLines, setQuickQuoteLines, index, 'description', e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small mb-0">Material</label>
                                    <input type="text" className="form-control form-control-sm" value={line.material} onChange={e => handleLineChange(quickQuoteLines, setQuickQuoteLines, index, 'material', e.target.value)} />
                                </div>
                                <div className="col-md-1">
                                    <label className="form-label small mb-0">Qty</label>
                                    <input type="number" className="form-control form-control-sm" value={line.quantity} onChange={e => handleLineChange(quickQuoteLines, setQuickQuoteLines, index, 'quantity', e.target.value)} min="1" />
                                </div>
                                <div className="col-md-1">
                                    <label className="form-label small mb-0">Price</label>
                                    <input type="number" className="form-control form-control-sm" value={line.price} onChange={e => handleLineChange(quickQuoteLines, setQuickQuoteLines, index, 'price', e.target.value)} min="0" step="0.01" />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small mb-0">Subtotal</label>
                                    <input type="text" className="form-control form-control-sm fw-bold" value={`$${(Number(line.quantity) * Number(line.price)).toFixed(2)}`} readOnly style={{ backgroundColor: 'var(--jq-subtotal-bg, #e8f5e9)' }} />
                                </div>
                            </div>
                        </div>
                    ))}
                    <button className="btn btn-sm btn-secondary" onClick={() => addLine(setQuickQuoteLines, getInitialQuickLine)}>+ Add Quick Part</button>
                </div>
            ) : (
                <div>
                    {/* F42: $0 sell rates price labour at nothing — say so in red, above the setup. */}
                    {zeroRates.length > 0 && (
                        <div className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: '0.85rem' }}>
                            <strong>Sell rate is $0/hr for {zeroRates.join(', ')}.</strong> Every calculated price on this quote omits that labour
                            (the price collapses to material only, below true cost). Set the rates in Quote Setup below, then Recalculate All Prices.
                            Approval is blocked while any sell rate is $0.
                        </div>
                    )}
                    {zeroRates.length === 0 && !(num(quoteSetup.material_markup, 0) > 0) && (
                        <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: '0.85rem' }}>
                            <strong>Material markup is 0%.</strong> Material and purchased components are priced at cost.
                        </div>
                    )}
                    <QuoteSetup
                        setup={quoteSetup}
                        onChange={(e) => setQuoteSetup(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                        onRecalculate={handleRecalculateAll}
                        isCalculating={loading}
                        forceExpanded={!isEditingAllowed && !!selectedQuote}
                        readOnly={!isEditingAllowed}
                    />
                    <div className="d-flex align-items-end gap-2 mb-3 p-2 border rounded bg-light flex-wrap" style={{ fontSize: '0.85rem' }}>
                        <strong className="text-muted align-self-center me-1" style={{ whiteSpace: 'nowrap' }}>Filter:</strong>
                        <div style={{ minWidth: '140px', flex: '1 1 140px' }}>
                            <label className="form-label small mb-0">Owner</label>
                            <select className="form-select form-select-sm" name="owner" value={materialFilters.owner} onChange={handleFilterChange}>
                                <option value="">All</option>
                                {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div style={{ minWidth: '130px', flex: '1 1 130px' }}>
                            <label className="form-label small mb-0">Type</label>
                            <select className="form-select form-select-sm" name="type" value={materialFilters.type} onChange={handleFilterChange}>
                                <option value="">All</option>
                                {filterOptions.types.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div style={{ minWidth: '120px', flex: '1 1 120px' }}>
                            <label className="form-label small mb-0">Thickness</label>
                            <select className="form-select form-select-sm" name="thickness" value={materialFilters.thickness} onChange={handleFilterChange}>
                                <option value="">All</option>
                                {availableThicknesses.map(t => <option key={t.thickness_id} value={t.thickness_id}>{t.display_thickness}</option>)}
                            </select>
                        </div>
                        <div style={{ minWidth: '130px', flex: '1 1 130px' }}>
                            <label className="form-label small mb-0">Finish</label>
                            <select className="form-select form-select-sm" name="finish" value={materialFilters.finish} onChange={handleFilterChange}>
                                <option value="">All</option>
                                {availableFinishes.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                    </div>

                    {advancedQuoteLines.map((line, index) => {
                        // --- Assembly grouping header (rendered above the FIRST line of each
                        // contiguous run of lines that share a _source_assembly_id). Click toggles
                        // collapse/expand for the whole group. Standalone lines (no
                        // _source_assembly_id) are unaffected and always render.
                        const asmId = line._source_assembly_id;
                        const prevAsmId = index > 0 ? advancedQuoteLines[index - 1]._source_assembly_id : null;
                        const isFirstOfAsmGroup = asmId && asmId !== prevAsmId;
                        const isAsmExpanded = !asmId || !!expandedAssemblies[asmId];

                        // Group summary (per set) — same object the Assembly Pricing card reads.
                        const groupSummary = asmId ? assemblySummaries[String(asmId)] : null;
                        const inOverride = !!(groupSummary && groupSummary.hasOverride);

                        let asmHeader = null;
                        if (isFirstOfAsmGroup) {
                            // F40: the API field is assembly_name (there is no `name`); the
                            // line itself carries the name it was added with.
                            const asmDef = assemblies.find(a => a.assembly_id === asmId);
                            const asmName = asmDef?.assembly_name || line._source_assembly_name || `#${asmId}`;
                            const counts = groupSummary ? groupSummary.counts : { parts: 0, components: 0, ops: 0 };
                            asmHeader = (
                                <div
                                    className="d-flex justify-content-between align-items-center p-2 rounded mb-2 border border-warning bg-warning bg-opacity-10 flex-wrap gap-2"
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => toggleAssemblyExpanded(asmId)}
                                    title={isAsmExpanded ? 'Click to collapse this assembly' : 'Click to expand and edit this assembly\'s parts'}
                                >
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span style={{ fontSize: '0.9rem', width: '1rem', textAlign: 'center' }}>
                                            {isAsmExpanded ? '▼' : '▶'}
                                        </span>
                                        <strong className="text-warning-emphasis">
                                            Assembly: {asmName}
                                        </strong>
                                        {groupSummary?.retired && (
                                            <span className="badge bg-warning text-dark border border-dark" title="This assembly is inactive (retired) in the catalog. Its definition still drives the set count and System Price.">retired assembly</span>
                                        )}
                                        <span className="badge bg-warning text-dark" title="manufactured part lines · purchased component lines · per-set assembly operations line">
                                            {counts.parts} part{counts.parts === 1 ? '' : 's'} · {counts.components} component{counts.components === 1 ? '' : 's'}{counts.ops ? ' · ops' : ''}
                                        </span>
                                        {groupSummary && (
                                            <span className="small text-muted" title={inOverride
                                                ? `override ${fmtMoney(groupSummary.override)}/set × ${groupSummary.setCount} sets`
                                                : `Σ line price × qty ÷ ${groupSummary.setCount} set(s) — the per-set price the Subtotal bills`}>
                                                {inOverride ? 'override' : 'quoted'} {fmtMoney(groupSummary.effectivePerSet)}/set × {groupSummary.setCount} = <strong>{fmtMoney(groupSummary.effectiveTotal)}</strong>
                                                {inOverride && <span className="badge bg-warning text-dark ms-1">override in force</span>}
                                            </span>
                                        )}
                                    </div>
                                    <span className="small text-muted">
                                        {isAsmExpanded ? 'Click to collapse' : 'Click to expand'}
                                    </span>
                                </div>
                            );
                        }

                        // Collapsed assembly: render header on first iteration, hide remaining
                        // lines in the group entirely.
                        if (asmId && !isAsmExpanded) {
                            return <React.Fragment key={`line-${line.id}`}>{asmHeader}</React.Fragment>;
                        }

                        if (line.is_consumable_view) {
                            return (
                                <React.Fragment key={`line-${line.id}`}>
                                    {asmHeader}
                                    <ConsumableLineRow
                                        line={line}
                                        index={index}
                                        onPatch={patchAdvancedLine}
                                        onRemove={(id) => removeLine(advancedQuoteLines, setAdvancedQuoteLines, id)}
                                        materialMarkup={quoteSetup.material_markup}
                                        readOnly={!isEditingAllowed}
                                        badges={(
                                            <LineBadges line={line} header={headerFrac} inOverride={inOverride} canEdit={isEditingAllowed}
                                                        onApply={(sel) => applyLinePrice(index, sel)}
                                                        onRefresh={() => handleCalculateCost(index, { keepPrice: true })} />
                                        )}
                                    />
                                </React.Fragment>
                            );
                        }

                        // Per-set "Assembly operations" line (design §3.4): the
                        // at/after-join work priced per SET from assembly_processes.
                        if (line.is_assembly_ops) {
                            const opsBreak = line.cost_breakdown?.breakdown?.operations || [];
                            const opsSubtotal = (parseFloat(line.price) || 0) * (parseInt(line.quantity, 10) || 0);
                            // F37: the ops line's cost and margin were never shown — per set.
                            const opsCost = num(line.cost_breakdown?.total_cost, NaN);
                            const opsPrice = parseFloat(line.price) || 0;
                            const opsMargin = Number.isFinite(opsCost) && opsPrice > 0 ? (opsPrice - opsCost) / opsPrice : null;
                            return (
                                <React.Fragment key={`line-${line.id}`}>
                                    {asmHeader}
                                    <div className="p-2 rounded mb-2"
                                         style={{ backgroundColor: 'var(--jq-bg-card, #FFFFFF)', fontSize: '0.875rem', border: '2px solid #17A2B8', borderLeft: '4px solid #17A2B8' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                                            <span className="badge rounded-pill text-white" style={{ backgroundColor: '#17A2B8', fontSize: '0.7rem' }}>ASSEMBLY OPS</span>
                                            <span className="small text-muted" title="Per SET: true cost = Σ minutes × wage rate + vendor charges; price = Σ minutes × sell rate + vendor charges × (1 + markup); margin = (price − cost) ÷ price">
                                                per set — cost <strong>{fmtMoney(opsCost, { dash: true })}</strong> · price <strong>{fmtMoney(opsPrice)}</strong> · margin{' '}
                                                <span className={`badge ${opsMargin == null ? 'bg-light text-dark border' : opsMargin < 0.2 ? 'bg-danger' : 'bg-success'}`}>{fmtPct(opsMargin)}</span>
                                            </span>
                                            <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={() => removeLine(advancedQuoteLines, setAdvancedQuoteLines, line.id)}>Remove</button>
                                        </div>
                                        <LineBadges line={line} header={headerFrac} inOverride={inOverride} canEdit={isEditingAllowed}
                                                    onApply={(sel) => applyLinePrice(index, sel)}
                                                    onRefresh={() => handleCalculateCost(index, { keepPrice: true })} />
                                        <div className="row g-1 align-items-end">
                                            <div className="col-md-5">
                                                <label className="form-label small mb-0">Description</label>
                                                <input type="text" className="form-control form-control-sm" value={line.description}
                                                       onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'description', e.target.value)} />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label small mb-0">Assemblies</label>
                                                <input type="number" className="form-control form-control-sm" value={line.quantity} min="1"
                                                       onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'quantity', e.target.value)} />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label small mb-0">$ / assembly</label>
                                                <input type="number" step="0.01" className="form-control form-control-sm" value={line.price}
                                                       title="Typing a price makes this line MANUAL: ↺ and Recalculate then refresh the cost but keep the price (drift badge shows the gap)."
                                                       onChange={e => setLinePriceManual(index, e.target.value)} />
                                                {line.cost_breakdown && (
                                                    <button type="button" className="btn btn-link btn-sm p-0 form-text" style={{ fontSize: '0.75rem' }} onClick={() => toggleBreakdown(index)}>
                                                        {line.show_breakdown ? 'Hide' : 'Details'}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label small mb-0">Subtotal</label>
                                                <div className="form-control form-control-sm bg-light">${opsSubtotal.toFixed(2)}</div>
                                            </div>
                                            <div className="col-md-1">
                                                <button className="btn btn-sm btn-outline-info w-100" title="Reprice from assembly processes — the price follows this line's basis (calculated by default; a manual price is kept and the drift badge shows the gap)"
                                                        onClick={() => handleCalculateCost(index)}>↺</button>
                                            </div>
                                        </div>
                                        {opsBreak.length > 0 && (
                                            <div className="mt-1 d-flex flex-wrap gap-2" style={{ fontSize: '0.75rem', color: 'var(--jq-text-muted, #6c757d)' }}>
                                                {opsBreak.map((op, i) => (
                                                    <span key={i} className="badge bg-light text-dark border" title={`cost ${fmtMoney(op.cost, { dash: true })} · price ${fmtMoney(op.price, { dash: true })}${op.quoted_rate != null ? ` · ${op.minutes} min × $${Number(op.quoted_rate).toFixed(2)}/hr` : ''}`}>
                                                        {op.station}{op.minutes != null ? ` ${op.minutes} min` : ''} · ${Number(op.price || 0).toFixed(2)}
                                                    </span>
                                                ))}
                                                <span className="ms-auto">Folds into the primary line at SO conversion</span>
                                            </div>
                                        )}
                                        {line.show_breakdown && line.cost_breakdown && (
                                            <QuoteCostAnalysisTable
                                                line={line}
                                                breakdown={line.cost_breakdown}
                                                isOps
                                                header={headerFrac}
                                                onSelectPrice={(sel) => applyLinePrice(index, sel)}
                                                readOnly={!isEditingAllowed}
                                                tm={isTmQuote}
                                            />
                                        )}
                                    </div>
                                </React.Fragment>
                            );
                        }
                        return (
                        <React.Fragment key={`line-${line.id}`}>
                        {asmHeader}
                        <div className="p-2 rounded mb-2"
                             style={{
                                 backgroundColor: index % 2 === 0 ? 'var(--jq-bg-card, #FFFFFF)' : 'var(--jq-bg-surface, #F0F2F5)',
                                 fontSize: '0.875rem',
                                 borderLeft: `4px solid ${index % 2 === 0 ? '#4A90D9' : '#7AB648'}`,
                                 borderTop: `2px solid ${index % 2 === 0 ? '#4A90D9' : '#7AB648'}`,
                                 borderRight: `2px solid ${index % 2 === 0 ? '#4A90D9' : '#7AB648'}`,
                                 borderBottom: `2px solid ${index % 2 === 0 ? '#4A90D9' : '#7AB648'}`,
                             }}
                        >
                            {/* Header row: Part # + Remove */}
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="badge rounded-pill text-white" style={{ backgroundColor: index % 2 === 0 ? '#4A90D9' : '#7AB648', fontSize: '0.7rem' }}>Part {index + 1}</span>
                                <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={() => removeLine(advancedQuoteLines, setAdvancedQuoteLines, line.id)}>Remove</button>
                            </div>
                            {/* Basis / drift / stale / override strip (F01, F05, F09, F10) */}
                            <LineBadges line={line} header={headerFrac} inOverride={inOverride} canEdit={isEditingAllowed}
                                        onApply={(sel) => applyLinePrice(index, sel)}
                                        onRefresh={() => handleCalculateCost(index, { keepPrice: true })} />
                            {/* Row 1: Product ID, Description, Qty, Price+Calc, Subtotal */}
                            <div className="row g-1 mb-1">
                                <div className="col-md-3">
                                    <label className="form-label small mb-0">Product ID</label>
                                    <input type="text" className="form-control form-control-sm" value={line.product_id} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'product_id', e.target.value)} />
                                    {line.part_id && (
                                        <RevisionSelector productId={line.product_id} currentPartId={line.part_id} onRevisionSelect={(newId) => handleRevisionChange(index, newId)} />
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small mb-0">Description</label>
                                    <input type="text" className="form-control form-control-sm" value={line.description} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'description', e.target.value)} />
                                </div>
                                <div className="col-md-1">
                                    <label className="form-label small mb-0">Qty</label>
                                    <input type="number" className="form-control form-control-sm" value={line.quantity} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'quantity', e.target.value)} min="1" />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small mb-0">
                                        Price
                                        {line.current_db_price > 0 && !isTmQuote && (
                                            <span
                                                className="ms-1 badge bg-info text-dark"
                                                title="Click to use the last saved book price (parts.price) — the line's basis becomes Book"
                                                style={{ cursor: 'pointer', fontSize: '0.65rem' }}
                                                onClick={() => isEditingAllowed && applyLinePrice(index, { price: line.current_db_price, price_source: 'book', price_basis: null })}
                                            >
                                                Last: ${line.current_db_price.toFixed(2)}
                                            </span>
                                        )}
                                    </label>
                                    {isTmQuote ? (
                                    <div className="input-group input-group-sm">
                                        <TmQuoteLinePrice line={line} />
                                        <button className="btn btn-outline-secondary" type="button"
                                                title="Refresh this line's cost breakdown (the estimate); a T&M line keeps no price"
                                                onClick={() => handleCalculateCost(index)}>Calc</button>
                                    </div>
                                    ) : (
                                    <div className="input-group input-group-sm">
                                        <input type="number" className="form-control" value={line.price}
                                               title="Typing a price makes this line MANUAL: Calc and Recalculate then refresh the cost but keep the price (the drift badge shows the gap). Pick a basis in the Cost Analysis to make it follow the calculator again."
                                               onChange={e => setLinePriceManual(index, e.target.value)} min="0" step="0.01" />
                                        <button className="btn btn-outline-secondary" type="button"
                                                title="Recalculate this line's cost breakdown and re-derive its price from its basis (calculated / target margin follow the new numbers; manual / book keep the price)"
                                                onClick={() => handleCalculateCost(index)}>Calc</button>
                                    </div>
                                    )}
                                    {line.cost_breakdown && (
                                        <a href="#" className="form-text small" style={{ fontSize: '0.75rem' }} onClick={(e) => {e.preventDefault(); toggleBreakdown(index);}}>
                                            {line.show_breakdown ? 'Hide' : 'Details'}
                                        </a>
                                    )}
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small mb-0">Subtotal</label>
                                    {isTmQuote ? <TmQuoteLineSubtotal line={line} /> : (
                                    <input type="text" className="form-control form-control-sm fw-bold" value={`$${(Number(line.quantity) * Number(line.price)).toFixed(2)}`} readOnly style={{ backgroundColor: 'var(--jq-subtotal-bg, #e8f5e9)' }} />
                                    )}
                                </div>
                            </div>
                            {/* Row 2: Dimensions + Material */}
                            <div className="row g-1 mb-1">
                                <div className="col-md-1">
                                    <label className="form-label small mb-0">L (in)</label>
                                    <input type="number" className="form-control form-control-sm" value={line.size_length} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'size_length', e.target.value)} min="0" />
                                </div>
                                <div className="col-md-1">
                                    <label className="form-label small mb-0">W (in)</label>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={line.size_width}
                                        onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'size_width', e.target.value)}
                                        min="0"
                                        disabled={line.material_category === 'Shape Stock'}
                                        placeholder={line.material_category === 'Shape Stock' ? "N/A" : ""}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small mb-0">Material</label>
                                    {line.part_id && !line.is_customer_supplied_material && (() => {
                                        // 'manual' is the part page's "Customer Supplied / No Cost"
                                        // radio (Sheet Stock) / "Manual / Customer Supplied" (Shape
                                        // Stock) — both mean the customer brought the steel.
                                        // 'manual_price' is the separate "Manual Price" radio where
                                        // the operator types a $ override.
                                        const methodLabels = {
                                            manual: 'Customer Supplied / No Cost',
                                            manual_price: 'Manual Price',
                                            solid_profile: 'Solid Profile', bounding_area: 'Bounding Area',
                                            weight: 'Weight', cut_length: 'Cut Length',
                                        };
                                        const m = line.material_price_method || '';
                                        const methodLabel = methodLabels[m]
                                            || (m.startsWith('nested_cut_list_') ? 'From Cut List'
                                                : m.startsWith('nested_') ? 'From Cut Sheet'
                                                : m ? m : 'Not set');
                                        const matCost = line.cost_breakdown?.breakdown?.material?.cost ?? line.material_cost_component;
                                        return (
                                            <div className="d-flex align-items-center gap-1 flex-wrap mb-1" style={{ fontSize: '0.75rem' }} title="Material cost is configured on the part page. Open the part to change it.">
                                                <span className="badge bg-secondary text-white">&#128274; Part-locked</span>
                                                <span className="badge bg-light text-dark border">{methodLabel}</span>
                                                {matCost != null && Number.isFinite(Number(matCost)) && (
                                                    <span className="text-muted">${Number(matCost).toFixed(2)}/pc</span>
                                                )}
                                                <a href={`/parts/${line.part_id}`} target="_blank" rel="noopener noreferrer" className="ms-auto" style={{ fontSize: '0.75rem' }}>Open Part &rarr;</a>
                                            </div>
                                        );
                                    })()}
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{ flex: 1 }}>
                                            {(() => {
                                                // For part-linked lines, only show the specific sheet
                                                // dropdown when the pricing method actually anchors to
                                                // a material_id (cut_length, nested_*, nested_cut_list_*).
                                                // For weight / bounding_area / solid_profile / manual /
                                                // manual_price, the cost calc doesn't read material_id —
                                                // it uses material_cwt_pricing keyed off type+finish+thickness
                                                // (formula methods) or just manual_material_price. Showing a
                                                // specific 48"x96" sheet there is misleading. Display
                                                // type · thickness · finish as descriptive text instead.
                                                const m = line.material_price_method || '';
                                                const methodAnchorsToSheet = m === 'cut_length' || m.startsWith('nested_');
                                                if (line.part_id && !methodAnchorsToSheet) {
                                                    const thicknessLabel = (filterOptions.thicknesses || []).find(t => t.thickness_id === line.thickness_id)?.display_thickness;
                                                    const parts = [line.material_type, thicknessLabel, line.surface_finish].filter(Boolean);
                                                    return (
                                                        <div className="form-control form-control-sm bg-light text-muted" style={{ cursor: 'default' }} title="Material identity is configured on the part page. The pricing method on this part doesn't anchor to a specific sheet — cost is computed from type+thickness+finish (or no cost for Customer Supplied).">
                                                            {parts.length ? parts.join(' · ') : '—'}
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <MaterialSelect
                                                        materials={materials}
                                                        filteredMaterials={filteredMaterials}
                                                        selectedId={line.material_id}
                                                        onChange={e => {
                                                            const newMatId = parseInt(e.target.value, 10);
                                                            const selectedMat = materials.find(m => m.material_id === newMatId);
                                                            const updatedLines = [...advancedQuoteLines];
                                                            updatedLines[index] = {
                                                                ...updatedLines[index],
                                                                material_id: newMatId,
                                                                material_type: selectedMat ? selectedMat.material_type : null,
                                                                thickness_id: selectedMat ? selectedMat.thickness_id : null,
                                                                surface_finish: selectedMat ? selectedMat.surface_finish : null,
                                                                material_category: selectedMat ? selectedMat.material_category : null
                                                            };
                                                            setAdvancedQuoteLines(updatedLines);
                                                        }}
                                                        disabled={!!line.part_id || line.is_customer_supplied_material}
                                                    />
                                                );
                                            })()}
                                        </div>
                                        <div className="form-check" style={{ whiteSpace: 'nowrap' }}>
                                            <input className="form-check-input" type="checkbox" checked={line.is_customer_supplied_material} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'is_customer_supplied_material', e.target.checked)} id={`custSupplied-${line.id}`} />
                                            <label className="form-check-label small" htmlFor={`custSupplied-${line.id}`}>Cust. Supplied</label>
                                        </div>
                                    </div>
                                    {!line.is_customer_supplied_material && !line.material_id && !line.part_id && (
                                        <div className="mt-1">
                                            <input type="number" step="0.01" className="form-control form-control-sm" value={line.manual_material_price || ""} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'manual_material_price', e.target.value)} placeholder="Manual material price ($)" />
                                        </div>
                                    )}
                                </div>
                                {/* Processes inline on same row */}
                                <div className="col-md-4">
                                    <label className="form-label small mb-0">Processes</label>
                                    {line.part_id ? (
                                        /* Part-locked: process steps are governed by the part record (the
                                           same source jobq-api prices from), so render them read-only,
                                           derived from the cost breakdown to always match the Cost Analysis.
                                           Edit them via "Open Part" above. Ad-hoc lines stay editable. */
                                        (() => {
                                            const activeProcs = deriveProcessesFromBreakdown(line);
                                            return (
                                                <div className="d-flex flex-wrap gap-1 align-items-center" style={{ fontSize: '0.8rem' }} title="Process steps & times are configured on the part page (the source of truth for cost). Use Open Part to change them.">
                                                    {activeProcs.length === 0 ? (
                                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>&mdash;</span>
                                                    ) : activeProcs.map(proc => {
                                                        const mins = parseFloat(line[PROCESS_MINUTE_FIELD[proc]]) || 0;
                                                        return (
                                                            <span key={proc} className="badge bg-light text-dark border">{proc}{mins > 0 ? ` · ${mins} min` : ''}</span>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                    <>
                                    <div className="d-flex flex-wrap gap-1" style={{ fontSize: '0.8rem' }}>
                                        {mainProcessOptions.map(proc => (
                                            <div key={proc} className="form-check form-check-inline m-0">
                                                <input type="checkbox" className="form-check-input" style={{ width: '14px', height: '14px' }} id={`proc-${proc}-${line.id}`} checked={(line.process_steps || []).includes(proc)} onChange={() => handleToggleProcessForLine(index, proc)} />
                                                <label className="form-check-label small" htmlFor={`proc-${proc}-${line.id}`}>{proc}</label>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="d-flex flex-wrap gap-1 mt-1" style={{ fontSize: '0.8rem' }}>
                                        {fabricationSubProcesses.map(proc => (
                                            <div key={proc} className="form-check form-check-inline m-0">
                                                <input type="checkbox" className="form-check-input" style={{ width: '14px', height: '14px' }} id={`proc-${proc}-${line.id}`} checked={(line.process_steps || []).includes(proc)} onChange={() => handleToggleProcessForLine(index, proc)} />
                                                <label className="form-check-label small" htmlFor={`proc-${proc}-${line.id}`}>{proc}</label>
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                    )}
                                </div>
                            </div>
                            {/* Row 3: Time estimates (ad-hoc lines only; Part-locked times show in the chips above + Cost Analysis) */}
                            {!line.part_id && (line.process_steps || []).length > 0 && (
                            <div className="d-flex flex-wrap gap-1 mt-1">
                                {(line.process_steps || []).includes("Laser") && (
                                    <div style={{ width: '90px' }}>
                                        <label className="form-label small mb-0" style={{ fontSize: '0.7rem' }}>Laser (min)</label>
                                        <input type="number" className="form-control form-control-sm" min="0" value={line.initial_est_laser_minutes} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'initial_est_laser_minutes', e.target.value)} />
                                    </div>
                                )}
                                {(line.process_steps || []).includes("Tap") && (
                                    <div style={{ width: '90px' }}>
                                        <label className="form-label small mb-0" style={{ fontSize: '0.7rem' }}>Tap (min)</label>
                                        <input type="number" className="form-control form-control-sm" min={0} value={line.initial_est_tap_minutes} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'initial_est_tap_minutes', e.target.value)} />
                                    </div>
                                )}
                                {(line.process_steps || []).includes("Brake") && (
                                    <div style={{ width: '90px' }}>
                                        <label className="form-label small mb-0" style={{ fontSize: '0.7rem' }}>Brake (min)</label>
                                        <input type="number" className="form-control form-control-sm" min={0} value={line.initial_est_brake_minutes} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'initial_est_brake_minutes', e.target.value)} />
                                    </div>
                                )}
                                {(line.process_steps || []).includes("Blast") && (
                                    <div style={{ width: '90px' }}>
                                        <label className="form-label small mb-0" style={{ fontSize: '0.7rem' }}>Blast (min)</label>
                                        <input type="number" className="form-control form-control-sm" min={0} value={line.initial_est_blast_minutes} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'initial_est_blast_minutes', e.target.value)} />
                                    </div>
                                )}
                                {(line.process_steps || []).includes("Finish") && (
                                    <div style={{ width: '90px' }}>
                                        <label className="form-label small mb-0" style={{ fontSize: '0.7rem' }}>Finish (min)</label>
                                        <input type="number" className="form-control form-control-sm" min={0} value={line.initial_est_finish_minutes} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, 'initial_est_finish_minutes', e.target.value)} />
                                    </div>
                                )}
                                {fabricationSubProcesses.map(proc => {
                                    const fieldName = proc === 'Tapping' ? 'initial_est_fab_tap_minutes'
                                        : proc === 'Welding' ? 'initial_est_weld_minutes'
                                        : `initial_est_${proc.toLowerCase()}_minutes`;
                                    return (line.process_steps || []).includes(proc) && (
                                        <div key={proc} style={{ width: '90px' }}>
                                            <label className="form-label small mb-0" style={{ fontSize: '0.7rem' }}>{proc} (min)</label>
                                            <input type="number" className="form-control form-control-sm" min={0} value={line[fieldName] || ''} onChange={e => handleLineChange(advancedQuoteLines, setAdvancedQuoteLines, index, fieldName, e.target.value)} />
                                        </div>
                                    );
                                })}
                            </div>
                            )}
                            {/* Cost Analysis (F08): every dollar next to the minutes × $/hr that produced it,
                                with source / default-rate badges; the basis radios set price + price_source /
                                price_basis on the line (F01). Renders "n/a — priced before provenance was
                                recorded" for breakdowns stored without inputs. */}
                            {line.show_breakdown && line.cost_breakdown && (
                                <QuoteCostAnalysisTable
                                    line={line}
                                    breakdown={line.cost_breakdown}
                                    header={headerFrac}
                                    onSelectPrice={(sel) => applyLinePrice(index, sel)}
                                    readOnly={!isEditingAllowed}
                                    tm={isTmQuote}
                                />
                            )}
                        </div>
                        </React.Fragment>
                    );})}
                    <button className="btn btn-sm btn-secondary" onClick={() => addLine(setAdvancedQuoteLines, getInitialAdvancedLine)}>+ Add Advanced Part</button>
                </div>
            )}
            {/* Assembly Pricing (F20 / F02 / F10 / F39 / F40): per assembly, per SET —
                Manufactured / Purchased / Operations rows with cost, quoted and calculated
                price and margin, then × sets; System Price with its source; the override
                box is a hint (placeholder), never pre-filled. */}
            {Object.keys(assemblySummaries).length > 0 && (
                <QuoteAssemblyPricingCard
                    summaries={Object.values(assemblySummaries)}
                    overrides={assemblyPriceOverrides}
                    isEditingAllowed={isEditingAllowed}
                    onOverrideChange={handleOverrideChange}
                    onSetCountCommit={(asmId, n) => handleChangeAssemblyQty(asmId, n)}
                    SetCountInput={AssemblySetCountInput}
                />
            )}

            <h5 className="mt-4">4. Delivery / Completion</h5>
            <div className="p-2 border rounded mb-3 bg-light" style={{ fontSize: '0.875rem' }}>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="d-flex align-items-center gap-2">
                        <div className="form-check form-check-inline m-0">
                            <input className="form-check-input" type="radio" name="completionMode" id="modeLeadTime" value="lead_time" checked={completionMode === 'lead_time'} onChange={() => setCompletionMode('lead_time')} />
                            <label className="form-check-label small" htmlFor="modeLeadTime">Lead Time</label>
                        </div>
                        <div className="form-check form-check-inline m-0">
                            <input className="form-check-input" type="radio" name="completionMode" id="modePromisedDate" value="date" checked={completionMode === 'date'} onChange={() => setCompletionMode('date')} />
                            <label className="form-check-label small" htmlFor="modePromisedDate">Date</label>
                        </div>
                    </div>
                    {completionMode === 'lead_time' ? (
                        <LeadTimeSelector value={leadTime} onChange={setLeadTime} />
                    ) : (
                        <input type="date" className="form-control form-control-sm" style={{ maxWidth: '180px' }} value={promisedDate} onChange={(e) => setPromisedDate(e.target.value)} />
                    )}
                    <button className="btn btn-outline-info btn-sm" onClick={handleEstimateCompletion} disabled={estimateLoading}>
                        {estimateLoading ? <><span className="spinner-border spinner-border-sm me-1"></span>Estimating...</> : 'Estimate Schedule'}
                    </button>
                </div>

                {scheduleEstimate && (
                    <div className="mt-2 pt-2 border-top">
                        <div className="card card-body p-2" style={{ fontSize: '0.85rem', backgroundColor: 'var(--jq-bg-card, #fff)' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <strong>
                                    Estimated Completion:{' '}
                                    {scheduleEstimate.estimated_completion_date
                                        ? new Date(scheduleEstimate.estimated_completion_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                                        : 'N/A'}
                                </strong>
                                <span className="text-muted small">{scheduleEstimate.total_production_hours} total hrs</span>
                            </div>
                            {scheduleEstimate.warning && (
                                <div className="alert alert-warning py-1 px-2 mb-1 small">{scheduleEstimate.warning}</div>
                            )}
                            <table className="table table-sm table-bordered mb-0">
                                <thead className="table-light">
                                    <tr><th>Station</th><th>Sub-Process</th><th>Est. Hours</th><th>Scheduled Date</th></tr>
                                </thead>
                                <tbody>
                                    {scheduleEstimate.station_breakdown.map((s, i) => (
                                        <tr key={i}>
                                            <td>{s.station}</td>
                                            <td>{s.sub_process || '-'}</td>
                                            <td>{s.estimated_hours.toFixed(2)}</td>
                                            <td>{s.estimated_date ? new Date(s.estimated_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : <span className="text-danger">Unscheduled</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* 5. Totals + 6. Notes - Combined compact row */}
            <div className="row g-2 mt-3 mb-3">
                <div className="col-md-8">
                    <h6 className="mb-1">5. Totals</h6>
                    {isTmQuote ? (
                        <ErrorBoundary label="The T&M totals">
                        <TmQuoteTotals
                            shipping={shipping} fees={additionalFees} discount={discount}
                            onShipping={setShipping} onFees={setAdditionalFees} onDiscount={setDiscount}
                            estimate={tmEstimate} nte={tmNte}
                            onEstimate={(v) => { setTmEstimate(v); setTmDirty(d => ({ ...d, tm_estimate_total: true })); }}
                            onNte={(v) => { setTmNte(v); setTmDirty(d => ({ ...d, tm_not_to_exceed: true })); }}
                            defaultEstimate={tmLinesEstimate(advancedQuoteLines).total}
                        />
                        </ErrorBoundary>
                    ) : (
                    <div className="d-flex align-items-end gap-2 p-2 border rounded bg-light flex-wrap" style={{ fontSize: '0.85rem' }}>
                        <div style={{ width: '100px' }}>
                            <label className="form-label small mb-0">Subtotal</label>
                            <input type="text" className="form-control form-control-sm" value={`$${subtotal.toFixed(2)}`} readOnly />
                        </div>
                        <div style={{ width: '90px' }}>
                            <label className="form-label small mb-0">Shipping</label>
                            <input type="number" step="0.01" className="form-control form-control-sm" value={shipping} onChange={(e) => setShipping(parseFloat(e.target.value) || 0)} />
                        </div>
                        <div style={{ width: '90px' }}>
                            <label className="form-label small mb-0">Fees</label>
                            <input type="number" step="0.01" className="form-control form-control-sm" value={additionalFees} onChange={(e) => setAdditionalFees(parseFloat(e.target.value) || 0)} />
                        </div>
                        <div style={{ width: '90px' }}>
                            <label className="form-label small mb-0">Discount</label>
                            <input type="number" step="0.01" className="form-control form-control-sm" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} />
                        </div>
                        <div style={{ width: '80px' }}>
                            <label className="form-label small mb-0">Tax</label>
                            <input type="number" step="0.01" className="form-control form-control-sm" value={tax} onChange={(e) => setTax(parseFloat(e.target.value) || 0)} />
                        </div>
                        <div style={{ width: '120px' }}>
                            <label className="form-label small mb-0 fw-bold text-success">Grand Total</label>
                            <input type="text" className="form-control form-control-sm fw-bold border-success" value={`$${grandTotal.toFixed(2)}`} readOnly style={{ backgroundColor: 'var(--jq-subtotal-bg, #e8f5e9)' }} />
                        </div>
                    </div>
                    )}
                </div>
                <div className="col-md-4">
                    <h6 className="mb-1">6. Notes</h6>
                    <textarea className="form-control form-control-sm" rows="2" value={notes} onChange={e => setNotes(e.target.value)} style={{ fontSize: '0.85rem' }}></textarea>
                </div>
            </div>

            {/* Quote terms (2026-10-08): the TERMS & CONDITIONS footer on the PDF. Saved on its own
                (PUT /quotes/{id}/terms), never in the quote save's body. */}
            <ErrorBoundary>
                <QuoteTermsCard
                    quoteId={!isCreating && selectedQuote ? selectedQuote.quote_id : null}
                    editable={!isCreating && !!selectedQuote && selectedQuote.status === 'Draft'}
                    apiBase={API_BASE}
                    onNotify={(message, type) => setNotification({ message, type })}
                />
            </ErrorBoundary>
            
            <div className="row mt-4">
                {/* --- Financial Summary Card (Left) — five-way cost split, labelled stored vs live (F38/F41) --- */}
                {quoteMode === 'Advanced' && financialSummary && (
                    <div className="col-md-6">
                        <QuoteFinancialSummaryCard
                            summary={financialSummary}
                            mode={hydrationInfo.ran ? 'live' : 'stored'}
                            hydratedAt={hydrationInfo.at}
                            reason={hydrationInfo.reason || 'hydrate'}
                            /* Decision 7 (wave 2): server-side Σ stored cost × qty from GET /quotes/{id}
                               and the PATCH response; optional keys — absent on a new quote / older API. */
                            costSubtotal={selectedQuote ? selectedQuote.cost_subtotal : null}
                            costSubtotalCoverage={selectedQuote ? selectedQuote.cost_subtotal_coverage : null}
                            /* A T&M quote: "Priced at completion", the cost and the internal estimate —
                               never revenue / margin (walk-through fix 1). null keeps the fixed card. */
                            tm={isTmQuote ? {
                                estimate: money2(tmEstimate) ?? tmLinesEstimate(advancedQuoteLines).total,
                                estimateIsDefault: money2(tmEstimate) === null,
                                nte: money2(tmNte),
                            } : null}
                        />
                    </div>
                )}

                {/* --- Production Time Summary (Right) --- */}
                {quoteMode === 'Advanced' && productionSummary.length > 0 && (
                    <div className="col-md-6">
                        <div className="card shadow-sm border-info h-100">
                            <div className="card-header bg-info text-white fw-bold">
                                Production Time Forecast (Estimated)
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm table-striped mb-0">
                                    <thead>
                                        <tr>
                                            <th>Workstation</th>
                                            <th className="text-end">Total Hours</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productionSummary.map(row => (
                                            <tr key={row.station}>
                                                <td>{row.station}</td>
                                                <td className="text-end fw-bold">{row.hours.toFixed(2)} hrs</td>
                                            </tr>
                                        ))}
                                        <tr className="table-light border-top">
                                            <td className="fw-bold">TOTAL ESTIMATED HOURS</td>
                                            <td className="text-end fw-bold text-primary">
                                                {productionSummary.reduce((acc, r) => acc + r.hours, 0).toFixed(2)} hrs
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="card-footer text-muted small">
                                * Calculated using historical averages where available, otherwise initial estimates.
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );

    return (
        <div className="d-flex flex-column h-100">
            <Notification message={notification.message} type={notification.type} onDismiss={() => setNotification({ message: '', type: '' })} />
            <ConfirmationModal {...confirmation} onCancel={() => setConfirmation({ show: false, message: '', onConfirm: () => {} })} />
            {/* Recalculate-All preview (F01): rendered OUTSIDE the read-only fieldset so its buttons always work. */}
            <QuoteRecalcPreviewModal
                show={!!recalcPreview}
                rows={recalcPreview ? recalcPreview.rows : []}
                oldTotal={recalcPreview ? recalcPreview.oldTotal : 0}
                newTotal={recalcPreview ? recalcPreview.newTotal : 0}
                failures={recalcPreview ? recalcPreview.failures : []}
                overrideNote={recalcPreview ? recalcPreview.overrideNote : null}
                onApply={applyRecalcPreview}
                onCancel={() => setRecalcPreview(null)}
            />
            <EmailModal 
                show={emailModal.show}
                title={`Email Quote #${emailModal.quoteId}`}
                recipientName={selectedQuote ? getCustomerName(selectedQuote.customer_id) : ""}
                onClose={() => setEmailModal({ show: false, quoteId: null })}
                onSend={handleSendEmail}
                isLoading={loading}
            />

            <SnapshotHistoryModal 
                show={showHistoryModal} 
                quoteId={selectedQuote?.quote_id} 
                onClose={() => setShowHistoryModal(false)} 
            />

            {/* Top Navigation Tabs */}
            <div className="bg-light border-bottom px-3 py-2">
                <ul className="nav nav-pills">
                    <li className="nav-item">
                        <button 
                            className={`nav-link ${activeTab === 'quotes' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('quotes')}
                        >
                            Quote Manager
                        </button>
                    </li>
                    <li className="nav-item">
                        <button 
                            className={`nav-link ${activeTab === 'rates' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('rates')}
                        >
                            Shop Rate Config
                        </button>
                    </li>
                </ul>
            </div>

            {/* Main Content Area */}
            <div className="flex-grow-1 overflow-hidden d-flex">
                {activeTab === 'rates' ? (
                    <div className="w-100 h-100">
                        <ShopRateManager />
                    </div>
                ) : (
                    <>
                        <div className="border-end p-3 d-flex flex-column" style={{ width: "350px", flexShrink: 0 }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h4 className="mb-0">Quotes</h4>
                                <button className="btn btn-primary btn-sm" onClick={handleCreateNew}>New</button>
                            </div>
                            <input type="text" className="form-control form-control-sm mb-2" placeholder="Search ID, customer, PO, cust SO, contact, part #, description, notes…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            <div className="row g-1 mb-2">
                                <div className="col-6">
                                    <select className="form-select form-select-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                                        <option value="">All Statuses</option>
                                        <option value="Draft">Draft</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Ordered">Ordered</option>
                                    </select>
                                </div>
                                <div className="col-6">
                                    <select className="form-select form-select-sm" value={filterDays} onChange={e => setFilterDays(e.target.value ? parseInt(e.target.value, 10) : '')}>
                                        <option value="">All Time</option>
                                        <option value="7">Last 7 Days</option>
                                        <option value="30">Last 30 Days</option>
                                        <option value="60">Last 60 Days</option>
                                        <option value="90">Last 90 Days</option>
                                        <option value="180">Last 6 Months</option>
                                        <option value="365">Last Year</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mb-2">
                                <select className="form-select form-select-sm" value={filterCustomerId} onChange={e => setFilterCustomerId(e.target.value)}>
                                    <option value="">All Customers</option>
                                    {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="text-muted small mb-1">{filteredQuotes.length} quote{filteredQuotes.length !== 1 ? 's' : ''}</div>
                            <div className="list-group flex-grow-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                                {filteredQuotes.map(q => (
                                    <a href="#" key={q.quote_id} className={`list-group-item list-group-item-action ${selectedQuote?.quote_id === q.quote_id ? 'active' : ''}`}
                                       onClick={(e) => { e.preventDefault(); handleSelectQuote(q); }}>
                                        <div className="d-flex w-100 justify-content-between">
                                            <h6 className="mb-1">Quote #{q.quote_id}{isTm(q) && <TmBadge className="ms-2" light={selectedQuote?.quote_id === q.quote_id} />}</h6>
                                            <small className={`badge bg-${q.status === 'Approved' ? 'success' : q.status === 'Ordered' ? 'primary' : 'secondary'}`}>{q.status}</small>
                                        </div>
                                        <p className="mb-1 small d-flex align-items-center gap-2">
                                            <span className="flex-grow-1 text-truncate">{getCustomerName(q.customer_id)}</span>
                                            {q.fulfillment_type === 'pickup' && (
                                                <span className={`badge ${selectedQuote?.quote_id === q.quote_id ? 'bg-light text-dark' : 'bg-warning text-dark'}`} style={{ fontSize: '0.6rem' }} title="Customer Pickup">
                                                    <i className="bi bi-person me-1"></i>Pickup
                                                </span>
                                            )}
                                            {q.fulfillment_type === 'delivery' && (
                                                <span className={`badge ${selectedQuote?.quote_id === q.quote_id ? 'bg-light text-dark' : 'bg-secondary'}`} style={{ fontSize: '0.6rem' }} title="We Deliver">
                                                    <i className="bi bi-truck me-1"></i>Delivery
                                                </span>
                                            )}
                                        </p>
                                        <small className="text-muted">{q.created_at ? new Date(q.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : ''}</small>
                                    </a>
                                ))}
                            </div>
                        </div>

                        <div className="flex-grow-1 p-4 overflow-auto">
                            <h4>{isCreating ? 'Create New Quote' : (selectedQuote ? (selectedQuote.status !== 'Draft' ? `Quote #${selectedQuote.quote_id}` : `Editing Quote #${selectedQuote.quote_id}`) : 'Select a Quote')}{!isCreating && isTm(selectedQuote) && <TmBadge className="ms-2 align-middle" />}</h4>
                            {(!isCreating && selectedQuote && selectedQuote.status !== 'Draft') && (
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className={`badge bg-${
                                        selectedQuote.status === 'Approved' ? 'success' :
                                        selectedQuote.status === 'Ordered' ? 'primary' : 'secondary'
                                    } fs-6`}>
                                        {selectedQuote.status}
                                    </span>
                                    {selectedQuote.approved_at && (
                                        <span className="text-muted small">Approved {new Date(selectedQuote.approved_at).toLocaleDateString()}</span>
                                    )}
                                </div>
                            )}
                            <hr/>

                            {/* Customer-supplied drawings — surfaces when this quote was
                                auto-created from a boyd-portal submission. Hidden when
                                customer_submission_id is null (manually-created quote). */}
                            {!isCreating && selectedQuote?.customer_submission_id && (
                                <CustomerSubmissionDrawingsCard
                                    customerSubmissionId={selectedQuote.customer_submission_id}
                                />
                            )}

                            {!isCreating && selectedQuote && selectedQuote.status === 'Ordered' && <ErrorBoundary label="The T&M order link"><TmQuoteOrderedBanner quote={selectedQuote} /></ErrorBoundary>}

                            {/* Action buttons for approved/non-draft quotes */}
                            {(!isCreating && selectedQuote && selectedQuote.status !== 'Draft') && (
                                <div className="d-flex justify-content-end align-items-center mb-3 flex-wrap gap-1">
                                    <div className="btn-group">
                                        {selectedQuote?.quote_id && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => setShowHistoryModal(true)}
                                                title="View previously sent/exported versions"
                                            >
                                                History
                                            </button>
                                        )}
                                        {selectedQuote?.quote_id && (
                                            <>
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleDuplicateQuote(selectedQuote.quote_id)}
                                                    disabled={loading}
                                                >
                                                    Duplicate
                                                </button>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => openPdf(`${API_BASE}/quotes/${selectedQuote.quote_id}/pdf`, null, { download: true })}
                                                >
                                                    Export PDF
                                                </button>
                                                {/* Decision 12: a T&M quote is an internal placeholder — never emailed. */}
                                                {!isTm(selectedQuote) && (
                                                <button
                                                    className="btn btn-outline-dark"
                                                    onClick={handleEmailQuoteClick}
                                                >
                                                    Email Quote
                                                </button>
                                                )}
                                            </>
                                        )}

                                        {!selectedQuote.generated_sales_order_id && selectedQuote.status === 'Approved' && (
                                            <button className="btn btn-success" onClick={() => handleConvertToSalesOrderClick(selectedQuote.quote_id)} disabled={loading}>
                                                Convert to Sales Order
                                            </button>
                                        )}

                                        {!selectedQuote.generated_sales_order_id && !selectedQuote.generated_invoice_id && selectedQuote.status === 'Approved' && (
                                            <button className="btn btn-outline-warning" onClick={() => handleUnapproveQuote(selectedQuote.quote_id)} disabled={loading}>
                                                Restore to Draft
                                            </button>
                                        )}

                                        {poModal.show && (
                                            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
                                                <div className="modal-dialog modal-dialog-centered">
                                                    <div className="modal-content">
                                                        <div className="modal-header">
                                                            <h5 className="modal-title">Convert to Sales Order</h5>
                                                            <button type="button" className="btn-close" onClick={closePoModal} disabled={loading} aria-label="Close"></button>
                                                        </div>
                                                        <div className="modal-body">
                                                            <div className="mb-3">
                                                                <label className="form-label">
                                                                    Yield / Billing Policy <span className="text-danger">*</span>
                                                                </label>
                                                                <select
                                                                    className={`form-select ${poModal.yieldPolicyId ? '' : 'border-danger'}`}
                                                                    value={poModal.yieldPolicyId}
                                                                    onChange={e => setPoModal(prev => ({ ...prev, yieldPolicyId: e.target.value }))}
                                                                    autoFocus
                                                                    disabled={isTm(selectedQuote)}
                                                                    title={isTm(selectedQuote) ? TM_POLICY_NOTE : undefined}
                                                                >
                                                                    <option value="">-- Select Policy --</option>
                                                                    {yieldPolicies.map(p => (
                                                                        <option key={p.policy_id} value={p.policy_id}>{p.name}</option>
                                                                    ))}
                                                                </select>
                                                                {poModal.yieldPolicyId && (
                                                                    <div className="form-text fst-italic" style={{ fontSize: '0.75rem' }}>
                                                                        {yieldPolicies.find(p => String(p.policy_id) === String(poModal.yieldPolicyId))?.description}
                                                                    </div>
                                                                )}
                                                                {isTm(selectedQuote) && (
                                                                    <div className="form-text" style={{ fontSize: '0.75rem' }}>
                                                                        <i className="bi bi-lock-fill me-1"></i>{TM_POLICY_NOTE}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="mb-3">
                                                                <label className="form-label">Customer PO Number (Optional)</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={poModal.poNumber}
                                                                    onChange={e => setPoModal(prev => ({ ...prev, poNumber: e.target.value }))}
                                                                    placeholder="Enter PO Number..."
                                                                />
                                                            </div>
                                                            <div className="mb-3">
                                                                <label className="form-label">Cust SO (Optional)</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={poModal.soNumber}
                                                                    onChange={e => setPoModal(prev => ({ ...prev, soNumber: e.target.value }))}
                                                                    placeholder="Enter SO Number..."
                                                                />
                                                            </div>
                                                            <div className="mb-3">
                                                                <label className="form-label">Attach Customer PO PDF (Optional)</label>
                                                                <input
                                                                    type="file"
                                                                    className="form-control"
                                                                    accept="application/pdf,.pdf"
                                                                    onChange={e => setPoModal(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                                                                />
                                                                {poModal.file && (
                                                                    <small className="text-muted d-block mt-1">
                                                                        Selected: {poModal.file.name} ({Math.round(poModal.file.size / 1024)} KB)
                                                                    </small>
                                                                )}
                                                            </div>
                                                            {poModal.file && (
                                                                <div className="mb-3">
                                                                    <label className="form-label">
                                                                        Your PIN <span className="text-danger">*</span>
                                                                    </label>
                                                                    <input
                                                                        type="password"
                                                                        className="form-control"
                                                                        value={poModal.pin}
                                                                        onChange={e => setPoModal(prev => ({ ...prev, pin: e.target.value }))}
                                                                        placeholder="Required to upload PDF"
                                                                        autoComplete="one-time-code"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="modal-footer">
                                                            <button type="button" className="btn btn-secondary" onClick={closePoModal} disabled={loading}>Cancel</button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={submitSalesOrderConversion}
                                                                disabled={loading || !poModal.yieldPolicyId || (poModal.file && !poModal.pin.trim())}
                                                            >
                                                                {loading ? 'Processing...' : 'Convert'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {!selectedQuote.generated_invoice_id &&
                                        selectedQuote.status === 'Approved' &&
                                        !selectedQuote.generated_sales_order_id && !isTm(selectedQuote) && (
                                            <button className="btn btn-primary" onClick={() => handleConvertToInvoice(selectedQuote.quote_id)} disabled={loading}>
                                                Convert to Invoice
                                            </button>
                                        )}

                                        {!selectedQuote.generated_invoice_id && selectedQuote.generated_sales_order_id && (
                                            <span className="d-inline-block" tabIndex="0" data-bs-toggle="tooltip" title="Use the Sales Order page to invoice this order.">
                                                <button className="btn btn-secondary disabled" style={{ pointerEvents: 'none' }}>
                                                    Invoice via Sales Order
                                                </button>
                                            </span>
                                        )}

                                        {selectedQuote.generated_sales_order_id && (
                                            <span className="btn btn-outline-primary disabled">
                                                Linked to {selectedQuote.generated_sales_order_number || `SO #${selectedQuote.generated_sales_order_id}`}
                                            </span>
                                        )}
                                        {selectedQuote.generated_invoice_id && (
                                            <span className="btn btn-outline-success disabled">
                                                Invoice #{selectedQuote.generated_invoice_id}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Full quote form - disabled fieldset for approved quotes makes everything read-only */}
                            <fieldset disabled={!isEditingAllowed}>
                                {(isCreating || selectedQuote) && renderQuoteForm()}
                            </fieldset>
                            
                            {(isCreating || (selectedQuote && selectedQuote.status === 'Draft')) && (
                                 <div className="mt-4 d-flex align-items-center">
                                    <button className="btn btn-success btn-lg me-2" onClick={handleSaveQuote} disabled={loading}>{loading ? 'Saving...' : (isCreating ? 'Create Quote' : 'Save Changes')}</button>
                                    
                                    {selectedQuote && selectedQuote.status === 'Draft' && (
                                        <button className="btn btn-info btn-lg me-2" onClick={() => handleApproveQuote(selectedQuote.quote_id)} disabled={loading}>{isTm(selectedQuote) ? 'Accept as T&M' : 'Approve Quote'}</button>
                                    )}
                                    
                                    {selectedQuote?.quote_id && (
                                        <>
                                            <button 
                                                className="btn btn-outline-secondary btn-lg me-2" 
                                                onClick={() => setShowHistoryModal(true)}
                                                title="View previously sent/exported versions"
                                                disabled={loading}
                                            >
                                                📜 History
                                            </button>
                                            {/* ------------------------------- */}

                                            <button className="btn btn-outline-primary btn-lg me-2" onClick={() => handleDuplicateQuote(selectedQuote.quote_id)} disabled={loading}>Duplicate</button>
                                            <button className="btn btn-secondary btn-lg me-2" onClick={() => openPdf(`${API_BASE}/quotes/${selectedQuote.quote_id}/pdf`, null, { download: true })} disabled={loading}>Export PDF</button>
                                            {!isTm(selectedQuote) && <button className="btn btn-outline-dark btn-lg me-2" onClick={handleEmailQuoteClick} disabled={loading}>Email Quote</button>}
                                            <button className="btn btn-outline-danger btn-lg" onClick={() => handleDeleteQuote(selectedQuote.quote_id)} disabled={loading}>Delete</button>
                                        </>
                                    )}
                                </div>
                            )}

                            {!isCreating && !selectedQuote && (
                                <div className="text-center mt-5 pt-5">
                                    <h5 className="text-muted">No quote selected</h5>
                                    <p className="text-muted">Select a quote from the list on the left, or create a new one to get started.</p>
                                    <button className="btn btn-primary" onClick={handleCreateNew}>Create New Quote</button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
            {showContactModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Contact</h5>
                                <button type="button" className="btn-close" onClick={() => setShowContactModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Name</label>
                                    <input className="form-control" value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Role</label>
                                    <select className="form-select" value={contactForm.role} onChange={e => setContactForm({...contactForm, role: e.target.value})}>
                                        <option value="General">General</option>
                                        <option value="Billing">Billing</option>
                                        <option value="Shipping">Shipping</option>
                                        <option value="Purchasing">Purchasing</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Email</label>
                                    <input className="form-control" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Phone</label>
                                    <input className="form-control" value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowContactModal(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleSaveContact}>Save Contact</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}